import React, { useEffect, useState, useRef, useMemo } from 'react';
import './ChatbotFrame.css';
import PublicChatHeader from './PublicChatHeader';
import IconButton from '@mui/material/IconButton';
import SendIcon from '@mui/icons-material/Send';
import PhoneIcon from '@mui/icons-material/Phone';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';
import MicOffIcon from '@mui/icons-material/MicOff';
import MicIcon from '@mui/icons-material/Mic';
import FlareIcon from '@mui/icons-material/Flare';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Button from '@mui/material/Button';
import { InputBase, FormControlLabel, Checkbox, TextField } from '@mui/material';
import { API_URL, BASE_URL, WS_URL } from '../../common/Constants';
import ChatRatingDialog from '../chat/ChatRatingDialog';
import PoweredByChattitude from '../common/PoweredByChattitude';
import { CalcomEventTypePicker, CalcomSlotsPicker } from '../chat/CalcomSchedulingPickers';
import { QuickReplyPicker } from '../chat/QuickReplyPicker';
import { useVoiceCall } from './useVoiceCall';
import IntermediateWelcomePanel from '../chat/IntermediateWelcomePanel';
import {
  WIDGET_CHAT_VIEW_KEY,
  WIDGET_CONVERSATION_STARTED_KEY,
  getIntermediateConfig,
  isWelcomeStepActive,
} from '../chat/intermediateWelcome';

/** Shown above the chat input during voice calls; does not affect input layout. */
function VoiceSpeakingIndicator({ inCall, speaking }) {
  if (!inCall) {
    return null;
  }
  return (
    <div
      aria-live="polite"
      style={{
        position: 'absolute',
        bottom: '100%',
        left: 0,
        right: 0,
        paddingBottom: 4,
        fontSize: 12,
        color: '#666',
        textAlign: 'center',
        opacity: speaking ? 1 : 0,
        transition: 'opacity 0.15s ease',
        pointerEvents: 'none',
      }}
    >
      Agent is speaking…
    </div>
  );
}

const getOrigin = () => {
  // If in an iframe, try to get the parent window's origin (the embedding page's domain)
  if (window.parent !== window) {
    try {
      // Try to access parent origin directly (works for same-origin)
      return window.parent.location.origin;
    } catch (e) {
      // Cross-origin: extract origin from document.referrer
      try {
        if (document.referrer) {
          const referrerUrl = new URL(document.referrer);
          return referrerUrl.origin;
        }
      } catch (e2) {
        // Fallback to current window's origin if referrer parsing fails
        console.warn('⚠️ Could not determine parent origin, using current window origin');
      }
    }
  }
  // If not in an iframe, return current window's origin
  return window.location.origin;
};

const getAgentFromHash = () => {
  // First try to get from hash (for hash routing)
  const hash = window.location.hash || '';
  const queryStart = hash.indexOf('?');
  if (queryStart !== -1) {
    const params = new URLSearchParams(hash.substring(queryStart + 1));
    const agentFromHash = params.get('agent');
    if (agentFromHash) return agentFromHash;
  }
  // Fallback to query parameters (for non-hash URLs)
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('agent');
};

const getTenantFromHash = () => {
  // First try to get from hash (for hash routing)
  const hash = window.location.hash || '';
  const queryStart = hash.indexOf('?');
  if (queryStart !== -1) {
    const params = new URLSearchParams(hash.substring(queryStart + 1));
    const tenantFromHash = params.get('tenant');
    if (tenantFromHash) return tenantFromHash;
  }
  // Fallback to query parameters (for non-hash URLs)
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('tenant');
};

const getPageUrlFromHash = () => {
  // First try to get from hash (for hash routing)
  const hash = window.location.hash || '';
  const queryStart = hash.indexOf('?');
  if (queryStart !== -1) {
    const params = new URLSearchParams(hash.substring(queryStart + 1));
    const pageUrl = params.get('pageUrl');
    if (pageUrl) return pageUrl;
  }
  // Fallback to query parameters (for non-hash URLs)
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('pageUrl');
};

const CALCOM_HISTORY_SCHEDULING_HTML = '<p><strong>Scheduling</strong></p><p>Choose a meeting type, then pick a date range to load available times.</p>';
const CALCOM_HISTORY_SLOTS_HTML = '<p><strong>Available times</strong></p><p>Choose a time, then enter your details to book.</p>';
const CALCOM_HISTORY_NO_SLOTS_HTML = '<p>No open times in this range. Try another date range or meeting length.</p>';

export function quickReplySendOptions(item, value, label) {
  return {
    ...(label && label !== value ? { displayMessage: label } : {}),
    ...(item.action ? { action: item.action } : {}),
    ...(item.pendingActionId ? { pendingActionId: item.pendingActionId } : {}),
  };
}

/** Unwrap legacy bot rows and replace LLM fluff with the same short copy as live Cal.com widget turns. */
function normalizeCalcomHistoryMessage(msg) {
  if (!msg) return msg;
  let m = typeof msg === 'object' ? { ...msg } : msg;
  if (typeof m.text === 'string') {
    const t = m.text.trim();
    if (t.startsWith('{') && !m.calcomEventTypes?.length && !m.calcomSlots && !m.quickReplies?.length) {
      try {
        const o = JSON.parse(t);
        if (typeof o.text === 'string') {
          m = { ...m, text: o.text };
          if (Array.isArray(o.calcomEventTypes) && o.calcomEventTypes.length > 0) {
            m.calcomEventTypes = o.calcomEventTypes;
          }
          if (o.calcomSlots && typeof o.calcomSlots === 'object') {
            m.calcomSlots = o.calcomSlots;
          }
          if (Array.isArray(o.quickReplies) && o.quickReplies.length > 0) {
            m.quickReplies = o.quickReplies;
          }
        }
      } catch {
        /* keep m */
      }
    }
  }
  if (m.calcomEventTypes?.length && !m.calcomSlots) {
    return { ...m, text: CALCOM_HISTORY_SCHEDULING_HTML };
  }
  if (m.calcomSlots && typeof m.calcomSlots === 'object') {
    const at = m.calcomSlots.allTimes;
    const noSlots = Array.isArray(at) && at.length === 0;
    return { ...m, text: noSlots ? CALCOM_HISTORY_NO_SLOTS_HTML : CALCOM_HISTORY_SLOTS_HTML };
  }
  return m;
}


const getCtreadFromHash = () => {
  // First try to get from hash (for hash routing)
  const hash = window.location.hash || '';
  const queryStart = hash.indexOf('?');
  if (queryStart !== -1) {
    const params = new URLSearchParams(hash.substring(queryStart + 1));
    const ctreadFromHash = params.get('ctread');
    if (ctreadFromHash) return ctreadFromHash === 'true';
  }
  // Fallback to query parameters (for non-hash URLs)
  const urlParams = new URLSearchParams(window.location.search);
  const ctread = urlParams.get('ctread');
  return ctread === 'true';
};

const getEmbedFullFromHash = () => {
  const hash = window.location.hash || '';
  const queryStart = hash.indexOf('?');
  if (queryStart !== -1) {
    const params = new URLSearchParams(hash.substring(queryStart + 1));
    if (params.get('embed') === 'full') return true;
  }
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('embed') === 'full';
};

const getTransparentFromHash = () => {
  const hash = window.location.hash || '';
  const queryStart = hash.indexOf('?');
  if (queryStart !== -1) {
    const params = new URLSearchParams(hash.substring(queryStart + 1));
    if (params.get('transparent') === 'true') return true;
  }
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('transparent') === 'true';
};

const ChatFormMessage = ({ fields, onSubmit, embedPlain }) => {
  const [values, setValues] = React.useState(
    fields.reduce((acc, f) => ({ ...acc, [f.name]: '' }), {})
  );
  const [error, setError] = React.useState('');

  const handleChange = e => {
    setValues({ ...values, [e.target.name]: e.target.value });
  };

  const handleSubmit = e => {
    e.preventDefault();
    // Basic email validation
    const emailField = fields.find(f => f.type === 'email');
    if (emailField) {
      const email = values[emailField.name];
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError('Please enter a valid email address.');
        return;
      }
    }
    setError('');
    onSubmit(values);
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        background: embedPlain ? 'transparent' : '#f5f5f5',
        padding: 12,
        borderRadius: 8,
        margin: '8px 0',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        border: embedPlain ? '1px solid rgba(0,0,0,0.12)' : 'none',
      }}
    >
      {fields.map(f => (
        <div
          key={f.name}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            marginBottom: 16,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <label
            htmlFor={f.name}
            style={{
              marginBottom: 6,
              fontWeight: 500,
              color: '#333',
              textAlign: 'left',
            }}
          >
            {f.label}
            {f.required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
          </label>

          {f.type === 'textarea' ? (
            <textarea
              id={f.name}
              name={f.name}
              value={values[f.name]}
              onChange={handleChange}
              required={f.required}
              rows={f.rows || 3}
              style={{
                width: '100%',
                minWidth: 0,
                padding: 8,
                borderRadius: 4,
                border: '1px solid #ccc',
                fontFamily: 'inherit',
                fontSize: 14,
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
          ) : (
            <input
              id={f.name}
              type={f.type}
              name={f.name}
              value={values[f.name]}
              onChange={handleChange}
              required={f.required}
              style={{
                width: '100%',
                minWidth: 0,
                padding: 8,
                borderRadius: 4,
                border: '1px solid #ccc',
                fontSize: 14,
                boxSizing: 'border-box',
              }}
            />
          )}
        </div>
      ))}


      {error && (
        <div style={{ color: 'red', marginBottom: 8 }}>{error}</div>
      )}

      <button type="submit">Submit</button>
    </form>
  );
};

// Place the helper function here, after imports:
function formatMessageWithLinks(text) {
  if (!text) return '';

  // Helper function to add ctread=true query parameter to a URL
  const addCtreadParam = (url) => {
    if (!url) return url;
    try {
      const urlObj = new URL(url);
      urlObj.searchParams.set('ctread', 'true');
      return urlObj.toString();
    } catch (e) {
      // If URL parsing fails, try simple string manipulation
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}ctread=true`;
    }
  };

  // If the message contains HTML tags (like img, video, etc.), don't convert URLs to links
  // as it would break the HTML structure (e.g., converting URL in img src attribute)
  const hasHtmlTags = /<[^>]+>/i.test(text);
  if (hasHtmlTags) {
    // Only process anchor tags if they exist, but don't convert plain URLs to links
    if (/<a\s/i.test(text)) {
      // Replace anchor tags to ensure they have target="_blank", rel="noopener noreferrer", and ctread=true
      return text.replace(/<a\s+([^>]*)>/gi, (match, attributes) => {
        // Extract href attribute
        const hrefMatch = attributes.match(/href\s*=\s*["']([^"']+)["']/i);
        let href = hrefMatch ? hrefMatch[1] : '';

        // Add ctread=true to href if it exists
        if (href) {
          const updatedHref = addCtreadParam(href);
          // Replace the href in attributes
          attributes = attributes.replace(/href\s*=\s*["'][^"']*["']/i, `href="${updatedHref}"`);
        }

        // Check if target="_blank" already exists
        const hasTarget = /target\s*=\s*["']_blank["']/i.test(attributes);
        // Check if rel="noopener noreferrer" already exists
        const hasRel = /rel\s*=\s*["'][^"']*noopener[^"']*noreferrer[^"']*["']/i.test(attributes) ||
          /rel\s*=\s*["'][^"']*noreferrer[^"']*noopener[^"']*["']/i.test(attributes);

        let newAttributes = attributes;

        // Add target="_blank" if not present
        if (!hasTarget) {
          newAttributes += (newAttributes.trim() ? ' ' : '') + 'target="_blank"';
        }

        // Add rel="noopener noreferrer" if not present
        if (!hasRel) {
          // Check if rel exists but doesn't have noopener noreferrer
          if (/rel\s*=\s*["'][^"']*["']/i.test(newAttributes)) {
            // Replace existing rel attribute
            newAttributes = newAttributes.replace(/rel\s*=\s*["'][^"']*["']/gi, 'rel="noopener noreferrer"');
          } else {
            // Add new rel attribute
            newAttributes += (newAttributes.trim() ? ' ' : '') + 'rel="noopener noreferrer"';
          }
        }

        return `<a ${newAttributes}>`;
      });
    }
    // If HTML tags exist but no anchor tags, return as-is (don't convert URLs)
    return text;
  }

  // If the message already contains an anchor tag, ensure it has target="_blank" and ctread=true
  if (/<a\s/i.test(text)) {
    // Replace anchor tags to ensure they have target="_blank", rel="noopener noreferrer", and ctread=true
    return text.replace(/<a\s+([^>]*)>/gi, (match, attributes) => {
      // Extract href attribute
      const hrefMatch = attributes.match(/href\s*=\s*["']([^"']+)["']/i);
      let href = hrefMatch ? hrefMatch[1] : '';

      // Add ctread=true to href if it exists
      if (href) {
        const updatedHref = addCtreadParam(href);
        // Replace the href in attributes
        attributes = attributes.replace(/href\s*=\s*["'][^"']*["']/i, `href="${updatedHref}"`);
      }

      // Check if target="_blank" already exists
      const hasTarget = /target\s*=\s*["']_blank["']/i.test(attributes);
      // Check if rel="noopener noreferrer" already exists
      const hasRel = /rel\s*=\s*["'][^"']*noopener[^"']*noreferrer[^"']*["']/i.test(attributes) ||
        /rel\s*=\s*["'][^"']*noreferrer[^"']*noopener[^"']*["']/i.test(attributes);

      let newAttributes = attributes;

      // Add target="_blank" if not present
      if (!hasTarget) {
        newAttributes += (newAttributes.trim() ? ' ' : '') + 'target="_blank"';
      }

      // Add rel="noopener noreferrer" if not present
      if (!hasRel) {
        // Check if rel exists but doesn't have noopener noreferrer
        if (/rel\s*=\s*["'][^"']*["']/i.test(newAttributes)) {
          // Replace existing rel attribute
          newAttributes = newAttributes.replace(/rel\s*=\s*["'][^"']*["']/gi, 'rel="noopener noreferrer"');
        } else {
          // Add new rel attribute
          newAttributes += (newAttributes.trim() ? ' ' : '') + 'rel="noopener noreferrer"';
        }
      }

      return `<a ${newAttributes}>`;
    });
  }
  // Otherwise, replace URLs with anchor tags
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
  return text.replace(urlRegex, url => {
    let href = url;
    if (!href.startsWith('http')) {
      href = 'https://' + href;
    }
    // Add ctread=true to the href
    const hrefWithCtread = addCtreadParam(href);
    return `<a href="${hrefWithCtread}" target="_blank" rel="noopener noreferrer">Click Here</a>`;
  });
}

// Safely render text with HTML as React elements (replaces dangerouslySetInnerHTML)
function renderTextWithLinks(text) {
  if (!text) return null;

  // Decode HTML entities if the text is HTML-escaped (but preserve HTML tags)
  let decodedText = text;
  if (typeof text === 'string' && text.includes('&lt;') && text.includes('&gt;')) {
    // Check if it's HTML-escaped HTML (like &lt;img&gt;)
    // Decode entities but preserve the HTML structure
    decodedText = text
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&');
  }

  // Check if text contains any HTML tags
  if (!/<[^>]+>/g.test(decodedText)) {
    return decodedText;
  }

  try {
    // Use DOMParser to safely parse HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${decodedText}</div>`, 'text/html');
    const container = doc.body.firstChild;

    if (!container) {
      return decodedText;
    }

    // Recursively convert DOM nodes to React elements
    let keyCounter = 0;
    const convertNodeToReact = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const textContent = node.textContent;
        return textContent ? textContent : null;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();
        const props = { key: `html-${keyCounter++}` };

        // Handle anchor tags with security attributes
        if (tagName === 'a') {
          let href = node.getAttribute('href') || '#';
          // Prevent javascript: URLs for security
          if (href.toLowerCase().startsWith('javascript:')) {
            href = '#';
          }
          props.href = href;
          const target = node.getAttribute('target');
          props.target = target || '_blank';
          const rel = node.getAttribute('rel');
          props.rel = rel || 'noopener noreferrer';
        } else {
          // Copy safe attributes for other tags (allow all except dangerous ones)
          Array.from(node.attributes).forEach(attr => {
            const attrName = attr.name.toLowerCase();
            // Skip dangerous attributes (event handlers, javascript:, etc.)
            if (attrName.startsWith('on') ||
              attr.value && typeof attr.value === 'string' &&
              (attr.value.toLowerCase().startsWith('javascript:') ||
                attr.value.toLowerCase().includes('javascript:'))) {
              return;
            }
            // Handle style attribute - convert CSS string to React style object
            if (attrName === 'style' && attr.value) {
              const styleObj = {};
              attr.value.split(';').forEach(declaration => {
                const [property, value] = declaration.split(':').map(s => s.trim());
                if (property && value) {
                  // Convert kebab-case to camelCase (e.g., font-size -> fontSize)
                  const camelProperty = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                  styleObj[camelProperty] = value;
                }
              });
              props.style = styleObj;
            } else {
              // Handle special cases for React
              // Convert 'class' to 'className' for React
              if (attrName === 'class') {
                props.className = attr.value;
              } else if (attrName === 'for') {
                // Convert 'for' to 'htmlFor' for React
                props.htmlFor = attr.value;
              } else {
                // For img tags, ensure numeric attributes are numbers
                if (tagName === 'img' && (attrName === 'width' || attrName === 'height')) {
                  const numValue = parseInt(attr.value, 10);
                  props[attrName] = isNaN(numValue) ? attr.value : numValue;
                } else {
                  // Use lowercase attribute name for standard HTML attributes
                  // This ensures React recognizes them correctly
                  props[attrName] = attr.value;
                }
              }
            }
          });
        }

        // Convert child nodes
        const children = Array.from(node.childNodes)
          .map(child => convertNodeToReact(child))
          .filter(child => child !== null && child !== undefined && child !== '');

        // Handle self-closing tags
        if (['br', 'hr', 'img', 'input', 'meta', 'link'].includes(tagName)) {
          return React.createElement(tagName, props);
        }

        // Create React element for the tag
        if (children.length === 0) {
          return React.createElement(tagName, props);
        }
        return React.createElement(tagName, props, ...children);
      }

      return null;
    };

    // Convert all child nodes of the container
    const children = Array.from(container.childNodes)
      .map(child => convertNodeToReact(child))
      .filter(child => child !== null && child !== undefined && child !== '');

    // Return single element or array of elements
    if (children.length === 0) {
      return decodedText;
    }
    if (children.length === 1) {
      return children[0];
    }
    return children;
  } catch (error) {
    // Fallback to plain text if parsing fails
    console.warn('Error parsing HTML:', error, 'Text:', text);
    return decodedText;
  }
}

const ChatbotFrame = () => {
  const isEmbedFullLayout = getEmbedFullFromHash();
  const [config, setConfig] = useState(null);
  const [agentId, setAgentId] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [error, setError] = useState(null);
  // Track if user sent at least one message (not a greeting)
  const [userSentMessage, setUserSentMessage] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [parentSessionId, setParentSessionId] = useState(null);
  const [waitingForParentSession, setWaitingForParentSession] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);

  // Helper to set loading state and broadcast to follower tabs
  const setLoadingWithBroadcast = (loadingState) => {
    setLoading(loadingState);
    loadingRef.current = loadingState; // Sync ref immediately
    try {
      if (window.BroadcastChannel) {
        const ch = new window.BroadcastChannel('slickbots-chat');
        ch.postMessage({ type: 'loading-state', loading: loadingState, clientId: clientIdRef.current });
        ch.close();
      }
    } catch (e) {
      console.error('❌ Error broadcasting loading state:', e);
    }
  };
  // Track if user has manually toggled the chat.
  // If a previous page already stored a preference, treat it as chosen so
  // "open on load" does not override it after navigation.
  const [userToggledMinimized, setUserToggledMinimized] = useState(() => {
    if (getEmbedFullFromHash()) return false;
    try {
      return localStorage.getItem('widgetMinimized') !== null;
    } catch (e) {
      return false;
    }
  });
  function hasVisitorChatMessage() {
    try {
      const raw = localStorage.getItem('widgetMessages');
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.some((message) => message && (message.role === 'user' || message.isBot === false));
    } catch (e) {
      return false;
    }
  }

  function isMobileDevice() {
    return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  }

  function shouldOfferWelcome(customization) {
    if (getEmbedFullFromHash()) return false;
    if (isMobileDevice()) return false;
    if (!isWelcomeStepActive(customization)) return false;
    try {
      if (localStorage.getItem(WIDGET_CONVERSATION_STARTED_KEY) === 'true') return false;
    } catch (e) {
      return false;
    }
    return !hasVisitorChatMessage();
  }

  function shouldStartOnWelcome(customization) {
    if (!shouldOfferWelcome(customization)) return false;
    try {
      if (localStorage.getItem('widgetMinimized') === 'true') return false;
      if (localStorage.getItem(WIDGET_CHAT_VIEW_KEY) === 'minimized') return false;
    } catch (e) {
      return false;
    }
    return true;
  }

  function getInitialMinimized(customization) {
    if (getEmbedFullFromHash()) {
      return false;
    }
    if (shouldStartOnWelcome(customization)) {
      return false;
    }
    // FIRST: Check if mobile - ALWAYS start minimized (ignore global state)
    const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (isMobile) {
      // Mobile devices ALWAYS start minimized - user must explicitly click to maximize
      console.log('📱 Mobile device - always starting minimized');
      return true;
    }

    // SECOND: Desktop - Check global minimized state from localStorage (cross-tab sync)
    const globalMinimized = localStorage.getItem('widgetMinimized');
    if (globalMinimized !== null) {
      console.log('🖥️ Desktop - using global minimized state from localStorage:', globalMinimized);
      return globalMinimized === 'true';
    }

    // THIRD: Use config default
    if (!customization) return false;
    const configDefault = customization.onPageLoadChatState === 'closed';
    console.log('📍 Using config default minimized state:', configDefault);
    return configDefault;
  }
  const [minimized, setMinimized] = useState(() => getInitialMinimized(config && config.customization));
  const [welcomeVisible, setWelcomeVisible] = useState(() => shouldStartOnWelcome(config && config.customization));
  const minimizedRef = useRef(minimized);
  // Sync minimized state with config.customization.onPageLoadChatState unless user toggled
  useEffect(() => {
    if (isEmbedFullLayout) return;
    if (!(config && config.customization)) return;
    if (shouldStartOnWelcome(config.customization)) {
      setWelcomeVisible(true);
      setMinimized(false);
      return;
    }
    if (!userToggledMinimized) {
      setWelcomeVisible(false);
      setMinimized(getInitialMinimized(config.customization));
    }
  }, [
    isEmbedFullLayout,
    userToggledMinimized,
    config && config.customization && config.customization.onPageLoadChatState,
    config && config.customization && config.customization.intermediate && config.customization.intermediate.enabled,
  ]);
  useEffect(() => {
    if (!isEmbedFullLayout) return;
    setMinimized(false);
    setUserToggledMinimized(true);
  }, [isEmbedFullLayout]);
  // Keep minimizedRef in sync with minimized state
  useEffect(() => {
    minimizedRef.current = minimized;
  }, [minimized]);
  const [fontSizeIdx, setFontSize] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [requestTranscript, setRequestTranscript] = useState(false);
  const [visitorEmail, setVisitorEmail] = useState('');
  const [visitorEmailError, setVisitorEmailError] = useState('');
  const [sendingTranscript, setSendingTranscript] = useState(false);
  // Add state for rating sessionId
  const [ratingSessionId, setRatingSessionId] = useState(null);
  // Ref to track if showing thank you message (for synchronous checks in broadcast handlers)
  const showingThankYouRef = useRef(false);
  // Agent leave popup state
  const [showAgentLeavePopup, setShowAgentLeavePopup] = useState(false);
  const [agentLeaveMessage, setAgentLeaveMessage] = useState('');
  // Form success modal state (moved here to be declared before useEffect that uses it)
  const [closeConfirmText, setCloseConfirmText] = useState(null);
  const [showFormSuccessModal, setShowFormSuccessModal] = useState(false);
  const [formSuccessMessage, setFormSuccessMessage] = useState('');

  // Live agent name - switches between bot and live agent
  const [liveAgentName, setLiveAgentName] = useState(null);

  // Starter prompts state
  const [presetResponses, setPresetResponses] = useState([]);
  const [showPresetResponses, setShowPresetResponses] = useState(true);

  // WebSocket state - WebSocket is now the primary mode
  const [ws, setWs] = useState(null);
  const [currentBotMessage, setCurrentBotMessage] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const streamTimeoutRef = useRef(null);
  const scrollThrottleRef = useRef(null);

  const isMutedRef = useRef(isMuted);
  const inputRef = useRef();
  const messagesEndRef = useRef();
  const wsRef = useRef(null);
  const sessionIdRef = useRef(sessionId); // Keep ref in sync for broadcast handlers
  const rootRef = useRef(null); // Ref for root div to measure actual size
  const configRef = useRef(config); // Keep ref in sync to access latest config in closures
  // Keep configRef in sync with config state for use in closures/handlers
  useEffect(() => {
    configRef.current = config;
  }, [config]);
  const hasHistoryLoadedRef = useRef(false); // Track if session history was loaded to suppress greeting

  // Persist a chosen automated phrase per session to avoid rapid changes on initial load
  const [automatedPickedPhrase, setAutomatedPickedPhrase] = useState(null);
  // Track if the minimized bubble should be shown
  const [showBubble, setShowBubble] = useState(true);
  // Unique client id per tab to avoid accepting our own BroadcastChannel echoes
  // We ALWAYS generate a new ID on mount to prevent collision if user duplicates the tab (which copies sessionStorage)
  const clientIdRef = useRef(`${Date.now()}-${Math.random().toString(36).slice(2, 9)}`);

  // Update sessionStorage just for reference/consistency, but rely on the Ref for the session
  useEffect(() => {
    sessionStorage.setItem('slickbots-client-id', clientIdRef.current);
  }, []);

  const {
    voiceError,
    isVoiceMuted,
    isBotSpeaking,
    startVoiceCall,
    endVoiceCall,
    toggleVoiceMute,
    handleVoiceTranscript,
    isInVoiceCall,
    isVoiceConnecting,
  } = useVoiceCall({
    agentId,
    tenantId,
    sessionId,
    clientId: clientIdRef.current,
    onTranscript: (msg) => {
      if (!msg?.text) return;
      setMessages((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        const filtered = prevArr.filter((m) => !m.isThinking);
        const role = msg.role || 'system';

        if (msg.calcomSlots && typeof msg.calcomSlots === 'object') {
          const slotsMsg = {
            role: 'bot',
            text: msg.text,
            calcomSlots: msg.calcomSlots,
            timestamp: new Date(),
          };

          const last = filtered[filtered.length - 1];
          if (last && last.role === 'bot' && !last.calcomSlots && !last.calcomEventTypes?.length) {
            const t = (last.text || '').toLowerCase();
            if (t.includes('available times') || t.includes('scheduling')) {
              return [...filtered.slice(0, -1), { ...last, ...slotsMsg }];
            }
          }

          let base = filtered;
          const u = base.length - 1;
          if (u >= 1 && base[u]?.role === 'user') {
            const next = [...base];
            const prevBot = next[u - 1];
            if (prevBot?.role === 'bot' && prevBot.calcomEventTypes?.length && !prevBot.calcomSlots) {
              next[u - 1] = {
                ...prevBot,
                calcomEventTypes: undefined,
                text: '<p><strong>Scheduling</strong></p><p>Choose a meeting type, then pick a date range to load available times.</p>',
              };
              base = next;
            }
          }

          return [...base, slotsMsg];
        }

        if (Array.isArray(msg.calcomEventTypes) && msg.calcomEventTypes.length > 0) {
          return [
            ...filtered,
            {
              role: 'bot',
              text: msg.text,
              calcomEventTypes: msg.calcomEventTypes,
              timestamp: new Date(),
            },
          ];
        }

        const entry = {
          role,
          text: msg.text,
          timestamp: new Date(),
        };
        if (Array.isArray(msg.quickReplies) && msg.quickReplies.length > 0) {
          entry.quickReplies = msg.quickReplies;
        }
        return [...filtered, entry];
      });
    },
  });

  // Detect parent window origin for secure cross-origin postMessage communication
  const parentOriginRef = useRef(null);
  if (parentOriginRef.current === null && window.parent !== window) {
    try {
      // Try to access parent origin directly (works for same-origin)
      parentOriginRef.current = window.parent.location.origin;
    } catch (e) {
      // Cross-origin: extract origin from document.referrer
      try {
        if (document.referrer) {
          const referrerUrl = new URL(document.referrer);
          parentOriginRef.current = referrerUrl.origin;
        }
      } catch (e2) {
        // Fallback: use '*' but log a warning (less secure)
        console.warn('⚠️ Could not determine parent origin for postMessage. Using wildcard (less secure).');
        parentOriginRef.current = '*';
      }
    }
  }

  // Keep sessionIdRef in sync with sessionId state
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // When the config/customization changes, choose a random automated phrase for this render
  useEffect(() => {
    try {
      const cust = config && config.customization ? config.customization : null;
      if (!cust) return;

      const bubbleType = cust.bubbleType || 'none';
      if (bubbleType === 'automated') {
        // Build phrases array (support both array and newline-string storage)
        let phrases = cust.automatedClosedAgentPhrases;
        if (typeof phrases === 'string') phrases = phrases.split(/\r?\n/);
        phrases = Array.isArray(phrases) ? phrases.map(p => String(p || '').trim()).filter(Boolean) : [];
        const picked = phrases[Math.floor(Math.random() * phrases.length)];
        setAutomatedPickedPhrase(picked);
      } else {
        setAutomatedPickedPhrase(null);
      }
    } catch (e) {
      // keep silent on errors selecting automated phrase
    }
  }, [config && config.customization && JSON.stringify(config.customization)]);

  // Single active tab ownership management
  const [isActiveTab, setIsActiveTab] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const activeTabRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const ownershipCheckIntervalRef = useRef(null);
  const intentionalDisconnectRef = useRef(false); // Track intentional disconnects
  const OWNERSHIP_KEY = 'slickbots-active-tab';
  const OWNERSHIP_STALE_MS = 5000; // 5 seconds timeout
  const OWNERSHIP_HEARTBEAT_MS = 2500; // Update every 2.5 seconds
  const OWNERSHIP_CHECK_MS = 1000; // Check every 1 second

  // Read current ownership from localStorage
  const readOwnership = () => {
    try {
      const raw = localStorage.getItem(OWNERSHIP_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  };

  // Write ownership to localStorage
  const writeOwnership = (ownership) => {
    try {
      localStorage.setItem(OWNERSHIP_KEY, JSON.stringify(ownership));
      // Broadcast ownership change via BroadcastChannel for faster updates
      if (window.BroadcastChannel) {
        try {
          const ch = new window.BroadcastChannel('slickbots-chat');
          ch.postMessage({
            type: 'ownership-changed',
            ownership: ownership,
            clientId: clientIdRef.current
          });
          ch.close();
        } catch (e) {
          // Ignore BroadcastChannel errors
        }
      }
    } catch (e) {
      console.error('❌ Error writing ownership:', e);
    }
  };

  // Clear ownership from localStorage
  const clearOwnership = () => {
    try {
      localStorage.removeItem(OWNERSHIP_KEY);
      // Broadcast ownership cleared
      if (window.BroadcastChannel) {
        try {
          const ch = new window.BroadcastChannel('slickbots-chat');
          ch.postMessage({
            type: 'ownership-cleared',
            clientId: clientIdRef.current
          });
          ch.close();
        } catch (e) {
          // Ignore BroadcastChannel errors
        }
      }
    } catch (e) {
      console.error('❌ Error clearing ownership:', e);
    }
  };

  // Check if ownership is stale (older than 5 seconds)
  const isOwnershipStale = (timestamp) => {
    if (!timestamp) return true;
    return Date.now() - timestamp > OWNERSHIP_STALE_MS;
  };

  // Check if this tab owns the chat
  const checkTabOwnership = () => {
    const ownership = readOwnership();
    if (!ownership || isOwnershipStale(ownership.timestamp)) {
      return false; // No active owner or stale
    }
    return ownership.tabId === clientIdRef.current;
  };

  // Claim ownership for this tab
  const claimTabOwnership = () => {
    const sessionId = sessionIdRef.current || localStorage.getItem('widgetSessionId');
    const ownership = {
      tabId: clientIdRef.current,
      timestamp: Date.now(),
      sessionId: sessionId || null
    };
    writeOwnership(ownership);
    activeTabRef.current = ownership;
    setIsActiveTab(true);
    setIsPaused(false);
    startOwnershipHeartbeat();
  };

  // Release ownership (when tab closes or user explicitly ends chat)
  const releaseTabOwnership = () => {
    // Only clear if we are the current owner
    const ownership = readOwnership();
    if (ownership && ownership.tabId === clientIdRef.current) {
      clearOwnership();
    }
    stopOwnershipHeartbeat();
    setIsActiveTab(false);
    activeTabRef.current = null;
  };

  // Start heartbeat to keep ownership alive
  const startOwnershipHeartbeat = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    heartbeatIntervalRef.current = setInterval(() => {
      const ownership = readOwnership();
      // Only update if we still own it
      if (ownership && ownership.tabId === clientIdRef.current) {
        const sessionId = sessionIdRef.current || localStorage.getItem('widgetSessionId');
        writeOwnership({
          tabId: clientIdRef.current,
          timestamp: Date.now(),
          sessionId: sessionId || null
        });
      } else {
        // Lost ownership, stop heartbeat
        stopOwnershipHeartbeat();
        setIsActiveTab(false);
        setIsPaused(true);
        // Don't auto-minimize - respect user's current state
      }
    }, OWNERSHIP_HEARTBEAT_MS);
  };

  // Stop heartbeat
  const stopOwnershipHeartbeat = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  };

  // Helper to cleanly disconnect WebSocket
  const disconnectWebSocket = () => {
    if (wsRef.current) {
      // Mark this as an intentional disconnect to prevent auto-reconnect
      intentionalDisconnectRef.current = true;

      // Avoid closing if already closed/closing
      if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) {
        wsRef.current.close();
      }
      wsRef.current = null;
      setWs(null);

      // Reset the flag after a short delay
      setTimeout(() => {
        intentionalDisconnectRef.current = false;
      }, 1000);
    }
  };

  // Streaming buffer refs to accumulate chunks and flush periodically
  const streamingBufferRef = useRef('');
  const streamingFlushIntervalRef = useRef(null);
  // When applying a remote snapshot we should suppress the automatic broadcast
  // from the messages useEffect to avoid echo loops between tabs.
  const suppressBroadcastRef = useRef(false);
  // Accumulator for the fully-received streamed text (used to finalize remote streams)
  const streamingAccumRef = useRef('');
  // Ref to track loading state synchronously for race-condition prevention
  const loadingRef = useRef(false);
  // Flag to prevent multiple calls to completeStreaming for the same stream
  const isCompletingStreamRef = useRef(false);
  // Ref to track currentBotMessage state synchronously (since state can't be read synchronously)
  const currentBotMessageRef = useRef('');
  // Ref to track streaming state synchronously (state updates are async, causing accumulator resets)
  const isStreamingRef = useRef(false);
  // Ref to queue messages while WebSocket connects (for aggressive leadership takeover)
  const pendingSocketMessagesRef = useRef([]);
  /** Quick-reply chips from WS side channel; attached when stream completes (avoids orphan row above text). */
  const pendingQuickRepliesRef = useRef(null);

  // --- message dedupe / persistence / broadcast helpers ---
  // Normalize text for dedupe keys: strip HTML and collapse whitespace so
  // formatted vs plain text versions dedupe correctly across tabs.
  const normalizeForKey = (text) => {
    if (!text) return '';
    try {
      const stripped = String(text).replace(/<[^>]*>/g, '');
      return stripped.replace(/\s+/g, ' ').trim();
    } catch (e) {
      return String(text || '').trim();
    }
  };
  const normalizeTs = (t) => {
    if (!t) return 0;
    if (typeof t === 'number') return t;
    if (t instanceof Date) return t.getTime();
    const n = Date.parse(String(t) || '');
    return isNaN(n) ? 0 : n;
  };

  const messageKey = (m) => {
    const role = m.role || '';
    const text = normalizeForKey((m.text || '').trim());
    // For transient thinking/waiting placeholders, ignore timestamp so duplicates collapse
    if (m && (m.isThinking || m.isWaiting)) {
      return `${role}::${text}::thinking`;
    }
    // Use role + trimmed text as the de-duplication key. Timestamp is used only for ordering.
    return `${role}::${text}`;
  };

  const messageExists = (arr, msg) => {
    const key = messageKey(msg);
    return arr.some(m => messageKey(m) === key);
  };

  const persistAndBroadcast = (next) => {
    // Simplified: only persist messages, no cross-tab broadcasting
    // Only persist if we're the active tab
    if (!isActiveTab) return;

    try {
      // Filter out thinking/waiting messages and validate before persisting
      const toPersist = next.filter(m => {
        // Remove transient messages
        if (m.isThinking || m.isWaiting) return false;
        // Validate message structure
        if (!m || typeof m !== 'object') return false;
        if (!m.text || typeof m.text !== 'string' || m.text.trim() === '') return false;
        if (!m.role || (m.role !== 'user' && m.role !== 'bot')) return false;
        return true;
      });

      if (toPersist.length > 0) {
        localStorage.setItem('widgetMessages', JSON.stringify(toPersist));
        localStorage.setItem('widgetMessagesTimestamp', String(normalizeTs(toPersist[toPersist.length - 1].timestamp)));
      }
    } catch (e) {
      console.error('❌ Error persisting messages:', e);
    }
  };

  // add message - prevent duplicates based on content + timestamp proximity
  // IMPORTANT: Always preserves ALL existing messages, just adds new ones
  const addMessageIfNotExists = (msg) => {
    // Validate message before adding
    if (!msg || !msg.text || typeof msg.text !== 'string' || msg.text.trim() === '') {
      console.warn('⚠️ Skipping invalid message:', msg);
      return;
    }

    setMessages(prev => {
      try {
        const prevArr = Array.isArray(prev) ? prev : [];

        const msgKey = `${msg.role}::${normalizeForKey(msg.text || '')}`;
        const msgTs = normalizeTs(msg.timestamp);

        // Check for duplicates: same content AND timestamps are close (within 10 seconds)
        // This prevents duplicates from sync issues (milliseconds/seconds difference) 
        // while allowing legitimate repeated questions (minutes/hours apart)
        const isDuplicate = prevArr.some(m => {
          const mKey = `${m.role}::${normalizeForKey(m.text || '')}`;

          // Same content (role + normalized text)
          if (mKey !== msgKey) return false;

          // Content matches - check timestamps
          const mTs = normalizeTs(m.timestamp);

          // If either timestamp is invalid, treat as duplicate
          if (msgTs === 0 || mTs === 0) {
            return true;
          }

          // If timestamps match exactly, definitely a duplicate
          if (msgTs === mTs) {
            return true;
          }

          // If timestamps are very close (< 10 seconds), it's a duplicate (sync issue)
          // This ignores milliseconds/seconds differences while catching true duplicates
          const timeDiff = Math.abs(msgTs - mTs);
          if (timeDiff < 10000) { // 10 seconds in milliseconds
            return true; // Duplicate
          }

          // Timestamps are >= 10 seconds apart - allow as legitimate repeat
          return false;
        });

        if (isDuplicate) {
          // This is a duplicate, skip it - but preserve all existing messages
          return prevArr;
        }

        // Not a duplicate, add the message
        // CRITICAL: Preserve ALL existing messages (including welcome message)
        const next = [...prevArr, msg];
        return next;
      } catch (e) {
        console.error('❌ Error adding message:', e);
        // fallback append - still preserve all existing messages
        const next = [...(prev || []), msg];
        return next;
      }
    });
  };
  // --- end helpers ---

  // Session management state
  const [isIdle, setIsIdle] = useState(false);
  const [idleMessageShown, setIdleMessageShown] = useState(false);
  const idleTimerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const httpHeartbeatIntervalRef = useRef(null); // Old HTTP heartbeat (deprecated)
  const lastActivityRef = useRef(Date.now());

  // Different font sizes for mobile vs desktop
  // const isMobileDevice = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const fontSizes = isMobileDevice ? [16, 18, 20] : [14, 16, 18];

  // Request session ID from parent window on mount
  useEffect(() => {
    // Check if we're in an iframe
    if (window.parent !== window) {
      let resolved = false;

      const requestSession = () => {
        try {
          window.parent.postMessage({ type: 'slickbots-request-session' }, '*');
        } catch (e) {
          console.log('Unable to request parent session:', e);
        }
      };

      // Listen for response from parent
      const handleMessage = (event) => {
        if (event.data && event.data.type === 'slickbots-session-response') {
          resolved = true;
          clearInterval(requestInterval);
          clearTimeout(timeout);
          setParentSessionId(event.data.sessionId);
          setWaitingForParentSession(false);
        }
      };

      window.addEventListener('message', handleMessage);

      requestSession();
      const requestInterval = setInterval(() => {
        if (!resolved) {
          requestSession();
        }
      }, 300);

      // Set timeout in case parent doesn't respond
      const timeout = setTimeout(() => {
        if (!resolved) {
          console.log('Parent did not respond to session request, continuing without parent session');
          resolved = true;
          clearInterval(requestInterval);
          setWaitingForParentSession(false);
        }
      }, 3000);

      return () => {
        window.removeEventListener('message', handleMessage);
        clearInterval(requestInterval);
        clearTimeout(timeout);
      };
    } else {
      // Not in iframe, skip waiting for parent
      setWaitingForParentSession(false);
    }
  }, []);

  // Notify parent whenever sessionId changes (including when it becomes null)
  useEffect(() => {
    if (window.parent === window) return;

    window.parent.postMessage({
      type: 'slickbots-session-changed',
      sessionId: sessionId
    }, '*');

  }, [sessionId]);

  // Ownership check and management useEffect
  useEffect(() => {
    // Check ownership on mount and periodically
    const checkOwnership = () => {
      const ownership = readOwnership();

      // Check if we currently own it (by tabId, regardless of staleness)
      const weOwnIt = ownership && ownership.tabId === clientIdRef.current;

      if (weOwnIt) {
        // We own it - update heartbeat if stale, make sure we're active
        if (isOwnershipStale(ownership.timestamp)) {
          // Ownership is stale but we own it - just update the heartbeat
          const sessionId = sessionIdRef.current || localStorage.getItem('widgetSessionId');
          writeOwnership({
            tabId: clientIdRef.current,
            timestamp: Date.now(),
            sessionId: sessionId || null
          });
        }

        // Make sure we're active
        if (!isActiveTab) {
          setIsActiveTab(true);
          setIsPaused(false);
          startOwnershipHeartbeat();
        } else if (!heartbeatIntervalRef.current) {
          // Heartbeat not running - start it
          startOwnershipHeartbeat();
        }
      } else {
        // We don't own it
        if (ownership && !isOwnershipStale(ownership.timestamp)) {
          // Another tab is active and fresh
          if (isActiveTab) {
            // We lost ownership - pause
            setIsActiveTab(false);
            setIsPaused(true);
            stopOwnershipHeartbeat();
            disconnectWebSocket();
            // Don't auto-minimize - respect user's current state
          } else if (!isPaused) {
            // Not paused yet - pause now
            setIsPaused(true);
            // Don't auto-minimize - keep widget visible so user can see "Chat Active Elsewhere"
          }
        } else {
          // No active owner or stale - but DON'T auto-claim
          // User must explicitly click "Continue" button to become active
          if (isActiveTab) {
            // We were active but ownership is now gone/stale - become paused
            console.log('⚠️ Lost ownership (no active owner) - becoming paused');
            setIsActiveTab(false);
            setIsPaused(true);
            stopOwnershipHeartbeat();
            disconnectWebSocket();
            // Don't auto-minimize - respect user's current state
          }
          // If already paused, stay paused - do NOT auto-claim ownership
          // User must click "Continue" button to activate
        }
      }
    };

    // Initial check
    checkOwnership();

    // Set up periodic ownership check
    ownershipCheckIntervalRef.current = setInterval(checkOwnership, OWNERSHIP_CHECK_MS);

    // Listen for ownership changes via BroadcastChannel
    let chatChannel = null;
    if (window.BroadcastChannel) {
      chatChannel = new window.BroadcastChannel('slickbots-chat');
      chatChannel.onmessage = (event) => {
        const { type, ownership, clientId } = event.data;
        // Ignore messages from this tab
        if (clientId && clientId === clientIdRef.current) return;

        if (type === 'ownership-changed') {
          // Ownership changed - check if we lost it
          if (ownership && ownership.tabId !== clientIdRef.current) {
            setIsActiveTab(false);
            setIsPaused(true);
            stopOwnershipHeartbeat();
            disconnectWebSocket();
            // Don't auto-minimize - respect user's current state
          }
        } else if (type === 'ownership-cleared') {
          // Ownership cleared - we can try to claim it
          checkOwnership();
        } else if (type === 'minimized-sync') {
          if (isEmbedFullLayout) {
            return;
          }
          // Sync minimized state across tabs (global minimized state)
          // BUT: Ignore if we're currently showing the thank you message (checked via ref for synchronous access)
          if (showingThankYouRef.current) {
            console.log('📡 Ignoring minimized-sync - currently showing thank you message (blocked by ref)');
            return; // Don't minimize while showing thank you message
          }

          const remoteMinimized = event.data.minimized;
          const remoteView = event.data.view;
          const welcomeAllowed = isWelcomeStepActive(configRef.current && configRef.current.customization);
          if (remoteView === 'intermediate' && welcomeAllowed) {
            setWelcomeVisible(true);
            setMinimized(false);
            setUserToggledMinimized(true);
            try {
              localStorage.setItem('widgetMinimized', 'false');
              localStorage.setItem(WIDGET_CHAT_VIEW_KEY, 'intermediate');
            } catch (e) { /* ignore */ }
          } else if (remoteView === 'open') {
            setWelcomeVisible(false);
            setUserToggledMinimized(true);
            if (typeof remoteMinimized === 'boolean') setMinimized(remoteMinimized);
            try {
              if (welcomeAllowed) {
                localStorage.setItem(WIDGET_CONVERSATION_STARTED_KEY, 'true');
              }
              localStorage.setItem(WIDGET_CHAT_VIEW_KEY, 'open');
              if (typeof remoteMinimized === 'boolean') {
                localStorage.setItem('widgetMinimized', remoteMinimized.toString());
              }
            } catch (e) { /* ignore */ }
          } else if (remoteView === 'minimized') {
            setWelcomeVisible(false);
            setMinimized(true);
            setUserToggledMinimized(true);
            try {
              localStorage.setItem('widgetMinimized', 'true');
              localStorage.setItem(WIDGET_CHAT_VIEW_KEY, 'minimized');
            } catch (e) { /* ignore */ }
          } else if (typeof remoteMinimized === 'boolean' && remoteMinimized !== minimized) {
            console.log('📡 Received minimized-sync from another tab:', remoteMinimized);
            if (!remoteMinimized) setWelcomeVisible(false);
            setMinimized(remoteMinimized);
            setUserToggledMinimized(true); // Mark as user-toggled to prevent config override
            localStorage.setItem('widgetMinimized', remoteMinimized.toString());
            console.log('✅ Applied global minimized state:', remoteMinimized);
          }
        } else if (type === 'show-close-confirm') {
          // Show close confirmation dialog on all tabs
          setShowCloseConfirm(true);
        } else if (type === 'dismiss-close-confirm') {
          // Handle close confirmation sync - dismiss on all tabs
          const action = event.data.action;
          setShowCloseConfirm(false);
          setCloseConfirmText(null);
          setRequestTranscript(false);
          setVisitorEmail('');
          setVisitorEmailError('');

          if (action === 'end') {
            const capturedSessionId = event.data.capturedSessionId || sessionId || localStorage.getItem('widgetSessionId');
            const shouldShowRating = event.data.hadUserMessage !== undefined
              ? event.data.hadUserMessage
              : userSentMessage;

            setRatingSessionId(capturedSessionId);
            endSession('closed', capturedSessionId, { broadcast: false });

            // Show rating dialog on all tabs if user sent a message
            if (shouldShowRating) {
              if (showFormSuccessModal) {
                setShowFormSuccessModal(false);
                setTimeout(() => setShowRatingDialog(true), 0);
              } else {
                setShowRatingDialog(true);
              }
            } else {
              // No rating needed - minimize immediately
              if (!isEmbedFullLayout) {
                setMinimized(true);
              }
            }
          } else if (action === 'continue') {
            // User chose to continue - just dismiss the dialog
            // No other action needed
          }
        } else if (type === 'complete-cleanup') {
          // COMPLETE cleanup on all tabs after rating dialog closes
          // BUT: Ignore own broadcast - the submitting tab handles cleanup locally
          if (event.data.clientId === clientIdRef.current) {
            console.log('📡 Ignoring own complete-cleanup broadcast - already handled locally');
            return;
          }

          console.log('🧹 Received complete-cleanup broadcast from another tab - clearing all state');

          // Clear the thank you ref
          showingThankYouRef.current = false;

          // Close dialogs
          setShowRatingDialog(false);
          setRatingSubmitted(false);
          setRatingSessionId(null);
          setShowCloseConfirm(false);
          setCloseConfirmText(null);
          setRequestTranscript(false);
          setVisitorEmail('');
          setVisitorEmailError('');

          // Clear ALL session-related state
          setSessionId(null);
          setLiveAgentName(null);
          setMessages([]);
          setUserSentMessage(false);
          setIsIdle(false);
          setIdleMessageShown(false);
          setShowPresetResponses(true);

          // Reset streaming state
          isStreamingRef.current = false;
          setIsStreaming(false);
          setCurrentBotMessage('');
          streamingBufferRef.current = '';
          streamingAccumRef.current = '';
          currentBotMessageRef.current = '';
          hasHistoryLoadedRef.current = false;

          // Clear localStorage
          try {
            localStorage.removeItem('widgetSessionId');
            localStorage.removeItem(WIDGET_CONVERSATION_STARTED_KEY);
            localStorage.removeItem('widgetSessionStartTime');
            localStorage.removeItem('lastWidgetActivity');
            localStorage.removeItem('widgetLiveAgentName');
            localStorage.removeItem('widgetMessages');
            localStorage.removeItem('widgetMessagesTimestamp');
            // Keep widgetMinimized — visitor explicitly closed the chat
          } catch (e) {
            console.error('❌ Error clearing localStorage:', e);
          }

          // Minimize if requested
          setWelcomeVisible(false);
          try {
            localStorage.setItem(WIDGET_CHAT_VIEW_KEY, 'minimized');
          } catch (e) { /* ignore */ }
          const shouldMinimize = event.data.minimize !== undefined ? event.data.minimize : true;
          if (shouldMinimize && !isEmbedFullLayout) {
            setUserToggledMinimized(true);
            setMinimized(true);
          }

          console.log('✅ Complete cleanup finished on this tab');
        } else if (type === 'close-rating-dialog') {
          // Another tab closed the rating dialog - do complete cleanup on this tab too
          console.log('📡 Received close-rating-dialog broadcast - performing complete cleanup');

          // Call the full cleanup handler (but don't broadcast again to avoid loops)
          // Just do the cleanup locally
          setShowRatingDialog(false);
          setRatingSubmitted(false);
          setRatingSessionId(null);

          // COMPLETE STATE CLEANUP - Clear ALL session-related state
          setSessionId(null);
          setLiveAgentName(null);
          setMessages([]);
          setUserSentMessage(false);
          setIsIdle(false);
          setIdleMessageShown(false);
          setShowPresetResponses(true);

          // Reset streaming state
          isStreamingRef.current = false;
          setIsStreaming(false);
          setCurrentBotMessage('');
          streamingBufferRef.current = '';
          streamingAccumRef.current = '';
          currentBotMessageRef.current = '';
          hasHistoryLoadedRef.current = false;

          // COMPLETE LOCALSTORAGE CLEANUP
          try {
            localStorage.removeItem('widgetSessionId');
            localStorage.removeItem(WIDGET_CONVERSATION_STARTED_KEY);
            localStorage.removeItem('widgetSessionStartTime');
            localStorage.removeItem('lastWidgetActivity');
            localStorage.removeItem('widgetLiveAgentName');
            localStorage.removeItem('widgetMessages');
            // Keep widgetMinimized — visitor explicitly closed the chat
          } catch (e) {
            console.error('❌ Error clearing localStorage:', e);
          }

          // Notify parent
          window.parent.postMessage({
            type: 'SLICKBOTS_SESSION_CHANGED',
            sessionId: null
          }, '*');

          setWelcomeVisible(false);
          try {
            localStorage.setItem(WIDGET_CHAT_VIEW_KEY, 'minimized');
          } catch (e) { /* ignore */ }
          const shouldMinimize = event.data.minimize !== undefined ? event.data.minimize : true;
          if (shouldMinimize && !isEmbedFullLayout) {
            setUserToggledMinimized(true);
            setMinimized(true);
          }

          console.log('✅ Complete cleanup finished on this tab (via broadcast)');
        } else if (type === 'rating-submitted') {
          // Another tab submitted rating - close rating dialog and minimize on this tab
          // Do NOT show thank you message on other tabs
          // But IGNORE if this is from the same tab (let that tab show the thank you message)
          if (event.data.clientId === clientIdRef.current) {
            console.log('📡 Ignoring own rating-submitted broadcast - will show thank you message');
            return; // Ignore own broadcast
          }

          console.log('📡 Received rating-submitted broadcast from another tab - closing dialog on this tab');
          showingThankYouRef.current = false; // Clear ref on other tabs (they don't show thank you)
          setShowRatingDialog(false);
          setRatingSubmitted(false);
          setRatingSessionId(null);
          if (!isEmbedFullLayout) {
            setMinimized(true);
          }
        } else if (type === 'remove-thinking-message') {
          // Remove thinking messages
          setMessages(prev => {
            const prevArr = Array.isArray(prev) ? prev : [];
            const filtered = prevArr.filter(m => !m.isThinking);
            if (filtered.length !== prevArr.length) {
              return filtered;
            }
            return prevArr;
          });
        } else if (type === 'form-message') {
          // Handle form message broadcast from another tab
          // Ignore own broadcast - the submitting tab handles it locally
          if (event.data.clientId === clientIdRef.current) {
            console.log('📡 Ignoring own form-message broadcast - already handled locally');
            return;
          }

          console.log('📡 Received form-message broadcast from another tab');
          const { formMessage, requestMessage } = event.data;

          setMessages(prev => {
            // Remove any previous form or waiting/thinking message
            const filtered = prev.filter(msg => !msg.isForm && !msg.isWaiting && !msg.isThinking);
            let newMessages = [...filtered];

            // Add requestMessage if present and not blank/null
            const requestMsg = requestMessage;
            if (requestMsg && typeof requestMsg === 'string' && requestMsg.trim() !== '') {
              console.log('✅ Adding requestMessage from broadcast:', requestMsg);
              newMessages.push({
                role: 'bot',
                text: formatMessageWithLinks(requestMsg),
                timestamp: new Date()
              });
            }

            // Add the form message
            if (formMessage) {
              newMessages.push(formMessage);
            }
            return newMessages;
          });
        } else if (type === 'clear-messages') {
          // Clear all messages when session ends
          setMessages([]);
          hasHistoryLoadedRef.current = false;
        }
      };
    }

    // Listen for storage events (fallback for cross-tab detection)
    const onStorage = (e) => {
      if (e && e.key && e.key !== OWNERSHIP_KEY) return;
      checkOwnership();
    };
    window.addEventListener('storage', onStorage);

    // Listen for visibility changes (CRITICAL for mobile)
    // Mobile browsers throttle/suspend background tabs, so we MUST check ownership when tab becomes visible
    const onVisibilityChange = () => {
      if (!document.hidden) {
        // Tab just became visible - check ownership immediately
        console.log('👁️ Tab became visible - checking ownership');
        checkOwnership();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (ownershipCheckIntervalRef.current) {
        clearInterval(ownershipCheckIntervalRef.current);
        ownershipCheckIntervalRef.current = null;
      }
      if (chatChannel) {
        chatChannel.close();
      }
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      stopOwnershipHeartbeat();
    };
  }, [isActiveTab, isPaused, minimized, sessionId, userSentMessage, showFormSuccessModal, isEmbedFullLayout]);

  useEffect(() => {
    // Don't initialize until we've heard back from parent (or timeout)
    if (waitingForParentSession) return;

    // NEW BEHAVIOR: "Follow Me" - New tabs ALWAYS claim ownership immediately
    // This makes the chat active on the most recently opened/focused tab
    // Other tabs will automatically become paused when they detect the ownership change
    console.log('🆕 New tab initialization - claiming ownership immediately ("follow me" behavior)');

    let claimedOwnership = false;
    const shouldInitialize = !isActiveTab;

    if (shouldInitialize) {
      // Always claim ownership on new tab/refresh - implement "follow me" behavior
      console.log('🎯 Claiming ownership for this tab');
      claimTabOwnership();
      claimedOwnership = true;
      setIsPaused(false); // Ensure not paused when we claim ownership

      // Apply minimized state: Mobile always starts minimized, Desktop inherits global state
      const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

      if (isEmbedFullLayout) {
        setMinimized(false);
        setUserToggledMinimized(true);
      } else if (isMobile) {
        const customizationNow = configRef.current && configRef.current.customization;
        if (shouldStartOnWelcome(customizationNow)) {
          setWelcomeVisible(true);
          setMinimized(false);
        } else if (!customizationNow) {
          // Config is still loading. Stay on the launcher without persisting, so an
          // Open agent with the welcome step can show once customization arrives.
          setMinimized(true);
        } else {
          // MOBILE: Always start new tabs minimized (Option A - only new tabs, not switching)
          console.log('📱 Mobile - forcing new tab to start minimized');
          setMinimized(true);
          setUserToggledMinimized(true);
          localStorage.setItem('widgetMinimized', 'true');
        }
      } else {
        // DESKTOP: Keep existing behavior - inherit global minimized state
        const globalMinimized = localStorage.getItem('widgetMinimized');
        if (globalMinimized !== null) {
          const shouldBeMinimized = globalMinimized === 'true';
          console.log('🖥️ Desktop - applying global minimized state:', shouldBeMinimized);
          setMinimized(shouldBeMinimized);
          setUserToggledMinimized(true); // Mark as user-toggled to prevent config override
        }
      }

      // Note: setIsActiveTab is called by claimTabOwnership
      // activeTabRef.current is set synchronously, so we can check it
    }

    // Only initialize WebSocket and fetch config if we're the active tab
    // But don't return - let the component render with config if available
    // Check both state and ref (ref is set synchronously, state is async)
    const isActive = isActiveTab || activeTabRef.current || claimedOwnership;
    if (!isActive) {
      // Not active - just load config but don't initialize WebSocket
      // This ensures all tabs can render the widget
      const agent = getAgentFromHash();
      let tenant = getTenantFromHash();
      const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
      const theme = params.get('theme');
      const origin = getOrigin();
      const API_BASE = BASE_URL;

      if (tenant && !isNaN(tenant)) tenant = parseInt(tenant, 10);

      // Fetch config so widget can render
      const loadConfig = async () => {
        try {
          const cfgRes = await fetch(`${API_BASE}/api/public/chatbot-config?agent=${encodeURIComponent(agent)}&tenant=${encodeURIComponent(tenant)}&origin=${encodeURIComponent(origin)}`);
          if (!cfgRes.ok) {
            let errorMessage = 'Not allowed or config not found';
            if (cfgRes.status === 406) {
              errorMessage = '';
              if (window.parent !== window) {
                window.parent.postMessage({ type: 'closeSlickbot' }, parentOriginRef.current || '*');
              }
            }
            try {
              const errorData = await cfgRes.json();
              if (errorData && errorData.error) {
                errorMessage = errorData.error;
              }
            } catch (e) {
              console.warn('Failed to parse error response:', e);
            }
            throw new Error(errorMessage);
          }
          const cfg = await cfgRes.json();
          setConfig({ ...cfg, theme, tenant });
          setAgentId(cfg.agentId);
          setTenantId(cfg.tenantId);
          if (cfg.customization && cfg.customization.presetResponses) {
            const activePresets = cfg.customization.presetResponses.filter(preset => preset.active);
            setPresetResponses(activePresets);
          }

          // Load messages from localStorage for display
          const storedMessages = localStorage.getItem('widgetMessages');
          if (storedMessages) {
            try {
              const parsed = JSON.parse(storedMessages);
              if (Array.isArray(parsed)) {
                const validMessages = parsed.filter(m => {
                  if (!m || typeof m !== 'object') return false;
                  if (!m.text || typeof m.text !== 'string' || m.text.trim() === '') return false;
                  if (!m.role || (m.role !== 'user' && m.role !== 'bot')) return false;
                  return true;
                }).map(normalizeCalcomHistoryMessage);
                setMessages(validMessages);
                if (validMessages.some(m => m.role === 'user')) {
                  setUserSentMessage(true);
                }
              }
            } catch (e) {
              console.error('❌ Error parsing stored messages:', e);
            }
          }

          // Restore sessionId from localStorage for display
          const storedSessionId = parentSessionId || localStorage.getItem('widgetSessionId');
          if (storedSessionId) {
            setSessionId(storedSessionId);
          }

          // Restore liveAgentName
          const storedLiveAgentName = localStorage.getItem('widgetLiveAgentName');
          if (storedLiveAgentName && storedSessionId) {
            setLiveAgentName(storedLiveAgentName);
          }

          // Restore global minimized state for non-active tabs
          if (!isEmbedFullLayout) {
            const globalMinimized = localStorage.getItem('widgetMinimized');
            if (globalMinimized !== null) {
              const shouldBeMinimized = globalMinimized === 'true';
              console.log('🔄 Non-active tab - applying global minimized state:', shouldBeMinimized);
              setMinimized(shouldBeMinimized);
              setUserToggledMinimized(true); // Prevent config override
            }
          }
        } catch (err) {
          setError(err.message);
        }
      };
      loadConfig();
      return; // Don't continue with WebSocket initialization
    }

    const agent = getAgentFromHash();
    let tenant = getTenantFromHash();
    const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const theme = params.get('theme');
    const origin = getOrigin();
    const API_BASE = BASE_URL;

    if (tenant && !isNaN(tenant)) tenant = parseInt(tenant, 10);

    // If this is a newly opened tab, request the latest messages from other tabs
    // Restore sessionId, messages, and minimized state from localStorage
    // Prefer parent-provided session over iframe's own storage
    let storedSessionId = parentSessionId || localStorage.getItem('widgetSessionId');
    const storedMessages = localStorage.getItem('widgetMessages');
    const storedMessagesTimestamp = localStorage.getItem('widgetMessagesTimestamp');
    const storedMinimized = getInitialMinimized() ? 'true' : localStorage.getItem('widgetMinimized');

    // Keep a startup baseline timestamp in a ref so we can compare remote broadcasts
    const startupMessagesTs = storedMessagesTimestamp ? Number(storedMessagesTimestamp) : 0;
    // store baseline for later comparison
    try { localStorage.setItem('widgetMessagesTimestamp', String(startupMessagesTs)); } catch (e) { }

    if (storedMessages) {
      try {
        const parsed = JSON.parse(storedMessages);
        // Validate that parsed is an array and each message has valid structure
        if (Array.isArray(parsed)) {
          const validMessages = parsed.filter(m => {
            // Ensure message has required fields and valid text
            if (!m || typeof m !== 'object') return false;
            if (!m.text || typeof m.text !== 'string' || m.text.trim() === '') return false;
            if (!m.role || (m.role !== 'user' && m.role !== 'bot')) return false;
            return true;
          }).map(normalizeCalcomHistoryMessage);
          setMessages(validMessages);
          if (validMessages.some(m => m.role === 'user')) {
            setUserSentMessage(true);
          }

          // Scroll to bottom after loading messages (with delay to ensure DOM is ready)
          if (validMessages.length > 0) {
            setTimeout(() => {
              messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
            }, 100);
          }
        } else {
          console.warn('⚠️ Stored messages is not an array, clearing');
          setMessages([]);
        }
      }
      catch (e) {
        console.error('❌ Error parsing stored messages:', e);
        setMessages([]);
      }
    }
    // Apply stored minimized state, but on mobile ALWAYS stay minimized (embed full: always expanded)
    if (!isEmbedFullLayout && storedMinimized !== null) {
      const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (isMobile) {
        // Mobile: ignore stored state, always minimized
        setMinimized(true);
      } else {
        // Desktop: use stored state
        setMinimized(storedMinimized === 'true');
      }
    }

    // Restore liveAgentName from localStorage if session exists
    const storedLiveAgentName = localStorage.getItem('widgetLiveAgentName');
    if (storedLiveAgentName && storedSessionId) {
      setLiveAgentName(storedLiveAgentName);
    }

    // NOTE: Old cross-tab message sync via BroadcastChannel has been removed.
    // This component now uses single-active-tab architecture.
    // Only ownership changes are communicated via BroadcastChannel (handled in a separate useEffect).

    // Validate session if present
    const validateAndStart = async () => {
      let validSession = false;
      if (storedSessionId) {
        try {
          const checkUrl = `${API_URL}/public/chat/check-session/${storedSessionId}?tenant=${tenant}`;
          // console.log('🔍 Validating session:', storedSessionId);
          // console.log('🔍 Check URL:', checkUrl);
          const res = await fetch(checkUrl);
          // console.log('🔍 Session validation response:', res.status, res.ok);
          if (res.ok) {
            validSession = true;
            setSessionId(storedSessionId);
            // console.log('✅ Session is valid, restoring:', storedSessionId);
          } else {
            // Session is invalid/expired
            // console.log('❌ Session is invalid/expired, clearing:', storedSessionId);
            localStorage.removeItem('widgetSessionId');
            localStorage.removeItem(WIDGET_CONVERSATION_STARTED_KEY);
            localStorage.removeItem('widgetSessionStartTime');
            localStorage.removeItem('widgetMessages');
            localStorage.removeItem('widgetMinimized');
            setSessionId(null);
            storedSessionId = null;
          }
        } catch (e) {
          // console.log('❌ Session validation error:', e);
          localStorage.removeItem('widgetSessionId');
            localStorage.removeItem(WIDGET_CONVERSATION_STARTED_KEY);
          setSessionId(null);
          storedSessionId = null;
        }
      }
      // Fetch public config from backend, including domain allow-list check
      try {
        const cfgRes = await fetch(`${API_BASE}/api/public/chatbot-config?agent=${encodeURIComponent(agent)}&tenant=${encodeURIComponent(tenant)}&origin=${encodeURIComponent(origin)}`);
        if (!cfgRes.ok) {
          let errorMessage = 'Not allowed or config not found';
          if (cfgRes.status === 406) {
            console.log('Warn: Agent is disabled. Or Config not found');
            errorMessage = '';
            if (window.parent !== window) {
              window.parent.postMessage({ type: 'closeSlickbot' }, parentOriginRef.current || '*');
            }
          }
          // Try to read error message from response body
          try {
            const errorData = await cfgRes.json();
            if (errorData && errorData.error) {
              errorMessage = errorData.error;
            }
          } catch (e) {
            // If JSON parsing fails, use default message
            console.warn('Failed to parse error response:', e);
          }
          throw new Error(errorMessage);
        }
        const cfg = await cfgRes.json();
        setConfig({ ...cfg, theme, tenant });
        // Set agentId and tenantId from the config response for WebSocket operations
        setAgentId(cfg.agentId);
        setTenantId(cfg.tenantId);
        if (cfg.customization && cfg.customization.presetResponses) {
          const activePresets = cfg.customization.presetResponses.filter(preset => preset.active);
          setPresetResponses(activePresets);
        }

        // If we have a valid session stored, fetch canonical server history first so behind tabs catch up
        // Leader decides whether to initialize WebSocket
        if (validSession && storedSessionId && !shouldStartOnWelcome(cfg.customization)) {
          // Restore minimized state from localStorage and mark as user-toggled to prevent config override
          if (!isEmbedFullLayout) {
            const storedMinimizedState = localStorage.getItem('widgetMinimized');
            if (storedMinimizedState !== null) {
              setMinimized(storedMinimizedState === 'true');
              setUserToggledMinimized(true); // Prevent config from overriding user's last state
            }
          }

          try {
            const historyUrl = `${API_URL}/chat-sessions/session-history/${storedSessionId}?tenantId=${cfg.tenantId}`;
            const historyRes = await fetch(historyUrl);
            if (historyRes.ok) {
              const json = await historyRes.json();
              if (json && Array.isArray(json.messages) && json.messages.length > 0) {
                // Mark that history was loaded - this will suppress greeting messages
                hasHistoryLoadedRef.current = true;

                // Normalize server messages timestamps to ISO for consistent dedupe
                const serverArr = json.messages.map(m =>
                  normalizeCalcomHistoryMessage({ ...m, timestamp: new Date(m.timestamp).toISOString() }));

                // Server is canonical - use only server messages, discard local messages
                // This ensures we always have exactly what the server has (no duplicates from merging)
                const dedupeArray = (arr) => {
                  // Sort first to ensure chronological order for neighbor check
                  const sorted = [...arr].sort((a, b) => normalizeTs(a.timestamp) - normalizeTs(b.timestamp));
                  const deduped = [];

                  for (let i = 0; i < sorted.length; i++) {
                    const current = sorted[i];
                    const prev = deduped.length > 0 ? deduped[deduped.length - 1] : null;

                    if (prev) {
                      const prevKey = messageKey(prev);
                      const currentKey = messageKey(current);
                      // If content matches perviously added message
                      if (prevKey === currentKey) {
                        const prevTs = normalizeTs(prev.timestamp);
                        const currentTs = normalizeTs(current.timestamp);
                        // Only filter out if it's very close in time (duplicate API record)
                        // 2 seconds window for history API duplicates
                        if (Math.abs(currentTs - prevTs) < 2000) {
                          continue;
                        }
                      }
                    }
                    deduped.push(current);
                  }
                  return deduped;
                };

                // Deduplicate server messages to handle any duplicates from API
                const dedupedServer = dedupeArray(serverArr);

                // Use only server messages (server is the source of truth)
                // Sort by timestamp to maintain chronological order
                const merged = dedupedServer.sort((a, b) => normalizeTs(a.timestamp) - normalizeTs(b.timestamp));

                const mergedLastTs = merged.length ? normalizeTs(merged[merged.length - 1].timestamp) : 0;

                // Always update when server history is fetched (server is canonical)
                // Local messages are discarded - server is the single source of truth
                if (merged.length > 0) {
                  setMessages(merged);
                  if (merged.some(m => m.role === 'user')) {
                    setUserSentMessage(true);
                  }
                  try {
                    // Persist and broadcast the server snapshot (replaces local messages)
                    persistAndBroadcast(merged);
                  } catch (e) {
                    // Fallback: direct localStorage write
                    try {
                      localStorage.setItem('widgetMessages', JSON.stringify(merged));
                      localStorage.setItem('widgetMessagesTimestamp', String(mergedLastTs || Date.now()));
                    } catch (ee) { /* ignore */ }
                  }

                  // Scroll to bottom after loading messages (with delay to ensure DOM is ready)
                  setTimeout(() => {
                    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
                  }, 150);
                }
              }
            }
          } catch (e) {
            // Ignore history fetch failures and continue
          }
          // Reconnect immediately for existing session (faster reconnection)
          // Only if we're the active tab (checked above)
          initializeWebSocket(cfg.agentId, cfg.tenantId, storedSessionId);
        } else if (shouldStartOnWelcome(cfg.customization)) {
          setWelcomeVisible(true);
          setMinimized(false);
        } else {
          // Create new session
          // Only if we're the active tab (checked above)
          initializeWebSocket(cfg.agentId, cfg.tenantId);
        }
      } catch (err) {
        setError(err.message);
      }
    };
    validateAndStart();
  }, [waitingForParentSession, parentSessionId]);

  // Post actual rendered size to parent for iframe resizing
  useEffect(() => {
    if (!(config && window.parent !== window)) return;
    if (isEmbedFullLayout) return;

    const updateSize = () => {
      // Detect mobile device
      const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

      if (minimized) {
        // Check if closed agent text should be shown
        const currentCustomization = config && config.customization ? config.customization : {};
        const showClosedText = currentCustomization && currentCustomization.showBubble === true && (isMobile ? (currentCustomization.showBubbleInMobile === true) : true);

        // Base size: 100x100 (60px icon + 20px padding on each side)
        let width = 100;
        let height = 100;

        // If bubble is visible, increase height to accommodate it
        if (showClosedText) {
          // Increase height to show bubble above icon
          // Bubble is positioned absolutely above, so we need extra space
          height = 220; // Enough space for bubble (~120px) + icon (100px)
          width = Math.max(width, 280); // Enough width for bubble (260px max + padding)
        }

        window.parent.postMessage(
          {
            type: 'slickbots-resize',
            view: 'minimized',
            minimized: true,
            chatWidth: width,
            chatHeight: height
          },
          parentOriginRef.current || '*'
        );
        return;
      }

      if (welcomeVisible && !isMobileDevice()) {
        const intermediate = getIntermediateConfig(config.customization || {});
        window.parent.postMessage(
          {
            type: 'slickbots-resize',
            view: 'intermediate',
            minimized: false,
            chatWidth: intermediate.width,
            chatHeight: intermediate.height
          },
          parentOriginRef.current || '*'
        );
        return;
      }

      // When not minimized
      if (isMobile) {
        // On mobile, send fullscreen dimensions when not minimized
        // Use screen dimensions or a large value to ensure fullscreen is triggered
        // widget.js checks if width > 300 or height > 250 to determine fullscreen
        const fullWidth = window.screen.width || window.outerWidth || 9999;
        const fullHeight = window.screen.height || window.outerHeight || 9999;
        window.parent.postMessage(
          {
            type: 'slickbots-resize',
            view: 'open',
            minimized: false,
            chatWidth: fullWidth,
            chatHeight: fullHeight
          },
          parentOriginRef.current || '*'
        );
      } else {
        // On desktop, send customization values (always send, even if defaults)
        const { chatWidth, chatHeight } = config.customization || {};
        window.parent.postMessage(
          {
            type: 'slickbots-resize',
            view: 'open',
            minimized: false,
            chatWidth: chatWidth || 350,
            chatHeight: chatHeight || 520
          },
          parentOriginRef.current || '*'
        );
      }
    };

    // Immediate update for state changes (minimized toggle)
    updateSize();

    // Also update with a small delay to ensure DOM is ready (for initial load)
    const timeoutId = setTimeout(updateSize, 50);

    // Also update on window resize for mobile fullscreen
    const handleResize = () => {
      if (!minimized) {
        const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        if (welcomeVisible && !isMobile && window.parent !== window) {
          const intermediate = getIntermediateConfig((config && config.customization) || {});
          window.parent.postMessage(
            {
              type: 'slickbots-resize',
              view: 'intermediate',
              minimized: false,
              chatWidth: intermediate.width,
              chatHeight: intermediate.height
            },
            parentOriginRef.current || '*'
          );
          return;
        }
        if (isMobile && window.parent !== window) {
          const fullWidth = window.screen.width || window.outerWidth || 9999;
          const fullHeight = window.screen.height || window.outerHeight || 9999;
          window.parent.postMessage(
            {
              type: 'slickbots-resize',
              view: 'open',
              minimized: false,
              chatWidth: fullWidth,
              chatHeight: fullHeight
            },
            parentOriginRef.current || '*'
          );
        } else if (!isMobile && window.parent !== window) {
          // Also update desktop on resize
          const { chatWidth, chatHeight } = config && config.customization ? config.customization : {};
          window.parent.postMessage(
            {
              type: 'slickbots-resize',
              view: 'open',
              minimized: false,
              chatWidth: chatWidth || 350,
              chatHeight: chatHeight || 520
            },
            '*'
          );
        }
      }
    };
    window.addEventListener('resize', handleResize);

    // Update when minimized state or config changes
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [config, minimized, welcomeVisible, isEmbedFullLayout]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    // Only scroll and persist if we're the active tab
    if (!isActiveTab) return;

    scrollToBottom();
    // Persist messages to localStorage immediately (no cross-tab sync needed)
    // This ensures welcome messages are preserved even if user sends message quickly
    try {
      // Filter out thinking/waiting messages and validate before persisting
      const toPersist = messages.filter(m => {
        if (m.isThinking || m.isWaiting) return false;
        if (!m || typeof m !== 'object') return false;
        if (!m.text || typeof m.text !== 'string' || m.text.trim() === '') return false;
        if (!m.role || (m.role !== 'user' && m.role !== 'bot')) return false;
        return true;
      });

      // Always persist messages immediately, even if just the welcome message
      // This prevents timing issues where user sends first message before persistence runs
      localStorage.setItem('widgetMessages', JSON.stringify(toPersist));
      if (toPersist.length > 0) {
        const lastTs = normalizeTs(toPersist[toPersist.length - 1].timestamp);
        localStorage.setItem('widgetMessagesTimestamp', String(lastTs));
      } else {
        // Clear timestamp if no messages
        localStorage.removeItem('widgetMessagesTimestamp');
      }
    } catch (e) {
      console.error('❌ Error persisting messages in useEffect:', e);
    }
  }, [messages, isActiveTab]);

  // Auto-scroll during streaming when currentBotMessage changes
  useEffect(() => {
    if (isStreaming && currentBotMessage) {
      scrollToBottom();
    }
  }, [currentBotMessage, isStreaming]);

  // Keep currentBotMessageRef in sync with currentBotMessage state
  useEffect(() => {
    currentBotMessageRef.current = currentBotMessage;
  }, [currentBotMessage]);

  // Helper function to scroll to bottom with throttling during streaming
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Throttled scroll function for streaming
  const throttledScrollToBottom = () => {
    if (scrollThrottleRef.current) {
      clearTimeout(scrollThrottleRef.current);
    }
    scrollThrottleRef.current = setTimeout(() => {
      scrollToBottom();
    }, 50); // Throttle to every 50ms during streaming
  };

  // Streaming flush scheduler: periodically flush streamingBufferRef to currentBotMessage
  const ensureStreamingFlushRunning = (interval = 120) => {
    if (streamingFlushIntervalRef.current) return;
    streamingFlushIntervalRef.current = setInterval(() => {
      if (!streamingBufferRef.current) return;
      const bufferContent = streamingBufferRef.current;
      setCurrentBotMessage(prev => {
        const updated = (prev || '') + bufferContent;
        currentBotMessageRef.current = updated; // Update ref synchronously
        return updated;
      });
      streamingBufferRef.current = '';
      throttledScrollToBottom();
    }, interval);
  };

  const stopStreamingFlush = () => {
    if (streamingFlushIntervalRef.current) {
      clearInterval(streamingFlushIntervalRef.current);
      streamingFlushIntervalRef.current = null;
    }
  };

  // Session lifecycle management
  useEffect(() => {
    if (sessionId && config && config.customization) {
      // Store session in localStorage for persistence
      localStorage.setItem('widgetSessionId', sessionId);
      localStorage.setItem('widgetSessionStartTime', Date.now().toString());

      // WebSocket handles its own heartbeat - no HTTP heartbeat needed

      // Start idle detection if enabled
      // Check last activity from localStorage and use it to calculate proper timeout
      if (config.customization.idleDetectionEnabled) {
        const lastActivityStr = localStorage.getItem('lastWidgetActivity');
        if (lastActivityStr) {
          const lastActivity = parseInt(lastActivityStr, 10);
          if (!isNaN(lastActivity)) {
            lastActivityRef.current = lastActivity;
          }
        }
        // Update activity to reset timer based on last activity time
        updateActivity();
      }

      // Cleanup on unmount or session change
      return () => {
        clearIdleTimers();
        stopHeartbeat();
      };
    }
  }, [sessionId, config]);

  // Handle page unload - release ownership and end session if explicitly closed
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Release ownership when tab closes
      releaseTabOwnership();

      // Only end session if it was explicitly closed (not just tab close)
      // Session will persist in localStorage for resume
      // We don't end session on beforeunload - let heartbeat timeout handle it
      // or let the user explicitly close via END CHAT button
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []); // Only set up once

  // Handle minimize/maximize state changes for session management
  useEffect(() => {
    if (!config || !config.customization) return;

    // Persist last launcher state across pages/tabs. After an explicit
    // user close/minimize, userToggledMinimized keeps it saved even with
    // no session. Automatic idle collapse does not set that flag, so the
    // next visit can follow the agent setting again.
    const view = minimized ? 'minimized' : (welcomeVisible ? 'intermediate' : 'open');
    const hasActiveSession = !!(sessionId || localStorage.getItem('widgetSessionId'));
    if (hasActiveSession || userToggledMinimized) {
      localStorage.setItem('widgetMinimized', minimized.toString());
      localStorage.setItem(WIDGET_CHAT_VIEW_KEY, view);
      console.log('💾 Saved global minimized state:', minimized, view);
    }

    // Broadcast minimized state to all tabs for immediate sync
    if (window.BroadcastChannel) {
      try {
        const chatChannel = new window.BroadcastChannel('slickbots-chat');
        chatChannel.postMessage({
          type: 'minimized-sync',
          minimized: minimized,
          view: view,
          clientId: clientIdRef.current
        });
        chatChannel.close();
        console.log('📡 Broadcasted minimized state to all tabs:', minimized);
      } catch (e) {
        console.error('❌ Error broadcasting minimized state:', e);
      }
    }

    if (minimized) {
      clearIdleTimers();
    } else if (sessionId) {
      updateActivity();
    }
  }, [minimized, welcomeVisible, sessionId, config, userToggledMinimized]);

  // Session management - always create fresh session on page load
  // (Removed duplicate session creation logic)

  // DEPRECATED: HTTP session creation - kept for potential fallback scenarios only
  // WebSocket mode handles session creation automatically
  // Play sound utility - only plays on the currently active/visible tab
  const playSound = (type) => {
    if (isMutedRef.current) return;

    // Only play sound if this tab is currently visible/active
    if (document.hidden) {
      return;
    }

    const soundFile = type === 'sent'
      ? '/sounds/sent_default.wav'
      : '/sounds/rec_default.wav';
    try {
      const audio = new Audio(soundFile);
      audio.volume = 0.5;
      audio.play().catch(err => console.error('Error playing sound:', err));
    } catch (error) {
      console.error('Error creating audio:', error);
    }
  };

  // Format timestamp utility - forced to EST timezone (America/New_York)
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    const format = customization.timestampFormat || 'MM/DD/YYYY hh:mm a';

    // Force EST timezone (America/New_York handles EST/EDT automatically)
    const timeZone = 'America/New_York';
    const locale = 'en-US'; // Use consistent locale for date formatting

    switch (format) {
      case 'hh:mm a':
        return date.toLocaleTimeString(locale, {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
          timeZone: timeZone
        });
      case 'HH:mm':
        return date.toLocaleTimeString(locale, {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZone: timeZone
        });
      case 'MM/DD/YYYY hh:mm a':
        return date.toLocaleDateString(locale, {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
          timeZone: timeZone
        }) + ' ' + date.toLocaleTimeString(locale, {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
          timeZone: timeZone
        });
      case 'DD/MM/YYYY HH:mm':
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          timeZone: timeZone
        }) + ' ' + date.toLocaleTimeString(locale, {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZone: timeZone
        });
      case 'YYYY-MM-DD HH:mm':
        // For this format, use Intl.DateTimeFormat to get parts in EST timezone
        const formatter = new Intl.DateTimeFormat(locale, {
          timeZone: timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
        const parts = formatter.formatToParts(date);
        const year = parts.find(p => p.type === 'year').value;
        const month = parts.find(p => p.type === 'month').value;
        const day = parts.find(p => p.type === 'day').value;
        const hour = parts.find(p => p.type === 'hour').value;
        const minute = parts.find(p => p.type === 'minute').value;
        return `${year}-${month}-${day} ${hour}:${minute}`;
      default:
        return date.toLocaleTimeString(locale, { timeZone: timeZone });
    }
  };

  // Send message with activity tracking - WebSocket only
  const sendMessage = async () => {
    if (!input.trim() || !config) return;

    const messageText = input.trim();

    // Ensure WebSocket connection before sending
    const isConnected = await ensureWebSocketConnection(5000);

    if (!isConnected) {
      // Connection failed - keep message in input box so user can retry
      console.error('❌ Failed to establish WebSocket connection. Message not sent. Please try again.');
      return;
    }

    // Now we're connected - send the message
    const sendSuccess = sendWebSocketMessage(messageText);

    if (sendSuccess) {
      // Message sent successfully - clear input and hide preset responses
      setInput('');
      setShowPresetResponses(false);
    } else {
      // Send failed - keep message in input box so user can retry
      console.error('❌ Failed to send message. Please try again.');
      return;
    }
  };

  // (adjustTextareaHeight helper removed — InputBase handles autosizing)

  // Handle preset response clicks
  const handlePresetClick = async (presetText) => {
    // Track activity and send the preset response - force reset idle timer
    updateActivity(true);

    // Ensure WebSocket connection before sending
    const isConnected = await ensureWebSocketConnection(5000);

    if (!isConnected) {
      // Connection failed - preset message not sent
      console.error('❌ Failed to establish WebSocket connection. Preset message not sent. Please try again.');
      return;
    }

    // Now we're connected - send the preset message
    const sendSuccess = sendWebSocketMessage(presetText);

    if (sendSuccess) {
      // Message sent successfully - hide preset responses
      setShowPresetResponses(false);
    } else {
      // Send failed
      console.error('❌ Failed to send preset message. Please try again.');
      return;
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      // Prevent default newline insertion when Enter is used to send
      if (!e.shiftKey) {
        e.preventDefault();
        // Only send if not loading and input is not empty
        if (!loading && input.trim()) {
          sendMessage();
        }
      }
    } else {
      // Update activity on typing - force reset idle timer
      updateActivity(true);
    }
  };

  // Keep isMutedRef in sync
  React.useEffect(() => { isMutedRef.current = isMuted; }, [isMuted]);

  // Session management utilities - reset idle timer on any activity
  const updateActivity = (forceReset = false) => {
    const now = Date.now();

    // Always update lastActivity timestamp on any activity (message sent or received)
    lastActivityRef.current = now;
    localStorage.setItem('lastWidgetActivity', lastActivityRef.current.toString());

    // Reset idle state if user becomes active again
    if (isIdle) {
      setIsIdle(false);
      setIdleMessageShown(false);
    }

    // Always clear and restart idle timers on activity
    clearIdleTimers();
    if (config && config.customization && config.customization.idleDetectionEnabled && !minimized) {
      startIdleTimer();
    }
  };

  const clearIdleTimers = () => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const startIdleTimer = () => {
    if (!config || !config.customization) {
      return;
    }

    // Check if there was recent activity before starting the timer
    // If activity was very recent (within last 5 seconds), don't start timer yet
    // This prevents idle message from appearing immediately after page refresh or message
    const lastActivity = lastActivityRef.current || Date.now();
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivity;

    // Use configured timeout value (default 60 seconds for testing)
    const idleTimeoutMs = (config.customization.idleTimeout || 60) * 1000;

    // If there was very recent activity, adjust the timeout accordingly
    const adjustedTimeout = Math.max(0, idleTimeoutMs - timeSinceLastActivity);

    // Only set timer if we have time remaining (don't set if time has already elapsed)
    if (adjustedTimeout > 0) {
      idleTimerRef.current = setTimeout(() => {
        handleIdleTimeout();
      }, adjustedTimeout);
    }
  };

  const handleIdleTimeout = () => {
    if (!config || !config.customization || minimized) {
      return;
    }

    if (!idleMessageShown) {
      setIdleMessageShown(true);
      setIsIdle(true);

      // Add idle message
      const idleMsg = {
        role: 'bot',
        text: config.customization.idleMessage || "It looks like you're idle. Can I help you with anything else?",
        timestamp: new Date()
      };
      setMessages(prev => {
        return [...prev, idleMsg];
      });

      // Start close timer
      const closeTimeoutMs = (config.customization.closeIdleTimeout || 300) * 1000;
      closeTimerRef.current = setTimeout(() => {
        handleCloseTimeout();
      }, closeTimeoutMs);
    }
  };

  const handleCloseTimeout = () => {
    if (!config || !config.customization) return;

    // Add close message
    const closeMsg = {
      role: 'bot',
      text: config.customization.closeIdleMessage || "Thank you for chatting with us, please reconnect at anytime.",
      timestamp: new Date()
    };
    setMessages(prev => [...prev, closeMsg]);

    // End session and close chat
    setTimeout(() => {
      endSession('closed'); // This clears session, localStorage, and notifies parent
      if (!isEmbedFullLayout) {
        setMinimized(true);
      }
      setIsIdle(false);
      setIdleMessageShown(false);

      // Force cleanup to ensure session is completely cleared after idle timeout
      setSessionId(null);
      setLiveAgentName(null); // Reset to bot agent name

      // Force clear localStorage to be absolutely sure
      try {
        localStorage.removeItem('widgetSessionId');
        localStorage.removeItem(WIDGET_CONVERSATION_STARTED_KEY);
        localStorage.removeItem('widgetSessionStartTime');
        localStorage.removeItem('lastWidgetActivity');
        localStorage.removeItem('widgetLiveAgentName');
        // Idle close is not an explicit visitor minimize/close — only keep
        // a minimized lock if the visitor already chose it.
        if (localStorage.getItem('widgetMinimized') !== 'true') {
          localStorage.removeItem('widgetMinimized');
        }
      } catch (e) {
        console.error('❌ Error clearing localStorage:', e);
      }

      // Force notify parent one more time to ensure session is cleared
      if (window.parent !== window) {
        try {
          window.parent.postMessage({
            type: 'slickbots-session-changed',
            sessionId: null
          }, '*');
        } catch (e) {
          console.error('❌ Error notifying parent:', e);
        }
      }
    }, 1000);
  };

  const endSession = async (reason = 'closed', sessionIdOverride = null, opts = {}) => {
    // Use provided sessionId override, or fall back to state, or localStorage
    const effectiveSessionId = sessionIdOverride || sessionId || localStorage.getItem('widgetSessionId');
    if (!effectiveSessionId) {
      console.log('⚠️ No session to end');
      return;
    }

    console.log('🔚 Ending session:', effectiveSessionId, 'Reason:', reason);

    try {
      // End LiveKit voice call so the agent does not keep using this sessionId
      await endVoiceCall();

      // Mark as intentional disconnect before closing WebSocket
      intentionalDisconnectRef.current = true;

      // Close WebSocket first if it exists
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close(1000, 'Session ended'); // Normal closure
        wsRef.current = null;
        setWs(null);
      }

      // Call backend to end session (works for both HTTP and WebSocket modes)
      await fetch(`${API_URL}/public/chat/end-session/${effectiveSessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reason: reason,
          tenantId: tenantId || configRef.current?.tenantId || null,
          agentId: agentId || configRef.current?.agentId || null,
        })
      });

      // COMPLETE STATE CLEANUP - Clear ALL session-related state
      setMessages([]);
      setSessionId(null);
      setUserSentMessage(false);
      setIsIdle(false);
      setIdleMessageShown(false);
      setLiveAgentName(null);
      setShowPresetResponses(true); // Reset for next session

      // COMPLETE LOCALSTORAGE CLEANUP - Remove ALL session data
      localStorage.removeItem('widgetSessionId');
            localStorage.removeItem(WIDGET_CONVERSATION_STARTED_KEY);
      localStorage.removeItem('widgetSessionStartTime');
      localStorage.removeItem('lastWidgetActivity');
      localStorage.removeItem('widgetLiveAgentName');
      localStorage.removeItem('widgetMessages');
      localStorage.removeItem('widgetMessagesTimestamp');

      // Reset history flag so greeting can show on next session
      hasHistoryLoadedRef.current = false;

      // Clear timers
      clearIdleTimers();
      stopHeartbeat();

      // Broadcast clear messages to all tabs
      try {
        if (window.BroadcastChannel) {
          const ch = new window.BroadcastChannel('slickbots-chat');
          ch.postMessage({ type: 'clear-messages', clientId: clientIdRef.current });
          ch.close();
        }
      } catch (e) {
        console.error('❌ Error broadcasting clear-messages:', e);
      }

      // Notify parent to clear session
      if (window.parent !== window) {
        window.parent.postMessage({
          type: 'slickbots-session-changed',
          sessionId: null
        }, '*');
      }

      // Reset streaming state completely
      isStreamingRef.current = false;
      setIsStreaming(false);
      setCurrentBotMessage('');
      currentBotMessageRef.current = '';
      streamingBufferRef.current = '';
      streamingAccumRef.current = '';
      if (streamTimeoutRef.current) {
        clearTimeout(streamTimeoutRef.current);
        streamTimeoutRef.current = null;
      }
      stopStreamingFlush();

      // Reset intentional disconnect flag after a delay
      setTimeout(() => {
        intentionalDisconnectRef.current = false;
      }, 2000);

      console.log('✅ Session ended and cleanup complete');
    } catch (error) {
      console.error('❌ Error ending session:', error);
      // Reset flag even on error
      setTimeout(() => {
        intentionalDisconnectRef.current = false;
      }, 2000);
    }
  };

  const startHeartbeat = () => {
    // This function is now deprecated - WebSocket mode handles its own heartbeat
    // Keeping for potential fallback scenarios only

    if (httpHeartbeatIntervalRef.current) {
      clearInterval(httpHeartbeatIntervalRef.current);
    }

    httpHeartbeatIntervalRef.current = setInterval(async () => {
      if (!sessionId) {
        stopHeartbeat();
        return;
      }

      try {
        const response = await fetch(`${API_URL}/public/chat/check-session/${sessionId}`);
        if (!response.ok) {
          // Session ended on server
          setSessionId(null);
          localStorage.removeItem('widgetSessionId');
            localStorage.removeItem(WIDGET_CONVERSATION_STARTED_KEY);
          stopHeartbeat();
        }
      } catch (error) {
        console.error('💔 HTTP heartbeat error:', error);
      }
    }, 60000); // 1 minute
  };

  const stopHeartbeat = () => {
    if (httpHeartbeatIntervalRef.current) {
      clearInterval(httpHeartbeatIntervalRef.current);
      httpHeartbeatIntervalRef.current = null;
    }
  };

  // Extract customization if present
  const customization = config && config.customization ? config.customization : {};
  const enableVoice = customization.enableVoice === true;
  // Waiting message logic: show '{Agent Name} {Customized Waiting Message}' if both set, else one, else fallback
  const agentName = customization.agentName || (config && config.botName) || '';
  const waitingMessage = customization.waitingMessage || '';
  let waitingText = '';
  if (agentName && waitingMessage) {
    waitingText = `${agentName} ${waitingMessage}`;
  } else if (agentName) {
    waitingText = agentName;
  } else if (waitingMessage) {
    waitingText = waitingMessage;
  } else {
    waitingText = 'Thinking';
  }

  // Header button handlers
  const handleClose = () => {
    // Show confirmation modal instead of immediately closing
    setRequestTranscript(false);
    setVisitorEmail('');
    setVisitorEmailError('');
    setShowCloseConfirm(true);

    // Broadcast to all tabs to show the close confirmation
    try {
      if (window.BroadcastChannel) {
        const ch = new window.BroadcastChannel('slickbots-chat');
        ch.postMessage({
          type: 'show-close-confirm',
          clientId: clientIdRef.current
        });
        ch.close();
      }
    } catch (e) {
      console.error('❌ Error broadcasting show-close-confirm:', e);
    }
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const submitVisitorTranscriptRequest = async (capturedSessionId) => {
    if (customization.visitorTranscriptEnabled !== true || !requestTranscript) {
      return true;
    }

    const trimmedEmail = visitorEmail.trim();
    if (!trimmedEmail) {
      setVisitorEmailError('Email is required.');
      return false;
    }
    if (!isValidEmail(trimmedEmail)) {
      setVisitorEmailError('Please enter a valid email address.');
      return false;
    }

    const effectiveTenantId = tenantId || configRef.current?.tenantId || null;
    if (!effectiveTenantId || !capturedSessionId) {
      setVisitorEmailError('Unable to save transcript request for this chat session.');
      return false;
    }

    try {
      setSendingTranscript(true);
      const response = await fetch(`${API_URL}/public/chat/transcript-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: effectiveTenantId,
          sessionId: capturedSessionId,
          email: trimmedEmail,
        })
      });

      if (!response.ok) {
        let apiMsg = 'Unable to save transcript request. Please try again.';
        try {
          const errJson = await response.json();
          if (errJson && typeof errJson.message === 'string') {
            apiMsg = errJson.message;
          }
        } catch {
          // Ignore parse errors and keep fallback message.
        }
        setVisitorEmailError(apiMsg);
        return false;
      }

      setVisitorEmailError('');
      return true;
    } catch (err) {
      console.error('❌ Error saving transcript request:', err);
      setVisitorEmailError('Unable to save transcript request. Please try again.');
      return false;
    } finally {
      setSendingTranscript(false);
    }
  };

  const handleEndChat = async () => {
    // Capture sessionId for rating - try from state first, then localStorage as fallback
    const capturedSessionId = sessionId || localStorage.getItem('widgetSessionId');

    const transcriptOk = await submitVisitorTranscriptRequest(capturedSessionId);
    if (!transcriptOk) {
      return;
    }

    proceedWithEndChat(capturedSessionId);
  };

  const proceedWithEndChat = (capturedSessionId, opts = {}) => {
    const { skipRating = false } = opts;
    // Capture userSentMessage BEFORE any cleanup - this state will be cleared by endSession
    const hadUserMessage = userSentMessage;

    setRatingSessionId(capturedSessionId);

    // Pass the captured sessionId to endSession so it doesn't rely on state
    // Suppress broadcast because we are about to broadcast dismiss-close-confirm which triggers endSession everywhere
    endSession('closed', capturedSessionId, { broadcast: false });
    setShowCloseConfirm(false);
    setCloseConfirmText(null);
    setRequestTranscript(false);
    setVisitorEmail('');
    setVisitorEmailError('');

    // Broadcast to all tabs to dismiss the close confirmation dialog and end session
    // Include hadUserMessage so follower tabs know whether to show rating
    try {
      if (window.BroadcastChannel) {
        const ch = new window.BroadcastChannel('slickbots-chat');
        ch.postMessage({
          type: 'dismiss-close-confirm',
          action: 'end',
          capturedSessionId: capturedSessionId,
          hadUserMessage: skipRating ? false : hadUserMessage,
          clientId: clientIdRef.current
        });
        ch.close();
      }
    } catch (e) {
      console.error('❌ Error broadcasting dismiss-close-confirm:', e);
    }

    // Show rating dialog if user sent a message (use captured value)
    // Don't minimize yet - let rating dialog show first
    if (hadUserMessage && !skipRating) {
      if (showFormSuccessModal) {
        setShowFormSuccessModal(false); // Close the modal first
        setTimeout(() => setShowRatingDialog(true), 0); // Open rating after modal closes
      } else {
        setShowRatingDialog(true);
      }
    } else {
      // No rating needed - minimize immediately and sync across tabs
      if (!isEmbedFullLayout) {
        setMinimized(true);
        try {
          if (window.BroadcastChannel) {
            const ch = new window.BroadcastChannel('slickbots-chat');
            ch.postMessage({
              type: 'minimized-sync',
              minimized: true,
              clientId: clientIdRef.current
            });
            ch.close();
          }
        } catch (e) {
          console.error('❌ Error broadcasting minimized state:', e);
        }
      }
    }
  };

  const handleEmbedNewSession = () => {
    const capturedSessionId = sessionId || localStorage.getItem('widgetSessionId');
    proceedWithEndChat(capturedSessionId, { skipRating: true });
    setInput('');
    setLoading(false);
  };

  const handleRatingDialogClose = (minimize = true, broadcastToOthers = true) => {
    console.log('🧹 Rating dialog closing - performing complete cleanup', { minimize, broadcastToOthers });

    // Clear the thank you ref (in case it's still set)
    showingThankYouRef.current = false;

    // Close rating dialog
    setShowRatingDialog(false);
    setRatingSubmitted(false);
    setRatingSessionId(null);
    setRequestTranscript(false);
    setVisitorEmail('');
    setVisitorEmailError('');

    // COMPLETE STATE CLEANUP - Clear ALL session-related state
    setSessionId(null);
    setLiveAgentName(null);
    setMessages([]);
    setUserSentMessage(false);
    setIsIdle(false);
    setIdleMessageShown(false);
    setShowPresetResponses(true); // Reset for next session

    // Reset streaming state
    isStreamingRef.current = false;
    setIsStreaming(false);
    setCurrentBotMessage('');
    streamingBufferRef.current = '';
    streamingAccumRef.current = '';
    currentBotMessageRef.current = '';
    hasHistoryLoadedRef.current = false; // Allow greeting for next session

    // COMPLETE LOCALSTORAGE CLEANUP - Remove ALL chat-related data
    try {
      console.log('🧹 Clearing ALL localStorage chat data');
      localStorage.removeItem('widgetSessionId');
            localStorage.removeItem(WIDGET_CONVERSATION_STARTED_KEY);
      localStorage.removeItem('widgetSessionStartTime');
      localStorage.removeItem('lastWidgetActivity');
      localStorage.removeItem('widgetLiveAgentName');
      localStorage.removeItem('widgetMessages');
      localStorage.removeItem('widgetMessagesTimestamp');
      // Keep widgetMinimized so an explicit close stays minimized on later visits
      console.log('✅ localStorage cleared');
    } catch (e) {
      console.error('❌ Error clearing localStorage:', e);
    }

    // Broadcast complete cleanup to OTHER tabs only (not to self to avoid loop/interruption)
    if (broadcastToOthers) {
      try {
        if (window.BroadcastChannel) {
          const ch = new window.BroadcastChannel('slickbots-chat');
          ch.postMessage({
            type: 'complete-cleanup',
            minimize: minimize,
            clientId: clientIdRef.current
          });
          ch.close();
          console.log('📡 Broadcasted complete-cleanup to other tabs');
        }
      } catch (e) {
        console.error('❌ Error broadcasting complete-cleanup:', e);
      }
    }

    // Notify parent that session is cleared
    if (window.parent !== window) {
      try {
        window.parent.postMessage({
          type: 'slickbots-session-changed',
          sessionId: null
        }, '*');
      } catch (e) {
        console.error('❌ Error notifying parent:', e);
      }
    }

    // Minimize and broadcast
    setWelcomeVisible(false);
    if (minimize && !isEmbedFullLayout) {
      setUserToggledMinimized(true);
      setMinimized(true);
      try {
        localStorage.setItem(WIDGET_CHAT_VIEW_KEY, 'minimized');
      } catch (e) { /* ignore */ }
      try {
        if (window.BroadcastChannel) {
          const ch = new window.BroadcastChannel('slickbots-chat');
          ch.postMessage({
            type: 'minimized-sync',
            minimized: true,
            view: 'minimized',
            clientId: clientIdRef.current
          });
          ch.close();
        }
      } catch (e) {
        console.error('❌ Error broadcasting minimized state:', e);
      }
    }

    console.log('✅ Complete cleanup finished - ready for fresh session');
  };

  const handleRatingSubmit = async (rating, comment) => {
    if (!ratingSessionId) {
      console.error('❌ Rating submission failed - no session ID available');
      // Still show thank you message even if submission fails
      setRatingSubmitted(true);
      return;
    }

    console.log('⭐ Submitting rating:', rating);

    // Use numeric tenantId from state (set when config was loaded), not encrypted tenant from URL
    const requestBody = { tenantId: tenantId, rating, comment };
    const apiUrl = `${API_URL}/public/chat/rate-session/${ratingSessionId}`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const responseData = await response.text();
        console.log('✅ Rating submitted successfully');
      } else {
        const errorText = await response.text();
        console.error('❌ Rating submission failed with status:', response.status, response.statusText);
        console.error('❌ Error response:', errorText);
      }
    } catch (err) {
      console.error('❌ Rating submission error:', err);
      console.error('❌ Error details:', {
        message: err.message,
        name: err.name,
        stack: err.stack
      });
    }

    // Show thank you message
    setRatingSubmitted(true);
    showingThankYouRef.current = true; // Set ref synchronously to prevent premature minimize
    console.log('💬 Showing thank you message (ref set to block minimize)');

    // Broadcast to other tabs that rating was submitted
    // Other tabs will close immediately without showing thank you
    try {
      if (window.BroadcastChannel) {
        const ch = new window.BroadcastChannel('slickbots-chat');
        ch.postMessage({
          type: 'rating-submitted',
          clientId: clientIdRef.current
        });
        ch.close();
      }
    } catch (e) {
      console.error('❌ Error broadcasting rating-submitted:', e);
    }

    // NOTE: Thank you message will be shown by ChatRatingDialog component
    // The dialog will call onThankYouTimeout after displaying the message
    // That's when cleanup will happen (via handleRatingDialogClose)
  };

  const handleCancelClose = () => {
    setShowCloseConfirm(false);
    setCloseConfirmText(null);
    setRequestTranscript(false);
    setVisitorEmail('');
    setVisitorEmailError('');

    // Broadcast to all tabs to dismiss the close confirmation dialog
    try {
      if (window.BroadcastChannel) {
        const ch = new window.BroadcastChannel('slickbots-chat');
        ch.postMessage({
          type: 'dismiss-close-confirm',
          action: 'continue',
          clientId: clientIdRef.current
        });
        ch.close();
      }
    } catch (e) {
      console.error('❌ Error broadcasting dismiss-close-confirm:', e);
    }
  };

  // Fetch history from server and activate chat
  const fetchHistoryAndActivate = async () => {
    setIsActivating(true);

    console.log('🔄 fetchHistoryAndActivate - Starting activation from paused state');

    // 🧹 Clear thinking message immediately when Continue is clicked
    setLoadingWithBroadcast(false);

    // Remove any thinking messages from the array
    setMessages(prev => {
      const prevArr = Array.isArray(prev) ? prev : [];
      return prevArr.filter(m => !m.isThinking);
    });

    try {
      const storedSessionId = sessionIdRef.current || localStorage.getItem('widgetSessionId');
      if (!storedSessionId) {
        console.error('❌ No session ID available for history fetch');
        setIsActivating(false);
        setLoadingWithBroadcast(false); // Ensure loading is cleared
        return;
      }

      // Claim ownership first
      claimTabOwnership();

      // Get config for tenantId
      const cfg = configRef.current;
      if (!cfg || !cfg.tenantId) {
        console.error('❌ Config not available for history fetch');
        setIsActivating(false);
        setLoadingWithBroadcast(false); // Ensure loading is cleared
        return;
      }

      // Fetch history from server
      try {
        const historyUrl = `${API_URL}/chat-sessions/session-history/${storedSessionId}?tenantId=${cfg.tenantId}`;
        const historyRes = await fetch(historyUrl);

        if (historyRes.ok) {
          const json = await historyRes.json();
          if (json && Array.isArray(json.messages) && json.messages.length > 0) {
            // Normalize server messages timestamps
            const serverArr = json.messages.map(m =>
              normalizeCalcomHistoryMessage({ ...m, timestamp: new Date(m.timestamp).toISOString() }));

            // Deduplicate server messages
            const dedupeArray = (arr) => {
              const sorted = [...arr].sort((a, b) => normalizeTs(a.timestamp) - normalizeTs(b.timestamp));
              const deduped = [];

              for (let i = 0; i < sorted.length; i++) {
                const current = sorted[i];
                const prev = deduped.length > 0 ? deduped[deduped.length - 1] : null;

                if (prev) {
                  const prevKey = messageKey(prev);
                  const currentKey = messageKey(current);
                  if (prevKey === currentKey) {
                    const prevTs = normalizeTs(prev.timestamp);
                    const currentTs = normalizeTs(current.timestamp);
                    if (Math.abs(currentTs - prevTs) < 2000) {
                      continue;
                    }
                  }
                }
                deduped.push(current);
              }
              return deduped;
            };

            const dedupedServer = dedupeArray(serverArr);
            // 🧹 Filter out any thinking messages from server data
            const merged = dedupedServer
              .filter(m => !m.isThinking)
              .sort((a, b) => normalizeTs(a.timestamp) - normalizeTs(b.timestamp));

            if (merged.length > 0) {
              setMessages(merged);
              if (merged.some(m => m.role === 'user')) {
                setUserSentMessage(true);
              }

              // Persist messages
              try {
                localStorage.setItem('widgetMessages', JSON.stringify(merged));
                const mergedLastTs = merged.length ? normalizeTs(merged[merged.length - 1].timestamp) : Date.now();
                localStorage.setItem('widgetMessagesTimestamp', String(mergedLastTs));
              } catch (e) {
                console.error('❌ Error persisting messages:', e);
              }

              console.log('✅ History loaded, messages restored:', merged.length);

              // Scroll to bottom
              setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
              }, 150);
            }
          }
        } else {
          // Server fetch failed - fall back to localStorage
          console.warn('⚠️ History fetch failed, falling back to localStorage');
          const storedMessages = localStorage.getItem('widgetMessages');
          if (storedMessages) {
            try {
              const parsed = JSON.parse(storedMessages);
              if (Array.isArray(parsed) && parsed.length > 0) {
                // 🧹 Filter out any thinking messages from localStorage
                const filtered = parsed.filter(m => !m.isThinking).map(normalizeCalcomHistoryMessage);
                setMessages(filtered);
                if (filtered.some(m => m.role === 'user')) {
                  setUserSentMessage(true);
                }
                console.log('✅ History loaded from localStorage:', filtered.length);
                setTimeout(() => {
                  messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
                }, 150);
              }
            } catch (e) {
              console.error('❌ Error parsing stored messages:', e);
            }
          }
        }
      } catch (e) {
        console.error('❌ Error fetching history:', e);
        // Fall back to localStorage
        const storedMessages = localStorage.getItem('widgetMessages');
        if (storedMessages) {
          try {
            const parsed = JSON.parse(storedMessages);
            if (Array.isArray(parsed) && parsed.length > 0) {
              // 🧹 Filter out any thinking messages from localStorage
              const filtered = parsed.filter(m => !m.isThinking);
              setMessages(filtered);
              if (filtered.some(m => m.role === 'user')) {
                setUserSentMessage(true);
              }
              console.log('✅ History loaded from localStorage (error fallback):', filtered.length);
              setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
              }, 150);
            }
          } catch (err) {
            console.error('❌ Error parsing stored messages:', err);
          }
        }
      }

      // Connect WebSocket
      if (cfg.agentId && cfg.tenantId) {
        initializeWebSocket(cfg.agentId, cfg.tenantId, storedSessionId);
      }

      // 🧹 Ensure loading is cleared after activation
      setLoadingWithBroadcast(false);
      setIsActivating(false);

      console.log('✅ fetchHistoryAndActivate - Activation complete, chat ready for input');
    } catch (error) {
      console.error('❌ Error activating chat:', error);
      setLoadingWithBroadcast(false); // Ensure loading is cleared even on error
      setIsActivating(false);
    }
  };

  // Handle Continue button click
  const handleContinueClick = async () => {
    console.log('🔄 Continue button clicked - clearing thinking message and activating');

    // 🧹 Clear thinking message immediately
    setLoadingWithBroadcast(false);
    setMessages(prev => {
      const prevArr = Array.isArray(prev) ? prev : [];
      return prevArr.filter(m => !m.isThinking);
    });

    // Apply global minimized state before activating
    if (!isEmbedFullLayout) {
      const globalMinimized = localStorage.getItem('widgetMinimized');
      if (globalMinimized !== null) {
        const shouldBeMinimized = globalMinimized === 'true';
        console.log('🔄 Continue clicked - applying global minimized state:', shouldBeMinimized);
        setMinimized(shouldBeMinimized);
        setUserToggledMinimized(true);
      }
    }

    await fetchHistoryAndActivate();
  };

  const handleMinimize = () => {
    if (isEmbedFullLayout) return;
    setUserToggledMinimized(true);
    setWelcomeVisible(false);
    setMinimized(true);
    try {
      localStorage.setItem('widgetMinimized', 'true');
      localStorage.setItem(WIDGET_CHAT_VIEW_KEY, 'minimized');
    } catch (e) { /* ignore */ }
    if (window.parent !== window) {
      const isMobile = isMobileDevice();
      const currentCustomization = configRef.current && configRef.current.customization ? configRef.current.customization : {};
      const showClosedText = currentCustomization.showBubble === true && (isMobile ? currentCustomization.showBubbleInMobile === true : true);
      window.parent.postMessage(
        {
          type: 'slickbots-resize',
          view: 'minimized',
          minimized: true,
          chatWidth: showClosedText ? 280 : 100,
          chatHeight: showClosedText ? 220 : 100
        },
        parentOriginRef.current || '*'
      );
    }

    // Broadcast minimized state to all tabs for global sync
    try {
      if (window.BroadcastChannel) {
        const ch = new window.BroadcastChannel('slickbots-chat');
        ch.postMessage({
          type: 'minimized-sync',
          minimized: true,
          view: 'minimized',
          clientId: clientIdRef.current
        });
        ch.close();
      }
    } catch (e) {
      console.error('❌ Error broadcasting minimized state:', e);
    }

    // If the chat is being minimized after idle timeout criteria is met,
    // we should end the session
    if (isIdle && config && config.customization && config.customization.closeOnMinimize) {
      endSession('closed'); // This clears session, localStorage, and notifies parent

      // Force cleanup to ensure session is completely cleared
      setSessionId(null);
      setLiveAgentName(null); // Reset to bot agent name

      // Force clear localStorage to be absolutely sure
      try {
        localStorage.removeItem('widgetSessionId');
        localStorage.removeItem(WIDGET_CONVERSATION_STARTED_KEY);
        localStorage.removeItem('widgetSessionStartTime');
        localStorage.removeItem('lastWidgetActivity');
        localStorage.removeItem('widgetLiveAgentName');
      } catch (e) {
        console.error('❌ Error clearing localStorage:', e);
      }

      // Force notify parent one more time to ensure session is cleared
      if (window.parent !== window) {
        try {
          window.parent.postMessage({
            type: 'slickbots-session-changed',
            sessionId: null
          }, '*');
        } catch (e) {
          console.error('❌ Error notifying parent:', e);
        }
      }
    } else {
      // Just minimize normally - clear idle timers to prevent session ending while minimized
      clearIdleTimers();
    }
  };
  const handleFontSize = () => setFontSize(idx => (idx + 1) % fontSizes.length);
  const handleMute = () => setIsMuted(m => !m);
  const handleChatIconClick = async () => {
    setUserToggledMinimized(true);
    setShowBubble(true); // Reset bubble visibility when reopening chat

    // Claim ownership when maximizing (always claim to become active)
    if (!isActiveTab) {
      claimTabOwnership();
    }

    // Check if we're paused (chat active elsewhere)
    if (isPaused) {
      // Just maximize - the paused UI will show "Chat Active Elsewhere" with Continue button
      setWelcomeVisible(false);
      setMinimized(false);
      return;
    }

    const customizationNow = (configRef.current && configRef.current.customization) || customization;
    if (!isEmbedFullLayout && shouldOfferWelcome(customizationNow)) {
      setWelcomeVisible(true);
      setMinimized(false);
      return;
    }
    setWelcomeVisible(false);

    // Check if we have an active session (freshly check localStorage in case it was cleared)
    const storedSessionId = localStorage.getItem('widgetSessionId');
    const hasMessages = messages && messages.length > 0;

    if (!sessionId && !storedSessionId) {
      // No session - start completely fresh
      console.log('🆕 Starting fresh session - no previous session found');

      // Ensure state is completely clean for fresh start
      setMessages([]);
      setUserSentMessage(false);
      setShowPresetResponses(true);
      hasHistoryLoadedRef.current = false;

      if (agentId && tenantId) {
        initializeWebSocket(agentId, tenantId);
      } else {
        // Try getting from configRef if state is null
        const cfg = configRef.current;
        if (cfg && cfg.agentId && cfg.tenantId) {
          initializeWebSocket(cfg.agentId, cfg.tenantId);
        } else {
          console.warn('⚠️ Cannot initialize WebSocket - missing required values');
        }
      }
    } else if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      // We have a session but no active WebSocket - reconnect
      const sessionToUse = sessionId || storedSessionId;
      console.log('🔄 Reconnecting existing session:', sessionToUse);

      if (agentId && tenantId) {
        initializeWebSocket(agentId, tenantId, sessionToUse);
      } else {
        // Try getting from configRef
        const cfg = configRef.current;
        if (cfg && cfg.agentId && cfg.tenantId) {
          initializeWebSocket(cfg.agentId, cfg.tenantId, sessionToUse);
        } else {
          console.warn('⚠️ Cannot reconnect WebSocket - missing required values');
        }
      }
    } else {
      console.log('✅ WebSocket already connected, reusing existing session');
    }

    setMinimized(false);

    // Broadcast maximized state to all tabs
    try {
      if (window.BroadcastChannel) {
        const ch = new window.BroadcastChannel('slickbots-chat');
        ch.postMessage({
          type: 'minimized-sync',
          minimized: false,
          view: 'open',
          clientId: clientIdRef.current
        });
        ch.close();
      }
    } catch (e) {
      console.error('❌ Error broadcasting maximized state:', e);
    }

    // Immediately send resize message to parent to expand iframe (especially important on mobile)
    if (window.parent !== window && !isEmbedFullLayout) {
      const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (isMobile) {
        // On mobile, send fullscreen dimensions immediately
        // Use screen dimensions or a large value to ensure fullscreen is triggered
        // widget.js checks if width > 300 or height > 250 to determine fullscreen
        const fullWidth = window.screen.width || window.outerWidth || 9999;
        const fullHeight = window.screen.height || window.outerHeight || 9999;
        window.parent.postMessage(
          {
            type: 'slickbots-resize',
            view: 'open',
            minimized: false,
            chatWidth: fullWidth,
            chatHeight: fullHeight
          },
          parentOriginRef.current || '*'
        );
      } else {
        // On desktop, send customization values
        const { chatWidth, chatHeight } = config && config.customization ? config.customization : {};
        window.parent.postMessage(
          {
            type: 'slickbots-resize',
            view: 'open',
            minimized: false,
            chatWidth: chatWidth || 350,
            chatHeight: chatHeight || 520
          },
          parentOriginRef.current || '*'
        );
      }
    }

    // Restart session management timers when reopening
    updateActivity();
  };

  const handleStartConversation = () => {
    try {
      localStorage.setItem(WIDGET_CONVERSATION_STARTED_KEY, 'true');
      localStorage.setItem(WIDGET_CHAT_VIEW_KEY, 'open');
    } catch (e) { /* ignore */ }
    setWelcomeVisible(false);
    handleChatIconClick();
  };

  // Ensure WebSocket connection is established (connects if needed and waits for connection)
  const ensureWebSocketConnection = async (maxWaitMs = 5000) => {
    // If already connected, return immediately
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      return true;
    }

    // Ensure we're the active tab (necessary for WebSocket connection)
    if (!isActiveTab) {
      claimTabOwnership();
    }

    // Get agentId and tenantId
    const agId = configRef.current?.agentId || agentId;
    const tId = configRef.current?.tenantId || tenantId;

    if (!agId || !tId) {
      console.error('❌ Cannot connect WebSocket - missing agentId or tenantId');
      return false;
    }

    // Get sessionId if available
    const currentSessionId = sessionIdRef.current || localStorage.getItem('widgetSessionId');

    // If WebSocket exists but not connected, close it first
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      wsRef.current.close();
      wsRef.current = null;
    }

    // Initialize WebSocket connection
    initializeWebSocket(agId, tId, currentSessionId);

    // Wait for connection with timeout
    return new Promise((resolve) => {
      const startTime = Date.now();
      const checkInterval = setInterval(() => {
        // Check if connected
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          clearInterval(checkInterval);
          resolve(true);
          return;
        }

        // Check if timeout exceeded
        if (Date.now() - startTime >= maxWaitMs) {
          clearInterval(checkInterval);
          console.error('❌ WebSocket connection timeout');
          resolve(false);
          return;
        }

        // Check if connection failed (CLOSED state without ever opening)
        // Only fail if we had a socket that is now closed (not if it was never created)
        if (wsRef.current && wsRef.current.readyState === WebSocket.CLOSED && Date.now() - startTime > 1000) {
          // Give it at least 1 second before declaring failure, as CLOSED might be transient during connection
          clearInterval(checkInterval);
          console.error('❌ WebSocket connection closed/failed');
          resolve(false);
          return;
        }
      }, 100); // Check every 100ms
    });
  };

  // WebSocket initialization and management
  const initializeWebSocket = (agentId, tenantId, sessionId) => {
    // Only connect if we're the active tab
    // Check both state and ref (ref is set synchronously, state is async)
    if (!isActiveTab && !activeTabRef.current) {
      console.log('⚠️ Cannot connect WebSocket - tab is not active');
      return;
    }

    // If WebSocket already exists and is open/connecting, don't create a new one
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      console.log('✅ WebSocket already connected or connecting, skipping initialization');
      return;
    }

    // Clear intentional disconnect flag - we're intentionally connecting now
    intentionalDisconnectRef.current = false;

    // Respect page-load configuration on auto-start/reconnect:
    // only auto-expand when config says open and user hasn't manually toggled yet.
    const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const shouldAutoExpandFromConfig =
      configRef.current &&
    configRef.current.customization &&
      configRef.current.customization.onPageLoadChatState !== 'closed';
    const storedMinimizedPref = (() => {
      try {
        return localStorage.getItem('widgetMinimized');
      } catch (e) {
        return null;
      }
    })();
    const willAutoExpand = !isMobile && !userToggledMinimized && storedMinimizedPref !== 'true' && shouldAutoExpandFromConfig && !isEmbedFullLayout;
    if (willAutoExpand) {
      setMinimized(false);
    }
    // Mobile stays minimized; desktop "closed" stays minimized until user opens it
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      // Mark as intentional before closing
      intentionalDisconnectRef.current = true;
      wsRef.current.close();
      // Reset flag after close
      setTimeout(() => {
        intentionalDisconnectRef.current = false;
      }, 500);
    }

    // If this is a NEW session (no sessionId), prepare for greeting but DON'T clear yet
    // Let the session handler decide whether to clear based on whether there are stale messages
    if (!sessionId) {
      hasHistoryLoadedRef.current = false; // Allow greeting for new session

      console.log('🔍 initializeWebSocket - New session, preparing for greeting');
      console.log('   Current message count:', messages ? messages.length : 0);
      console.log('   Will wait for session message to decide if clearing is needed');

      // Reset preset responses to show them again for the new session
      setShowPresetResponses(true);

      // Reset streaming state to ensure greeting stream is handled cleanly
      isStreamingRef.current = false; // Update ref synchronously
      setIsStreaming(false);
      setCurrentBotMessage('');
      streamingBufferRef.current = '';
      streamingAccumRef.current = '';
      if (streamTimeoutRef.current) {
        clearTimeout(streamTimeoutRef.current);
        streamTimeoutRef.current = null;
      }
      stopStreamingFlush();
    }

    const WS_BASE = WS_URL;
    // Get current page URL to pass to backend for greeting matching
    const currentPageUrl = getPageUrlFromHash();
    const queryParams = [];
    if (sessionId) {
      queryParams.push(`sessionId=${encodeURIComponent(sessionId)}`);
    }
    if (currentPageUrl) {
      queryParams.push(`pageUrl=${encodeURIComponent(currentPageUrl)}`);
    }
    if (getEmbedFullFromHash()) {
      queryParams.push('embed=full');
    }
    const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
    const wsUrl = `${WS_BASE}/ws/public-chat/${encodeURIComponent(agentId)}/${encodeURIComponent(tenantId)}/0${queryString}`;
    try {
      const websocket = new WebSocket(wsUrl);
      wsRef.current = websocket;
      setWs(websocket);

      websocket.onopen = (event) => {
        if (window.BroadcastChannel && sessionId) {
          const chatChannel = new window.BroadcastChannel('slickbots-chat');
          chatChannel.postMessage({ type: 'session-sync', sessionId, clientId: clientIdRef.current });
          chatChannel.close();
        }

        // Note: We no longer flush queued messages here because sendMessage now waits for connection
        // Messages are sent immediately after connection is established in sendMessage
      };
      websocket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (err) {
          console.error('❌ Error parsing WebSocket message:', err);
          console.error('❌ Raw message data:', event.data);
        }
      };
      websocket.onclose = (event) => {
        setWs(null);
        wsRef.current = null;

        // Don't reconnect if this was an intentional disconnect (e.g., lost ownership)
        if (intentionalDisconnectRef.current) {
          console.log('🔌 WebSocket intentionally closed, not reconnecting');
          return;
        }

        // Check if session was intentionally ended (cleared from localStorage)
        // IMPORTANT: Only check localStorage, not sessionId from closure which may be stale
        const currentSessionId = localStorage.getItem('widgetSessionId');

        // Only reconnect if ALL conditions are met:
        // 1. We have a session
        // 2. We're the active tab (check ref for synchronous value)
        // 3. Not minimized (check ref for synchronous value)
        // 4. This wasn't an intentional disconnect
        const shouldReconnect = currentSessionId &&
          activeTabRef.current &&
          !minimizedRef.current &&
          !intentionalDisconnectRef.current;

        if (!shouldReconnect) {
          console.log('🔌 WebSocket closed, not reconnecting', {
            hasSession: !!currentSessionId,
            isActive: !!activeTabRef.current,
            isMinimized: minimizedRef.current,
            intentionalDisconnect: intentionalDisconnectRef.current
          });
          return;
        }

        // For normal closure (1000), reconnect faster as it's likely a page refresh
        // For abnormal closures, wait a bit longer
        const reconnectDelay = event.code === 1000 ? 1000 : 3000;

        console.log(`🔄 Scheduling reconnect in ${reconnectDelay}ms (code: ${event.code})`);

        setTimeout(() => {
          // Double-check all conditions again before reconnecting
          const stillHasSession = localStorage.getItem('widgetSessionId');
          const stillActive = activeTabRef.current;
          const stillMaximized = !minimizedRef.current;
          const notIntentional = !intentionalDisconnectRef.current;

          if (!wsRef.current && stillHasSession && stillActive && stillMaximized && notIntentional) {
            console.log('🔄 Reconnecting WebSocket...');
            // Use stored agentId/tenantId from state - they were set when config was loaded
            if (agentId && tenantId) {
              initializeWebSocket(agentId, tenantId, stillHasSession);
            }
          } else {
            console.log('🔌 Reconnect cancelled', {
              hasWs: !!wsRef.current,
              hasSession: !!stillHasSession,
              isActive: !!stillActive,
              isMaximized: stillMaximized,
              notIntentional
            });
          }
        }, reconnectDelay);
      };
      websocket.onerror = (error) => { };
    } catch (error) { }
  };

  const handleWebSocketMessage = (data) => {
    switch (data.type) {
      case 'session':
        // Check if this is a NEW session by comparing with current sessionId and localStorage
        const currentSessionId = sessionId || localStorage.getItem('widgetSessionId');
        const isNewSession = !currentSessionId || currentSessionId !== data.sessionId;

        console.log('📋 Session message received:', {
          newSessionId: data.sessionId,
          currentSessionId,
          isNewSession,
          currentMessageCount: messages ? messages.length : 0
        });

        setSessionId(data.sessionId);

        // Store session in localStorage for persistence
        localStorage.setItem('widgetSessionId', data.sessionId);

        // If this is a NEW session (not restored), only clear if we have stale messages from previous session
        if (isNewSession) {
          hasHistoryLoadedRef.current = false; // Allow greeting for new session

          // Check both state and localStorage for existing messages
          const stateHasMessages = messages && messages.length > 0;
          const existingMessages = localStorage.getItem('widgetMessages');
          const storageHasMessages = existingMessages && existingMessages !== '[]';

          // CRITICAL: For a truly new session (fresh start), DON'T clear empty messages
          // The greeting hasn't arrived yet! Only clear if there are STALE messages from old session
          const hasStaleMessages = stateHasMessages || storageHasMessages;

          console.log('🔍 Session handler - New session check:', {
            stateHasMessages,
            storageHasMessages,
            hasStaleMessages,
            action: hasStaleMessages ? 'clear stale messages' : 'wait for greeting'
          });

          if (hasStaleMessages) {
            console.log('🧹 Session handler: Clearing STALE messages from previous session');
            setMessages([]);
            localStorage.removeItem('widgetMessages');
            localStorage.removeItem('widgetMessagesTimestamp');

            // Reset rating-related states
            setRatingSubmitted(false);
            setShowRatingDialog(false);
            setRatingSessionId(null);

            // Broadcast to clear messages on follower tabs
            try {
              if (window.BroadcastChannel) {
                const ch = new window.BroadcastChannel('slickbots-chat');
                ch.postMessage({ type: 'clear-messages', clientId: clientIdRef.current });
                ch.close();
              }
            } catch (e) { }
          } else {
            console.log('✅ Session handler: Fresh start, waiting for greeting to arrive');
            // Fresh start with no messages - don't clear, just wait for greeting
            // hasHistoryLoadedRef stays false to allow greeting
          }
        } else {
          // Existing session - mark history as loaded to suppress duplicate greeting
          const stateHasMessages = messages && messages.length > 0;
          const existingMessages = localStorage.getItem('widgetMessages');
          if (stateHasMessages || existingMessages) {
            try {
              if (existingMessages) {
                const parsed = JSON.parse(existingMessages);
                if (Array.isArray(parsed) && parsed.length > 0) {
                  hasHistoryLoadedRef.current = true;
                }
              } else if (stateHasMessages) {
                hasHistoryLoadedRef.current = true;
              }
            } catch (e) {
              // Ignore parse errors
              if (stateHasMessages) {
                hasHistoryLoadedRef.current = true;
              }
            }
          }
        }

        // Broadcast session to all follower tabs so they can use it for ratings
        try {
          if (window.BroadcastChannel) {
            const ch = new window.BroadcastChannel('slickbots-chat');
            ch.postMessage({ type: 'session-sync', sessionId: data.sessionId, clientId: clientIdRef.current });
            ch.close();
          }
        } catch (e) {
          console.error('❌ Error broadcasting session-sync:', e);
        }

        // Start session management (heartbeat, activity tracking)
        updateActivity(); // This will start idle timers if enabled
        startHeartbeat();
        break;

      case 'stream':
        // Use ref for streaming check to avoid async state issues that cause accumulator resets
        if (!isStreamingRef.current) {
          // Reset completion flag for new stream
          isCompletingStreamRef.current = false;
          isStreamingRef.current = true; // Set ref immediately (synchronous)
          setIsStreaming(true);
          pendingQuickRepliesRef.current = null;
          setLoadingWithBroadcast(false); // Hide thinking bubble when streaming starts
          setCurrentBotMessage('');
          currentBotMessageRef.current = ''; // Reset ref
          streamingAccumRef.current = ''; // Reset accumulator for new stream
          streamingBufferRef.current = ''; // Reset buffer for new stream

          // Remove thinking message as soon as first stream chunk arrives
          // IMPORTANT: Only remove thinking messages, preserve ALL other messages including welcome
          setMessages(prev => {
            const prevArr = Array.isArray(prev) ? prev : [];
            const filtered = prevArr.filter(m => !m.isThinking);
            // Return filtered array - preserves all messages except thinking
            return filtered;
          });

          // Broadcast removal of thinking message to all tabs
          try {
            const ch = new window.BroadcastChannel('slickbots-chat');
            ch.postMessage({ type: 'remove-thinking-message', clientId: clientIdRef.current });
            ch.close();
          } catch (e) { }
        }

        // Append incoming content to buffer and accumulator immediately
        const incomingContent = data.content || '';
        streamingBufferRef.current += incomingContent;
        // Ensure we're accumulating correctly - always append to existing accumulator
        // Initialize to empty string if undefined/null to prevent "undefined" or "null" in string
        if (streamingAccumRef.current === undefined || streamingAccumRef.current === null) {
          streamingAccumRef.current = '';
        }
        streamingAccumRef.current = streamingAccumRef.current + incomingContent;

        // Broadcast stream chunk to other tabs so they can mirror streaming output
        try {
          if (window.BroadcastChannel) {
            const ch = new window.BroadcastChannel('slickbots-chat');
            ch.postMessage({ type: 'stream-chunk', content: data.content || '', clientId: clientIdRef.current });
            ch.close();
          }
        } catch (e) { }

        // Ensure periodic flush is running to update UI at a lower frequency
        ensureStreamingFlushRunning(120);

        // Reset timeout for streaming completion detection (fallback if stream-end doesn't arrive)
        if (streamTimeoutRef.current) {
          clearTimeout(streamTimeoutRef.current);
        }

        // Set timeout to detect end of streaming (no new content for 1.5 seconds)
        // This is a fallback - stream-end should normally trigger completion
        // Only set timeout for active tab
        if (isActiveTab.current) {
          streamTimeoutRef.current = setTimeout(() => {
            // Only complete if we're still streaming (stream-end might have already completed)
            if (isStreamingRef.current && !isCompletingStreamRef.current) {
              completeStreaming();
            }
          }, 1500);
        }
        break;

      case 'message':
        // Handle non-streaming bot message (including greeting)
        setLoadingWithBroadcast(false);

        // Stop any pending streaming timers/flushes to prevent duplicate "sub-messages"
        // This handles the case where server sends stream chunks followed by a final message
        if (streamTimeoutRef.current) {
          clearTimeout(streamTimeoutRef.current);
          streamTimeoutRef.current = null;
        }
        stopStreamingFlush();
        isStreamingRef.current = false; // Update ref synchronously
        setIsStreaming(false);
        isCompletingStreamRef.current = false; // Reset completion flag
        streamingBufferRef.current = '';
        streamingAccumRef.current = '';
        setCurrentBotMessage('');

        const botMsg = { role: 'bot', text: formatMessageWithLinks(data.message), timestamp: new Date() };

        // Add the bot message (will remove thinking messages internally)
        addMessageIfNotExists(botMsg);

        // Explicitly remove thinking messages to ensure they're cleared
        setMessages(prev => {
          const prevArr = Array.isArray(prev) ? prev : [];
          const filtered = prevArr.filter(m => !m.isThinking);
          // Preserve all messages including welcome messages
          return filtered;
        });

        // Reset idle timer when bot message is received - this is activity
        updateActivity(true);

        // Broadcast bot message to follower tabs immediately (bypassing React render cycle)
        // This is CRITICAL for mobile where background tabs throttle useEffect/rendering
        try {
          if (window.BroadcastChannel) {
            const ch = new window.BroadcastChannel('slickbots-chat');
            ch.postMessage({
              type: 'bot-message',
              message: botMsg,
              clientId: clientIdRef.current
            });
            ch.close();
          }
        } catch (e) {
          console.error('❌ Error broadcasting bot message:', e);
        }

        // Broadcast removal of thinking message to all tabs
        try {
          const ch = new window.BroadcastChannel('slickbots-chat');
          ch.postMessage({ type: 'remove-thinking-message', clientId: clientIdRef.current });
          ch.close();
        } catch (e) { }

        playSound('receive');
        break;

      case 'form': {
        // Debug logging for requestMessage
        console.log('📋 Form received:', {
          hasRequestMessage: !!data.requestMessage,
          requestMessageType: typeof data.requestMessage,
          requestMessageValue: data.requestMessage,
          requestMessageLength: data.requestMessage ? String(data.requestMessage).length : 0
        });

        setLoadingWithBroadcast(false); // Hide thinking bubble

        // Remove thinking message when bot response arrives
        setMessages(prev => {
          const prevArr = Array.isArray(prev) ? prev : [];
          const lastThinkingIdx = [...prevArr].reverse().findIndex(m => m.isThinking);
          if (lastThinkingIdx !== -1) {
            const idx = prevArr.length - 1 - lastThinkingIdx;
            const newMessages = [...prevArr];
            newMessages.splice(idx, 1);
            return newMessages;
          }
          return prevArr;
        });

        try {
          const ch = new window.BroadcastChannel('slickbots-chat');
          ch.postMessage({ type: 'remove-thinking-message', clientId: clientIdRef.current });
          ch.close();
        } catch (e) { }


        // Parse dynamic fields (support both array and JSON string)
        let fieldsArray = [];
        if (Array.isArray(data.formFields)) {
          fieldsArray = data.formFields;
        } else if (typeof data.formFields === 'string') {
          try {
            fieldsArray = JSON.parse(data.formFields);
          } catch (e) {
            console.error('Failed to parse formFields JSON:', e);
            fieldsArray = [];
          }
        }

        // Prepare dynamic fields for the form
        const dynamicFields = fieldsArray.map(f => ({
          name: f.fieldName,
          label: f.fieldName,
          type: f.fieldType === 'String' ? 'text'
            : f.fieldType === 'Number' ? 'number'
              : f.fieldType === 'Email' ? 'email'
                : f.fieldType === 'Text' ? 'textarea'
                  : 'text',
          required: !!f.requiredStatus,
          multiline: f.fieldType === 'Text',
          rows: f.fieldType === 'Text' ? 3 : undefined
        }));

        // Create the form message object
        const formMessage = {
          id: Date.now(),
          isForm: true,
          formFields: dynamicFields.length > 0 ? dynamicFields : [
            { name: 'name', label: 'Name', type: 'text', required: true },
            { name: 'email', label: 'Email', type: 'email', required: true }
          ],
          role: 'bot',
          timestamp: new Date(),
          // Store successMessage for use after form submission
          successMessage: data.successMessage || ''
        };

        setMessages(prev => {
          // Remove any previous form or waiting/thinking message
          const filtered = prev.filter(msg => !msg.isForm && !msg.isWaiting && !msg.isThinking);
          let newMessages = [...filtered];

          // Add requestMessage if present and not blank/null
          // Check if requestMessage exists, is a string, and is not empty/whitespace
          const requestMsg = data.requestMessage;
          if (requestMsg && typeof requestMsg === 'string' && requestMsg.trim() !== '') {
            console.log('✅ Adding requestMessage:', requestMsg);
            newMessages.push({
              role: 'bot',
              text: formatMessageWithLinks(requestMsg),
              timestamp: new Date()
            });
          } else {
            console.warn('⚠️ Skipping requestMessage - invalid or empty:', {
              requestMsg,
              type: typeof requestMsg,
              isEmpty: requestMsg && typeof requestMsg === 'string' ? requestMsg.trim() === '' : 'N/A'
            });
          }

          // Add the dynamic form message
          newMessages.push(formMessage);
          return newMessages;
        });

        // Broadcast form message to all follower tabs
        try {
          if (window.BroadcastChannel) {
            const ch = new window.BroadcastChannel('slickbots-chat');
            ch.postMessage({
              type: 'form-message',
              formMessage: formMessage,
              requestMessage: data.requestMessage,
              clientId: clientIdRef.current
            });
            ch.close();
          }
        } catch (e) {
          console.error('❌ Error broadcasting form-message:', e);
        }
        break;
      }

      case 'formSubmission':
        // Form submission was successful, no additional action needed
        // The confirmation message is already added in the onSubmit handler
        break;

      case 'heartbeat':
        // Handle backend heartbeat - no action needed, just acknowledge receipt
        break;

      case 'voice-transcript': {
        const voiceRole = data.role === 'user' ? 'user' : 'bot';
        const voiceText = data.text || '';
        if (!voiceText.trim()) {
          break;
        }
        console.log('[Voice] transcript via WebSocket:', voiceRole, voiceText.substring(0, 80));
        setLoadingWithBroadcast(false);
        const voiceExtras = {};
        if (Array.isArray(data.calcomEventTypes) && data.calcomEventTypes.length > 0) {
          voiceExtras.calcomEventTypes = data.calcomEventTypes;
        }
        if (data.calcomSlots && typeof data.calcomSlots === 'object') {
          voiceExtras.calcomSlots = data.calcomSlots;
        }
        if (Array.isArray(data.quickReplies) && data.quickReplies.length > 0) {
          voiceExtras.quickReplies = data.quickReplies;
        }
        handleVoiceTranscript(voiceRole, voiceText, voiceExtras);
        if (voiceRole === 'user') {
          setUserSentMessage(true);
        }
        break;
      }

      case 'calcom_event_types': {
        setLoadingWithBroadcast(false);
        setMessages(prev => {
          const prevArr = Array.isArray(prev) ? prev : [];
          const filtered = prevArr.filter(m => !m.isThinking);
          return [...filtered, {
            role: 'bot',
            text: '<p><strong>Scheduling</strong></p><p>Choose a meeting type, then pick a date range to load available times.</p>',
            calcomEventTypes: Array.isArray(data.items) ? data.items : [],
            timestamp: new Date(),
          }];
        });
        playSound('receive');
        break;
      }

      case 'calcom_slots': {
        setLoadingWithBroadcast(false);
        setMessages(prev => {
          const prevArr = Array.isArray(prev) ? prev : [];
          const filtered = prevArr.filter(m => !m.isThinking);
          const payload = data.payload && typeof data.payload === 'object' ? data.payload : {};
          const slotsMsg = {
            role: 'bot',
            text: '<p><strong>Available times</strong></p><p>Choose a time, then enter your details to book.</p>',
            calcomSlots: payload,
            timestamp: new Date(),
          };

          // Merge into a trailing streamed shell (race: stream-end before side channel)
          const last = filtered[filtered.length - 1];
          if (last && last.role === 'bot' && !last.calcomSlots && !last.calcomEventTypes?.length) {
            const t = (last.text || '').toLowerCase();
            if (t.includes('available times') || t.includes('scheduling')) {
              return [...filtered.slice(0, -1), { ...last, ...slotsMsg }];
            }
          }

          // Collapse the meeting-type row (immediately before this user turn) to static text
          let base = filtered;
          const u = base.length - 1;
          if (u >= 1 && base[u]?.role === 'user') {
            const next = [...base];
            const prev = next[u - 1];
            if (prev?.role === 'bot' && prev.calcomEventTypes?.length && !prev.calcomSlots) {
              next[u - 1] = {
                ...prev,
                calcomEventTypes: undefined,
                text: '<p><strong>Scheduling</strong></p><p>Choose a meeting type, then pick a date range to load available times.</p>',
              };
              base = next;
            }
          }

          return [...base, slotsMsg];
        });
        playSound('receive');
        break;
      }

      case 'quick_replies': {
        setLoadingWithBroadcast(false);
        const qrItems = Array.isArray(data.items) ? data.items : [];
        if (!qrItems.length) {
          break;
        }
        // Side channel often arrives before stream-end; stash until bot text is finalized.
        if (isStreamingRef.current || isCompletingStreamRef.current) {
          pendingQuickRepliesRef.current = qrItems;
          break;
        }
        setMessages(prev => {
          const prevArr = Array.isArray(prev) ? prev : [];
          const filtered = prevArr.filter(m => !m.isThinking);
          const withoutOrphans = filtered.filter(m =>
            !(m.role === 'bot' && !normalizeForKey(m.text) && m.quickReplies?.length)
          );
          const last = withoutOrphans[withoutOrphans.length - 1];
          if (last && last.role === 'bot') {
            return [...withoutOrphans.slice(0, -1), { ...last, quickReplies: qrItems }];
          }
          pendingQuickRepliesRef.current = qrItems;
          return withoutOrphans;
        });
        playSound('receive');
        break;
      }

      case 'session-end-offer':
        const closeText = data.content ||
          "I want to make sure I've helped with everything you needed. Are you ready to finish, or should we continue?";

        setCloseConfirmText(closeText);
        setShowCloseConfirm(true);
        setLoadingWithBroadcast(false); // Hide "thinking" bubble if showing

        // Remove thinking message when showing close confirmation
        setMessages(prev => {
          const prevArr = Array.isArray(prev) ? prev : [];
          const lastThinkingIdx = [...prevArr].reverse().findIndex(m => m.isThinking);
          if (lastThinkingIdx !== -1) {
            const idx = prevArr.length - 1 - lastThinkingIdx;
            const newMessages = [...prevArr];
            newMessages.splice(idx, 1);
            return newMessages;
          }
          return prevArr;
        });

        // Broadcast to all follower tabs so they also show the close confirmation
        try {
          if (window.BroadcastChannel) {
            const ch = new window.BroadcastChannel('slickbots-chat');
            ch.postMessage({
              type: 'session-end-offer',
              content: closeText,
              clientId: clientIdRef.current
            });
            ch.close();
          }
        } catch (e) {
          console.error('❌ Error broadcasting session-end-offer:', e);
        }

        // Broadcast removal of thinking message to all tabs
        try {
          const ch = new window.BroadcastChannel('slickbots-chat');
          ch.postMessage({ type: 'remove-thinking-message', clientId: clientIdRef.current });
          ch.close();
        } catch (e) { }
        break;

      case 'error':
        console.error('❌ WebSocket error from server:', data.message);
        // Ignore "Missing message or session not properly initialized" errors that occur after form messages
        // This happens because the backend tries to process form messages as regular chat messages
        if (data.message && data.message.includes('Missing message or session not properly initialized')) {
          return; // Don't set error state for this specific error
        }
        setError(data.message);
        setLoadingWithBroadcast(false);
        isStreamingRef.current = false; // Update ref synchronously
        setIsStreaming(false);
        break;

      case 'stream-end':
        // Cancel the timeout since stream-end explicitly signals completion
        if (streamTimeoutRef.current) {
          clearTimeout(streamTimeoutRef.current);
          streamTimeoutRef.current = null;
        }

        // Broadcast stream-end so other tabs can finalize streaming
        try {
          if (window.BroadcastChannel) {
            const ch = new window.BroadcastChannel('slickbots-chat');
            ch.postMessage({ type: 'stream-end', clientId: clientIdRef.current });
            ch.close();
          }
        } catch (e) { }

        // Complete streaming - this will use the full accumulator
        completeStreaming();
        break;

      case 'handoffStart':
        setLoadingWithBroadcast(false);

        const handoffMsg = {
          role: 'bot',
          text: data.content || 'Transferring to live agent...',
          timestamp: new Date(),
          isHandoff: true
        };

        // Remove thinking message and add handoff message in a single state update
        setMessages(prev => {
          const prevArr = Array.isArray(prev) ? prev : [];
          // Remove thinking message if it exists
          const filtered = prevArr.filter(m => !m.isThinking);
          // Add handoff message
          const next = [...filtered, handoffMsg];
          persistAndBroadcast(next);
          return next;
        });

        // Broadcast handoff message to follower tabs
        try {
          if (window.BroadcastChannel) {
            const ch = new window.BroadcastChannel('slickbots-chat');
            ch.postMessage({
              type: 'handoff-message',
              message: handoffMsg,
              clientId: clientIdRef.current
            });
            ch.close();
          }
        } catch (e) {
          console.error('❌ Error broadcasting handoff-message:', e);
        }

        // Broadcast removal of thinking message to all tabs
        try {
          const ch = new window.BroadcastChannel('slickbots-chat');
          ch.postMessage({ type: 'remove-thinking-message', clientId: clientIdRef.current });
          ch.close();
        } catch (e) { }

        playSound('receive');
        break;

      case 'agentConnected':
        setLoadingWithBroadcast(false);

        // Store the live agent name
        if (data.liveAgentName) {
          setLiveAgentName(data.liveAgentName);
          localStorage.setItem('widgetLiveAgentName', data.liveAgentName);
        }

        const agentConnectedMsg = {
          role: 'bot',
          text: data.content || 'A live agent has joined the conversation.',
          timestamp: new Date(),
          isHandoff: true  // Use same styling as handoff
        };

        // Remove thinking message and add agent connected message in a single state update
        setMessages(prev => {
          const prevArr = Array.isArray(prev) ? prev : [];
          const filtered = prevArr.filter(m => !m.isThinking);
          const next = [...filtered, agentConnectedMsg];
          // persistAndBroadcast is handled by useEffect on messages change
          // persistAndBroadcast(next);
          return next;
        });

        // Broadcast agent connected message to follower tabs
        try {
          if (window.BroadcastChannel) {
            const ch = new window.BroadcastChannel('slickbots-chat');
            ch.postMessage({
              type: 'handoff-message',
              message: agentConnectedMsg,
              liveAgentName: data.liveAgentName,
              clientId: clientIdRef.current
            });
            ch.close();
          }
        } catch (e) {
          console.error('❌ Error broadcasting agent-connected-message:', e);
        }

        // Broadcast removal of thinking message to all tabs
        try {
          const ch = new window.BroadcastChannel('slickbots-chat');
          ch.postMessage({ type: 'remove-thinking-message', clientId: clientIdRef.current });
          ch.close();
        } catch (e) { }

        playSound('receive');
        break;

      case 'agentDisconnected':
        setLoadingWithBroadcast(false);

        // Clear the live agent name and switch back to bot
        setLiveAgentName(null);
        localStorage.removeItem('widgetLiveAgentName');

        const agentDisconnectedMsg = {
          role: 'bot',
          text: data.content || 'The live agent has left the conversation.',
          timestamp: new Date(),
          isHandoff: true  // Use same styling as handoff
        };

        // Remove thinking message and add agent disconnected message in a single state update
        setMessages(prev => {
          const prevArr = Array.isArray(prev) ? prev : [];
          const filtered = prevArr.filter(m => !m.isThinking);
          const next = [...filtered, agentDisconnectedMsg];
          // persistAndBroadcast is handled by useEffect on messages change
          // persistAndBroadcast(next);
          return next;
        });

        // Broadcast agent disconnected message to follower tabs
        try {
          if (window.BroadcastChannel) {
            const ch = new window.BroadcastChannel('slickbots-chat');
            ch.postMessage({
              type: 'handoff-message',
              message: agentDisconnectedMsg,
              liveAgentName: null,
              clientId: clientIdRef.current
            });
            ch.close();
          }
        } catch (e) {
          console.error('❌ Error broadcasting agent-disconnected-message:', e);
        }

        // Broadcast removal of thinking message to all tabs
        try {
          const ch = new window.BroadcastChannel('slickbots-chat');
          ch.postMessage({ type: 'remove-thinking-message', clientId: clientIdRef.current });
          ch.close();
        } catch (e) { }

        playSound('receive');
        break;

      case 'leave':
        // Agent has disconnected - show popup message then end chat and show rating
        console.log('🚪 Agent left the chat');

        // Capture sessionId for rating
        const leaveSessionId = sessionId || localStorage.getItem('widgetSessionId');
        const leaveMessage = data.content || 'The agent has left the chat.';

        // Set rating sessionId so rating dialog can submit properly
        setRatingSessionId(leaveSessionId);

        // Show the leave message popup
        setAgentLeaveMessage(leaveMessage);
        setShowAgentLeavePopup(true);

        // Broadcast to all tabs so they also show popup and prepare for rating
        try {
          if (window.BroadcastChannel) {
            const ch = new window.BroadcastChannel('slickbots-chat');
            ch.postMessage({
              type: 'agent-left',
              message: leaveMessage,
              capturedSessionId: leaveSessionId,
              clientId: clientIdRef.current
            });
            ch.close();
          }
        } catch (e) {
          console.error('❌ Error broadcasting agent-left:', e);
        }

        // After 3 seconds, close popup, end session, and show rating
        setTimeout(() => {
          setShowAgentLeavePopup(false);
          setAgentLeaveMessage('');

          // End the session
          endSession('agent_left', leaveSessionId);

          // Show rating dialog
          if (showFormSuccessModal) {
            setShowFormSuccessModal(false);
            setTimeout(() => setShowRatingDialog(true), 0);
          } else {
            setShowRatingDialog(true);
          }
        }, 3000);

        break;

      default:
        console.log('📨 Unknown WebSocket message type:', data.type);
    }
  };

  const completeStreaming = () => {
    // Prevent multiple calls to completeStreaming for the same stream
    if (isCompletingStreamRef.current) {
      console.log('⚠️ Already completing stream, skipping duplicate call');
      return;
    }

    // Mark that we're completing the stream
    isCompletingStreamRef.current = true;
    console.log('🏁 Starting stream completion');

    // Clear the timeout since we're completing now
    if (streamTimeoutRef.current) {
      clearTimeout(streamTimeoutRef.current);
      streamTimeoutRef.current = null;
    }

    // Clear any pending scroll throttle
    if (scrollThrottleRef.current) {
      clearTimeout(scrollThrottleRef.current);
      scrollThrottleRef.current = null;
    }

    // Stop periodic flush to prevent further updates
    stopStreamingFlush();

    // Flush any remaining buffered content to currentBotMessage state ONLY
    // NOTE: Do NOT add to accumulator - it already has all content because each chunk
    // was added immediately when it arrived. The buffer is just for batching UI updates.
    if (streamingBufferRef.current) {
      const remainingBuffer = streamingBufferRef.current;
      // Flush to currentBotMessage state to ensure UI is up to date
      setCurrentBotMessage(prev => {
        const updated = (prev || '') + remainingBuffer;
        currentBotMessageRef.current = updated; // Update ref synchronously
        return updated;
      });
      streamingBufferRef.current = '';
    }

    // Use accumulator as the single source of truth - it has ALL chunks accumulated
    // The accumulator is updated immediately when each chunk arrives, so it already has everything
    // Ensure we read the accumulator value directly - it should contain all accumulated chunks
    const finalMessage = streamingAccumRef.current || '';

    // Safety check: If accumulator seems incomplete (very short), log a warning
    // This helps diagnose if accumulator is being reset incorrectly
    if (finalMessage && finalMessage.trim().length > 0 && finalMessage.trim().length < 20) {
      console.warn('⚠️ Warning: Accumulator content seems incomplete. Length:', finalMessage.length);
    }

    if (finalMessage && finalMessage.trim()) {
      const finalText = formatMessageWithLinks(finalMessage.trim());
      const qrItems = pendingQuickRepliesRef.current;
      pendingQuickRepliesRef.current = null;
      const finalMsg = {
        role: 'bot',
        text: finalText,
        timestamp: new Date(),
        ...(Array.isArray(qrItems) && qrItems.length > 0 ? { quickReplies: qrItems } : {}),
      };

      // Normalize the raw message for duplicate checking (before HTML formatting)
      const normalizedFinalMessage = normalizeForKey(finalMessage.trim());

      setMessages(prev => {
        const prevArr = Array.isArray(prev) ? prev : [];
        console.log('📨 Completing stream, current messages count:', prevArr.length);

        // Remove ALL thinking messages first - preserve everything else INCLUDING welcome messages
        let filtered = prevArr.filter(m => !m.isThinking);
        // Drop chip-only orphan rows from quick_replies arriving before stream-end
        filtered = filtered.filter(m =>
          !(m.role === 'bot' && !normalizeForKey(m.text) && m.quickReplies?.length)
        );

        // Check if this exact message already exists to prevent duplicates
        // Only check the LAST message to avoid removing legitimate repeated questions
        // This ensures welcome messages are never removed accidentally
        if (filtered.length > 0) {
          const lastMsg = filtered[filtered.length - 1];
          const lastMsgNormalized = normalizeForKey(lastMsg.text || '');
          if (lastMsg.role === 'bot' && lastMsgNormalized === normalizedFinalMessage) {
            // Exact duplicate of last message, don't add again — but merge chips if needed
            console.log('⚠️ Duplicate streamed message detected, skipping');
            if (finalMsg.quickReplies?.length && !lastMsg.quickReplies?.length) {
              return [...filtered.slice(0, -1), { ...lastMsg, quickReplies: finalMsg.quickReplies }];
            }
            return filtered;
          }
        }

        // No duplicate found, add the final message
        // Preserve ALL existing messages (including welcome messages at the top)
        const next = [...filtered, finalMsg];
        console.log('✅ Streamed message added, new count:', next.length);
        return next;
      });

      playSound('receive');

      // Reset idle timer when bot message is received - this is activity
      updateActivity(true);

      // Final scroll to bottom and focus input
      setTimeout(() => scrollToBottom(), 100);
      setTimeout(() => { inputRef.current && inputRef.current.focus(); }, 100);
    }

    // Clear state
    setCurrentBotMessage('');
    streamingAccumRef.current = '';
    isStreamingRef.current = false; // Update ref synchronously
    setIsStreaming(false);

    // Reset the completion flag after a short delay to allow next stream
    setTimeout(() => {
      isCompletingStreamRef.current = false;
    }, 100);
  };

  const sendWebSocketMessage = (message, opts = {}) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('❌ WebSocket not connected');
      return false;
    }

    try {
      // CRITICAL: If there's a stream in progress, complete it first before sending new message
      // This ensures welcome message (or any previous response) is fully added before user message
      if (isStreamingRef.current) {
        const pendingContent = streamingAccumRef.current || currentBotMessageRef.current || '';
        if (pendingContent && pendingContent.trim()) {
          console.log('⚠️ Stream in progress, completing before sending user message');
          console.log('   Pending content length:', pendingContent.length);
          completeStreaming();
          // Give React a moment to process the state update
          // This is synchronous enough that the welcome message will be added before user message
        } else {
          console.log('⚠️ Stream flag set but no content, resetting stream state');
          isStreamingRef.current = false;
          setIsStreaming(false);
        }
      }

      // Send message through WebSocket with required parameters
      // Use stored agentId/tenantId from state (set when config was loaded)
      pendingQuickRepliesRef.current = null;
      wsRef.current.send(JSON.stringify({
        message: message,
        ...(opts.displayMessage ? { displayMessage: opts.displayMessage } : {}),
        agentId: agentId,
        tenantId: tenantId,
        ...opts // allow triggerForm or other flags
      }));

      // Add user message to UI after successful send
      // IMPORTANT: Use functional update to preserve existing messages (including welcome message)
      if (!opts.skipOptimisticAdd) {
        const userMsg = {
          role: 'user',
          text: message,
          ...(opts.displayMessage ? { displayText: opts.displayMessage } : {}),
          timestamp: new Date()
        };

        // Add user message while preserving ALL existing messages
        setMessages(prev => {
          const prevArr = Array.isArray(prev) ? prev : [];
          console.log('📤 Adding user message, current messages count:', prevArr.length);

          // If count is still 0, it means welcome message stream didn't complete yet
          // This is a timing issue - wait a moment for it to complete
          if (prevArr.length === 0 && isStreamingRef.current) {
            console.warn('⚠️ Welcome message may still be streaming, should have been completed above');
          }

          // Check if this exact user message already exists (avoid duplicates)
          const isDuplicate = prevArr.some(m =>
            m.role === 'user' &&
            m.text === message &&
            Math.abs(normalizeTs(m.timestamp) - normalizeTs(userMsg.timestamp)) < 5000
          );

          if (isDuplicate) {
            console.log('⚠️ Duplicate user message detected, skipping');
            return prevArr; // Skip duplicate
          }

          const withoutStaleQuickReplies = prevArr.map(m =>
            (m.quickReplies ? { ...m, quickReplies: undefined } : m)
          );

          // Add user message to array, preserving ALL existing messages (including welcome)
          const next = [...withoutStaleQuickReplies, userMsg];
          console.log('✅ User message added, new count:', next.length);
          return next;
        });

        setUserSentMessage(true); // Mark that user sent a message
      }

      playSound('sent');

      // Initialize streaming state - clear previous message and set up for new streaming
      setCurrentBotMessage('');
      isStreamingRef.current = false; // Update ref synchronously - will be set to true when first stream message arrives
      setIsStreaming(false); // Will be set to true when first stream message arrives
      // Set loading=true to show thinking bubble and disable input (broadcasts to followers)
      setLoadingWithBroadcast(true);

      // Update activity - force reset idle timer as this is real user activity
      updateActivity(true);

      return true;
    } catch (error) {
      console.error('❌ Error sending WebSocket message:', error);
      return false;
    }
  };

  const handleQuickReplyPick = (item) => {
    if (!item) return;
    const value = (item.value || item.label || '').trim();
    const label = (item.label || value).trim();
    if (!value) return;
    sendWebSocketMessage(value, quickReplySendOptions(item, value, label));
  };

  // Handle WebSocket connection lifecycle
  useEffect(() => {
    // Cleanup WebSocket on unmount
    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting'); // Normal closure
        wsRef.current = null;
      }
      if (streamTimeoutRef.current) {
        clearTimeout(streamTimeoutRef.current);
      }
      if (scrollThrottleRef.current) {
        clearTimeout(scrollThrottleRef.current);
      }
    };
  }, []);

  // WebSocket cleanup is handled in the main beforeunload handler above

  // Handler for closing the form success modal
  const handleContinueChatAfterForm = () => {
    setShowFormSuccessModal(false);
    setFormSuccessMessage('');
  };


  // WebSocket-based form submission handler
  const handleFormSubmit = async (values, msg) => {
    // Remove the form from messages immediately for UX
    setMessages(prev => prev.filter(m => !m.isForm));
    setLoading(true);

    // Broadcast form removal and success message to all tabs
    try {
      if (window.BroadcastChannel) {
        const ch = new window.BroadcastChannel('slickbots-chat');
        ch.postMessage({
          type: 'form-submitted',
          successMessage: msg.successMessage || 'Thank you for submitting your information!',
          clientId: clientIdRef.current
        });
        ch.close();
      }
    } catch (e) {
      console.error('❌ Error broadcasting form-submitted:', e);
    }

    if (isActiveTab && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'formSubmission',
        sessionId,
        data: values
      }));
    } else {
      try {
        const ch = new window.BroadcastChannel('slickbots-chat');
        ch.postMessage({ type: 'forward-form-submission', sessionId, data: values, clientId: clientIdRef.current });
        ch.close();
      } catch (e) { }
    }
    setFormSuccessMessage(msg.successMessage || 'Thank you for submitting your information!');
    setShowFormSuccessModal(true);
    setLoadingWithBroadcast(false);
  };

  // Extract customization and create memoized streaming message display - moved before conditional returns
  // const customization = config && config.customization ? config.customization : {};
  // Use live agent name if connected, otherwise use bot agent name
  const displayAgentName = liveAgentName || customization.agentName;
  const botTitle = customization.botTitle || (config && config.botName) || 'Chatbot';
  const isTransparentEmbed = isEmbedFullLayout && getTransparentFromHash();
  const chatBg = isTransparentEmbed ? 'transparent' : (customization.chatBackgroundColor || '#F8F9FA');
  const headerBg = customization.headerBackgroundColor || '#FFFFFF';
  const footerBg = customization.footerBackgroundColor || '#FFFFFF';
  const userMsgColor = customization.userMessageColor || '#e3f2fd';
  const botMsgColor = customization.botMessageColor || '#f5f5f5';
  const userTextColor = customization.userTextColor || '#000000';
  const botTextColor = customization.botTextColor || '#000000';
  const quickReplyTextColor = customization.quickReplyTextColor || '#1976d2';
  const quickReplyBorderColor = customization.quickReplyBorderColor || '#1976d2';
  const quickReplyBackgroundColor = customization.quickReplyBackgroundColor || '#FFFFFF';
  const inputPlaceholder = customization.inputPlaceholder || 'Type your message...';
  const inputBoxColor = customization.inputBoxColor || '#FFFFFF';
  const chatHeight = customization.chatHeight || 400;
  const chatIconShape = customization.chatIconShape || 'round';
  const chatIconSurface = customization.chatIconSurface || 'solid';
  const showChatIconShadow = customization.chatIconShadow !== false;
  const chatIconBorderRadius = chatIconShape === 'square' ? '0' : '50%';
  const hasCustomLauncherIcon = Boolean(customization.chatIcon || customization.logo);

  // Memoized streaming message display - moved before any conditional returns
  const streamingMessageDisplay = useMemo(() => {
    if (!isStreaming || !currentBotMessage) return null;
    if (isEmbedFullLayout) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', margin: '14px 0', maxWidth: '100%' }}>
          <div style={{
            maxWidth: '100%',
            wordBreak: 'break-word',
            fontFamily: customization.fontFamily || 'system-ui',
            fontSize: fontSizes[fontSizeIdx],
            lineHeight: customization.lineHeight || 1.6,
            color: botTextColor,
            textAlign: 'left',
          }}>
            {currentBotMessage}
            <span style={{
              display: 'inline-block',
              width: 2,
              height: fontSizes[fontSizeIdx],
              background: '#222',
              marginLeft: 2,
              animation: 'blink 1s infinite',
            }}>|</span>
          </div>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', margin: '14px 0' }}>
        {/* Agent Name for streaming message */}
        {displayAgentName && (
          <span style={{
            color: customization.agentNameColor || '#333',
            fontSize: '14px',
            fontFamily: customization.fontFamily || 'system-ui',
            fontWeight: 'bold',
            marginBottom: 4,
            alignSelf: 'flex-start'
          }}>
            {displayAgentName}
          </span>
        )}
        <span style={{
          display: 'inline-block',
          background: botMsgColor,
          color: '#222',
          padding: customization.bubblePadding || '14px 20px',
          borderRadius: 16,
          maxWidth: '80%',
          wordBreak: 'break-word',
          fontFamily: customization.fontFamily || 'system-ui, Inter, Arial, sans-serif',
          fontSize: fontSizes[fontSizeIdx],
          lineHeight: customization.lineHeight || 1.6,
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          textAlign: 'left',
          position: 'relative',
        }}>
          {currentBotMessage}
          <span style={{
            display: 'inline-block',
            width: 2,
            height: fontSizes[fontSizeIdx],
            background: '#222',
            marginLeft: 2,
            animation: 'blink 1s infinite'
          }}>|</span>
        </span>
      </div>
    );
  }, [isStreaming, currentBotMessage, displayAgentName, isEmbedFullLayout, customization.fontFamily, customization.bubblePadding, customization.lineHeight, customization.timestampColor, customization.timestampFontSize, customization.timestampFontFamily, botMsgColor, botTextColor, quickReplyTextColor, quickReplyBorderColor, quickReplyBackgroundColor, fontSizes, fontSizeIdx]);

  if (minimized) {
    const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    // Check if closed agent text should be shown
    const showClosedText = customization && customization.showBubble === true && (isMobile ? (customization.showBubbleInMobile === true) : true);

    // Compute bubble text once for both visibility check and rendering
    const computeBubbleText = () => {
      const bubbleType = customization.bubbleType || 'none';

      // Automated (Predefined Phrases) -> use the picked phrase (persisted per session) or fall back to a random pick
      if (bubbleType === 'automated') {
        let picked = automatedPickedPhrase;
        if (!picked) {
          // fallback random pick if for some reason persisted phrase is missing
          let phrases = customization.automatedClosedAgentPhrases;
          if (typeof phrases === 'string') phrases = phrases.split(/\r?\n/);
          phrases = Array.isArray(phrases) ? phrases.map(p => String(p || '').trim()).filter(Boolean) : [];
          picked = phrases[Math.floor(Math.random() * phrases.length)];
        }
        return String(picked || '');
      }

      // AI-based bubble -> match current URL with bubbleMatchedPages
      if (bubbleType === 'aiBased') {
        const currentUrl = getPageUrlFromHash();
        const matchedPages = customization.bubbleMatchedPages || [];

        // Normalize URL for comparison (remove trailing slash, convert to lowercase)
        const normalizeUrl = (url) => {
          if (!url) return '';
          try {
            const urlObj = new URL(url);
            // Remove trailing slash and convert to lowercase
            let normalized = urlObj.origin + urlObj.pathname;
            normalized = normalized.replace(/\/$/, '');
            return normalized.toLowerCase();
          } catch (e) {
            // If URL parsing fails, just normalize the string
            return String(url).replace(/\/$/, '').toLowerCase();
          }
        };

        const normalizedCurrentUrl = normalizeUrl(currentUrl);

        // Find matching page by comparing normalized URLs
        const matchedPage = matchedPages.find(page => {
          if (!page || !page.url) return false;
          const normalizedPageUrl = normalizeUrl(page.url);
          return normalizedPageUrl === normalizedCurrentUrl;
        });

        // If match found, return the nudge for that page
        if (matchedPage && matchedPage.nudge && matchedPage.nudge.trim()) {
          return matchedPage.nudge.trim();
        }

        // No match found - check unmatchedUrlOption
        const unmatchedOption = customization.unmatchedUrlOption || 'hide';
        if (unmatchedOption === 'fallback' && customization.fallbackNudge) {
          return customization.fallbackNudge;
        }

        // If option is 'hide' or no fallback configured, return empty to hide bubble
        return '';
      }

      return '';
    };

    const bubbleText = computeBubbleText();
    const bubbleType = customization.bubbleType || 'none';
    // For AI-based bubbles, if text is empty, it means no match and should be hidden
    // Don't set default text for AI-based bubbles when they should be hidden
    const finalBubbleText = (!bubbleText || !bubbleText.trim() || !bubbleText.length) && bubbleType !== 'aiBased'
      ? 'Open me up to chat!'
      : (bubbleText || '');

    const hasBubbleText = finalBubbleText && finalBubbleText.trim();

    // Only render the chat icon, not the frame, when minimized
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          alignItems: 'flex-end', // changed from 'center'
          paddingTop: (showClosedText && showBubble && hasBubbleText) ? '10px' : '20px',
          paddingRight: '20px',
          paddingBottom: '20px', // Always keep bottom padding consistent
          paddingLeft: '20px',
          boxSizing: 'border-box'
        }}
      >
        {/* Chat bubble above minimized icon */}
        {showClosedText && showBubble && hasBubbleText && (
          <div
            onClick={handleChatIconClick}
            style={{
              maxWidth: 260, // removed width: '100%'
              marginBottom: '10px',
              background: 'white',
              color: '#333',
              borderRadius: '16px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              padding: '14px 20px', // match widget
              fontSize: '16px',
              lineHeight: 1.6, // line spacing to match widget
              textAlign: 'left',
              border: '1px solid #eee',
              position: 'relative', // Enable absolute positioning for close button
              cursor: 'pointer'
            }}
          >
            {/* Close icon button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowBubble(false);
              }}
              style={{
                position: 'absolute',
                top: '-10px',
                right: '-10px',
                background: '#fff',
                border: '1px solid #ddd',
                cursor: 'pointer',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                width: '28px',
                height: '28px',
                transition: 'all 0.2s',
                zIndex: 10,
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f5f5f5';
                e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.2)';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#fff';
                e.target.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.15)';
                e.target.style.transform = 'scale(1)';
              }}
              aria-label="Close chat bubble"
            >
              <span style={{
                fontSize: '20px',
                color: '#333',
                lineHeight: 1,
                fontWeight: 'bold'
              }}>×</span>
            </button>
            {finalBubbleText}
          </div>
        )}
        {/* Minimized chat icon: use chatIcon (bubble when closed); logo is for left side of open chat window only */}
        <div
          className="chat-icon"
          style={{
            width: 60,
            height: 60,
            borderRadius: chatIconBorderRadius,
            // Preserve transparency for uploaded icons regardless of surface mode.
            background: hasCustomLauncherIcon
              ? 'transparent'
              : (chatIconSurface === 'floating' ? 'transparent' : headerBg),
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            boxShadow: showChatIconShadow
              ? (chatIconSurface === 'floating'
                ? '0 6px 16px rgba(0,0,0,0.22)'
                : '0 2px 10px rgba(0, 0, 0, 0.2)')
              : 'none',
            flexShrink: 0,
            overflow: 'hidden'
          }}
          onClick={handleChatIconClick}
          title="Open Chat"
        >
          {(customization.chatIcon || customization.logo) ? (
            <img
              src={customization.chatIcon || customization.logo}
              alt="Chatbot"
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: chatIconBorderRadius, display: 'block' }}
            />
          ) : (
            <span style={{ fontSize: 32, color: 'var(--primary-color, #1976d2)', fontWeight: 'bold' }}>💬</span>
          )}
        </div>
      </div>
    );
  }

  if (welcomeVisible && !isEmbedFullLayout && !isMobileDevice() && isWelcomeStepActive(customization)) {
    const intermediate = getIntermediateConfig(customization);
    const welcomeIsMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    return (
      <div
        className="slickbots-frame-root"
        style={{
          width: welcomeIsMobile ? '100%' : intermediate.width,
          maxWidth: '100%',
          height: welcomeIsMobile ? '100%' : intermediate.height,
          minHeight: welcomeIsMobile ? '100%' : intermediate.height,
          borderRadius: welcomeIsMobile ? 0 : 16,
          boxShadow: welcomeIsMobile ? 'none' : '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          boxSizing: 'border-box',
          background: '#fff',
        }}
      >
        <IntermediateWelcomePanel
          customization={customization}
          onMinimize={handleMinimize}
          onStart={handleStartConversation}
        />
      </div>
    );
  }

  if (error) {
    return <div className="slickbots-frame-error">{error}</div>;
  }
  // Always render the widget, even if config is not loaded yet
  // This ensures the chat icon shows on all tabs
  if (!config) {
    // Return minimized widget while config loads
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          alignItems: 'flex-end',
          padding: '10px',
          boxSizing: 'border-box'
        }}
      >
        <div
          className="chat-icon"
          style={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: '#FFFFFF',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
            flexShrink: 0
          }}
          title="Open Chat"
        >
          <span style={{ fontSize: 32, color: 'var(--primary-color, #1976d2)', fontWeight: 'bold' }}>💬</span>
        </div>
      </div>
    );
  }

  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const embedHasUserMessage = messages.some(m => m.role === 'user');
  const embedShowLanding = isEmbedFullLayout && !isPaused && !embedHasUserMessage;
  const showBottomInput = !isPaused && (!isEmbedFullLayout || embedHasUserMessage);
  const embedAgentLabel = displayAgentName || botTitle;

  return (
    <div
      className="slickbots-frame-root"
      style={{
        background: chatBg,
        fontSize: fontSizes[fontSizeIdx],
        position: 'relative',
        ...(isEmbedFullLayout
          ? {
            width: '100%',
            maxWidth: '100%',
            height: '100%',
            maxHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            borderRadius: 0,
            boxShadow: 'none',
            overflow: 'hidden',
            boxSizing: 'border-box',
          }
          : {
            width: isMobile ? '100%' : (customization.chatWidth || 350),
            maxWidth: isMobile ? '100%' : (customization.chatWidth || 350),
            height: isMobile ? '100vh' : chatHeight,
            maxHeight: isMobile ? '100vh' : chatHeight,
            borderRadius: isMobile ? 0 : 16,
            boxShadow: isMobile ? 'none' : '0 2px 8px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            boxSizing: 'border-box',
          }),
      }}
    >
      {!isEmbedFullLayout && (
        <div style={{ flexShrink: 0 }}>
          <PublicChatHeader
            customization={customization}
            onClose={handleClose}
            onMinimize={handleMinimize}
            isMuted={isMuted}
            setIsMuted={setIsMuted}
            onFontSizeChange={handleFontSize}
          />
        </div>
      )}

      <div
        className="slickbots-frame-body"
        style={{
          display: 'flex',
          flexDirection: 'column',
          ...(isEmbedFullLayout
            ? {
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
            }
            : {
              height: `calc(${chatHeight - 48 - 90}px)`,
              overflowY: 'auto',
              overflowX: 'hidden',
            }),
          background: chatBg,
          padding: '0 24px',
          boxSizing: 'border-box',
        }}
      >
        {/* Paused state UI - show when chat is active elsewhere - ONLY show this, hide everything else */}
        {isPaused ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            textAlign: 'center',
            padding: '40px 20px'
          }}>
            {/* GIF Image */}
            <img
              src="/images/chat_continue.gif"
              alt="Continue chat"
              style={{
                width: '240px',
                height: '240px',
                marginBottom: '24px',
                objectFit: 'contain'
              }}
            />
            {/* Updated message text */}
            <div style={{
              fontSize: '16px',
              fontWeight: 400,
              color: '#555',
              marginBottom: '24px',
              lineHeight: '1.5',
              maxWidth: '300px',
              fontFamily: customization.fontFamily || 'system-ui'
            }}>
              It looks like a chat is open in another tab. Continue your chat here.
            </div>
            <Button
              variant="contained"
              color="primary"
              onClick={handleContinueClick}
              disabled={isActivating}
              style={{
                padding: '12px 32px',
                fontSize: '16px',
                fontWeight: 600,
                borderRadius: '8px',
                textTransform: 'none',
                minWidth: '160px'
              }}
            >
              {isActivating ? 'Connecting...' : 'Continue'}
            </Button>
          </div>
        ) : embedShowLanding ? (
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '32px 24px 24px',
              minHeight: 0,
              overflowY: 'auto',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            {/* <h1
              style={{
                textAlign: 'center',
                fontSize: 22,
                fontWeight: 600,
                margin: '0 0 28px',
                color: '#111',
                fontFamily: customization.fontFamily || 'system-ui, Inter, sans-serif',
                lineHeight: 1.3,
                maxWidth: 560,
              }}
            >
              Ask me how {embedAgentLabel}
            </h1> */}
            <div style={{ width: '100%', maxWidth: 560, marginBottom: 28 }}>
              <div style={{ position: 'relative', width: '100%' }}>
                <VoiceSpeakingIndicator inCall={enableVoice && isInVoiceCall} speaking={isBotSpeaking} />
                <div
                  style={{
                    width: '100%',
                    background: inputBoxColor,
                    borderRadius: 16,
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    border: isInputFocused ? '1px solid var(--primary-color, #1976d2)' : '1px solid #cfcfcf',
                    boxShadow: isInputFocused ? '0 0 0 2px color-mix(in srgb, var(--primary-color, #1976d2) 20%, transparent)' : 'none',
                    minHeight: 56,
                    padding: '8px 12px',
                  }}
                >
                  <InputBase
                    inputRef={inputRef}
                    multiline
                    minRows={3}
                    maxRows={6}
                    value={input}
                    onChange={e => {
                      setInput(e.target.value);
                      updateActivity(true);
                    }}
                    onKeyDown={handleInputKeyDown}
                    onFocus={() => setIsInputFocused(true)}
                    onBlur={() => setIsInputFocused(false)}
                    placeholder={inputPlaceholder || 'Ask anything'}
                    sx={{
                      flex: 1,
                      border: 'none',
                      outline: 'none',
                      fontSize: fontSizes[fontSizeIdx],
                      fontFamily: customization.fontFamily || 'system-ui',
                      padding: '8px 12px',
                      background: 'transparent',
                      color: '#222',
                      borderRadius: 0,
                      boxShadow: 'none',
                      margin: 0,
                      '& textarea': { padding: 0, margin: 0, boxSizing: 'border-box' },
                    }}
                  />
                  {enableVoice && (
                    <IconButton
                      type="button"
                      title={isInVoiceCall ? 'End voice call' : 'Call AI (voice)'}
                      onClick={isInVoiceCall ? endVoiceCall : startVoiceCall}
                      disabled={loading || isVoiceConnecting || !sessionId}
                      color="primary"
                      size="medium"
                      sx={{
                        background: 'transparent',
                        color: isInVoiceCall ? '#d32f2f' : (customization.sendIconColor || customization.headerBackgroundColor || 'var(--primary-color, #1976d2)'),
                        borderRadius: 0,
                        width: 44,
                        height: 44,
                      }}
                    >
                      {isInVoiceCall ? <PhoneDisabledIcon /> : <PhoneIcon />}
                    </IconButton>
                  )}
                  {enableVoice && isInVoiceCall && (
                    <IconButton
                      type="button"
                      title={isVoiceMuted ? 'Unmute microphone' : 'Mute microphone'}
                      onClick={toggleVoiceMute}
                      color="primary"
                      size="medium"
                      sx={{ width: 44, height: 44 }}
                    >
                      {isVoiceMuted ? <MicOffIcon /> : <MicIcon />}
                    </IconButton>
                  )}
                  <IconButton
                    type="button"
                    onClick={sendMessage}
                    disabled={loading || !input.trim()}
                    color="primary"
                    size="medium"
                    sx={{
                      background: 'transparent',
                      color: customization.sendIconColor || customization.headerBackgroundColor || 'var(--primary-color, #1976d2)',
                      borderRadius: 0,
                      width: 44,
                      height: 44,
                    }}
                  >
                    <SendIcon />
                  </IconButton>
                </div>
              </div>
              {enableVoice && voiceError && (
                <div style={{ fontSize: 12, color: '#d32f2f', marginTop: 6, textAlign: 'center' }}>
                  {voiceError}
                </div>
              )}
            </div>
            {showPresetResponses && presetResponses.length > 0 && (
              <div
                style={{
                  width: '100%',
                  maxWidth: 560,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: 0,
                }}
              >
                {presetResponses.map((preset, index) => (
                  <button
                    key={preset.id || index}
                    type="button"
                    onClick={() => handlePresetClick(preset.text)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '14px 4px',
                      margin: 0,
                      border: 'none',
                      borderBottom: '1px solid rgba(0,0,0,0.08)',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: 15,
                      color: '#222',
                      fontFamily: customization.fontFamily || 'system-ui',
                      lineHeight: 1.45,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                    }}
                  >
                    <AutoAwesomeIcon
                      sx={{
                        fontSize: 12,
                        color: customization.starterPromptTextColor || 'var(--primary-color, #1976d2)',
                        flexShrink: 0,
                        mt: '2px',
                      }}
                    />
                    <span style={{ flex: 1, textAlign: 'left' }}>{preset.text}</span>
                  </button>
                ))}
              </div>
            )}
            {(isEmbedFullLayout && tenantId != null) && (
              <div
                style={{
                  width: '100%',
                  maxWidth: 560,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '20px 16px 8px',
                  boxSizing: 'border-box',
                  marginTop: 8,
                }}
              >
                <PoweredByChattitude
                  tenantId={tenantId}
                  poweredByLogoUrl={config?.customization?.poweredByLogoUrl}
                  poweredByLogoHref={config?.customization?.poweredByLogoHref}
                />
              </div>
            )}
          </div>
        ) : (
          /* Active chat UI - only show when not paused */
          <>
            {messages.map((msg, idx) => {
              if (msg.isForm) {
                return (
                  <ChatFormMessage
                    key={msg.id || idx}
                    fields={msg.formFields}
                    onSubmit={(values) => handleFormSubmit(values, msg)}
                    loading={loading}
                    embedPlain={isEmbedFullLayout}
                  />
                );
              }

              // Handoff message rendering - centered with light blue background and white text
              if (msg.isHandoff) {
                if (isEmbedFullLayout) {
                  return (
                    <div key={idx} style={{ margin: '14px 0', maxWidth: '100%', alignSelf: 'flex-start' }}>
                      <div style={{
                        maxWidth: '100%',
                        wordBreak: 'break-word',
                        fontFamily: customization.fontFamily || 'system-ui',
                        fontSize: fontSizes[fontSizeIdx],
                        lineHeight: customization.lineHeight || 1.6,
                        color: botTextColor,
                        textAlign: 'left',
                      }}>
                        {renderTextWithLinks(msg.text)}
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={idx} style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    margin: '14px 0',
                  }}>
                    <span style={{
                      display: 'inline-block',
                      background: '#87CEEB', // Light blue
                      color: '#FFFFFF', // White text
                      padding: '12px 20px',
                      borderRadius: 16,
                      maxWidth: '80%',
                      wordBreak: 'break-word',
                      fontFamily: customization.fontFamily || 'system-ui',
                      fontSize: fontSizes[fontSizeIdx],
                      lineHeight: customization.lineHeight || 1.6,
                      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                      textAlign: 'center',
                    }}>
                      {renderTextWithLinks(msg.text)}
                    </span>
                  </div>
                );
              }

              if (msg.calcomSlots && typeof msg.calcomSlots === 'object') {
                if (isEmbedFullLayout) {
                  return (
                    <div key={idx} style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      margin: '14px 0',
                      maxWidth: '100%',
                    }}>
                      <div style={{
                        maxWidth: '100%',
                        wordBreak: 'break-word',
                        fontFamily: customization.fontFamily || 'system-ui',
                        fontSize: fontSizes[fontSizeIdx],
                        lineHeight: customization.lineHeight || 1.6,
                        color: botTextColor,
                        textAlign: 'left',
                        marginBottom: 10,
                      }}>
                        {renderTextWithLinks(msg.text)}
                      </div>
                      <div style={{ marginTop: 0, maxWidth: '100%', alignSelf: 'flex-start' }}>
                        <CalcomSlotsPicker payload={msg.calcomSlots} onSendMessage={sendWebSocketMessage} />
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={idx} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    margin: '14px 0',
                    maxWidth: '100%',
                  }}>
                    {msg.role === 'bot' && displayAgentName && (
                      <span style={{
                        color: customization.agentNameColor || '#333',
                        fontSize: '14px',
                        fontFamily: customization.fontFamily || 'system-ui',
                        fontWeight: 'bold',
                        marginBottom: 4,
                        alignSelf: 'flex-start'
                      }}>
                        {displayAgentName}
                      </span>
                    )}
                    <span style={{
                      display: 'inline-block',
                      background: botMsgColor,
                      color: '#222',
                      padding: customization.bubblePadding || '14px 20px',
                      borderRadius: 16,
                      maxWidth: '80%',
                      wordBreak: 'break-word',
                      fontFamily: customization.fontFamily || 'system-ui',
                      fontSize: fontSizes[fontSizeIdx],
                      lineHeight: customization.lineHeight || 1.6,
                      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                      textAlign: 'left',
                    }}>
                      {renderTextWithLinks(msg.text)}
                    </span>
                    <div style={{ marginTop: 10, maxWidth: '100%', alignSelf: 'flex-start' }}>
                      <CalcomSlotsPicker payload={msg.calcomSlots} onSendMessage={sendWebSocketMessage} />
                    </div>
                  </div>
                );
              }

              if (msg.calcomEventTypes?.length) {
                if (isEmbedFullLayout) {
                  return (
                    <div key={idx} style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      margin: '14px 0',
                      maxWidth: '100%',
                    }}>
                      <div style={{
                        maxWidth: '100%',
                        wordBreak: 'break-word',
                        fontFamily: customization.fontFamily || 'system-ui',
                        fontSize: fontSizes[fontSizeIdx],
                        lineHeight: customization.lineHeight || 1.6,
                        color: botTextColor,
                        textAlign: 'left',
                        marginBottom: 10,
                      }}>
                        {renderTextWithLinks(msg.text)}
                      </div>
                      <div style={{ marginTop: 0, maxWidth: '100%', alignSelf: 'flex-start' }}>
                        <CalcomEventTypePicker
                          items={msg.calcomEventTypes}
                          onPick={(et, meta) => {
                            sendWebSocketMessage(meta.wireMessage, { displayMessage: meta.displayMessage });
                          }}
                        />
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={idx} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    margin: '14px 0',
                    maxWidth: '100%',
                  }}>
                    {msg.role === 'bot' && displayAgentName && (
                      <span style={{
                        color: customization.agentNameColor || '#333',
                        fontSize: '14px',
                        fontFamily: customization.fontFamily || 'system-ui',
                        fontWeight: 'bold',
                        marginBottom: 4,
                        alignSelf: 'flex-start'
                      }}>
                        {displayAgentName}
                      </span>
                    )}
                    <span style={{
                      display: 'inline-block',
                      background: botMsgColor,
                      color: '#222',
                      padding: customization.bubblePadding || '14px 20px',
                      borderRadius: 16,
                      maxWidth: '80%',
                      wordBreak: 'break-word',
                      fontFamily: customization.fontFamily || 'system-ui',
                      fontSize: fontSizes[fontSizeIdx],
                      lineHeight: customization.lineHeight || 1.6,
                      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                      textAlign: 'left',
                    }}>
                      {renderTextWithLinks(msg.text)}
                    </span>
                    <div style={{ marginTop: 10, maxWidth: '100%', alignSelf: 'flex-start' }}>
                      <CalcomEventTypePicker
                        items={msg.calcomEventTypes}
                        onPick={(et, meta) => {
                          sendWebSocketMessage(meta.wireMessage, { displayMessage: meta.displayMessage });
                        }}
                      />
                    </div>
                  </div>
                );
              }

              // Default message rendering:
              // Render animated thinking/waiting bubble for transient placeholders
              if (msg.isThinking || msg.isWaiting) {
                if (isEmbedFullLayout) {
                  return (
                    <div key={idx} style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      margin: '14px 0',
                      maxWidth: '100%',
                    }}>
                      {displayAgentName && (
                        <span style={{
                          color: customization.agentNameColor || '#333',
                          fontSize: '14px',
                          fontFamily: customization.fontFamily || 'system-ui',
                          fontWeight: 'bold',
                          marginBottom: 4,
                          alignSelf: 'flex-start',
                        }}>
                          {displayAgentName}
                        </span>
                      )}
                      <span style={{
                        color: customization.timestampColor || '#888',
                        fontSize: customization.timestampFontSize || '12px',
                        fontFamily: customization.timestampFontFamily || 'system-ui',
                        opacity: 0.85,
                        marginBottom: 6,
                      }}>
                        {formatTimestamp(msg.timestamp || new Date())}
                      </span>
                      <div style={{
                        color: botTextColor,
                        fontFamily: customization.fontFamily || 'system-ui',
                        fontSize: fontSizes[fontSizeIdx],
                        lineHeight: customization.lineHeight || 1.6,
                        opacity: 0.85,
                      }}>
                        {(() => {
                          const agentName = displayAgentName;
                          const waitingMessage = customization.waitingMessage || '';
                          if (agentName && waitingMessage) return `${agentName} ${waitingMessage}`;
                          if (agentName) return agentName;
                          if (waitingMessage) return waitingMessage;
                          return 'Thinking';
                        })()}{' '}
                        <span style={{ opacity: 0.6 }}>···</span>
                      </div>
                    </div>
                  );
                }
                // Render animated thinking/waiting bubble for transient placeholders
                return (
                  <div key={idx} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    margin: '14px 0',
                  }}>
                    {displayAgentName && (
                      <span style={{
                        color: customization.agentNameColor || '#333',
                        fontSize: '14px',
                        fontFamily: customization.fontFamily || 'system-ui',
                        fontWeight: 'bold',
                        marginBottom: 4,
                        alignSelf: 'flex-start'
                      }}>
                        {displayAgentName}
                      </span>
                    )}
                    <span style={{
                      display: 'inline-block',
                      background: botMsgColor,
                      color: '#222',
                      padding: customization.bubblePadding || '14px 20px',
                      borderRadius: 16,
                      maxWidth: '80%',
                      wordBreak: 'break-word',
                      fontFamily: customization.fontFamily || 'system-ui',
                      fontSize: fontSizes[fontSizeIdx],
                      lineHeight: customization.lineHeight || 1.6,
                      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                      position: 'relative',
                    }}>
                      <span style={{ opacity: 0.7 }}>
                        {(() => {
                          const agentName = displayAgentName;
                          const waitingMessage = customization.waitingMessage || '';
                          if (agentName && waitingMessage) {
                            return `${agentName} ${waitingMessage}`;
                          } else if (agentName) {
                            return agentName;
                          } else if (waitingMessage) {
                            return waitingMessage;
                          } else {
                            return 'Thinking';
                          }
                        })()}
                      </span>
                      <span style={{ display: 'inline-block', marginLeft: 8 }}>
                        <span style={{
                          display: 'inline-block',
                          width: 6, height: 6, borderRadius: '50%', background: '#bbb', margin: '0 2px',
                          animation: 'blink 1.4s infinite both', animationDelay: '0s'
                        }}></span>
                        <span style={{
                          display: 'inline-block',
                          width: 6, height: 6, borderRadius: '50%', background: '#bbb', margin: '0 2px',
                          animation: 'blink 1.4s infinite both', animationDelay: '0.2s'
                        }}></span>
                        <span style={{
                          display: 'inline-block',
                          width: 6, height: 6, borderRadius: '50%', background: '#bbb', margin: '0 2px',
                          animation: 'blink 1.4s infinite both', animationDelay: '0.4s'
                        }}></span>
                      </span>
                    </span>
                  </div>
                );
              }

              if (isEmbedFullLayout && msg.role === 'bot') {
                return (
                  <div key={idx} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    margin: '14px 0',
                    maxWidth: '100%',
                  }}>
                    <div style={{
                      maxWidth: '100%',
                      wordBreak: 'break-word',
                      fontFamily: customization.fontFamily || 'system-ui',
                      fontSize: fontSizes[fontSizeIdx],
                      lineHeight: customization.lineHeight || 1.6,
                      color: botTextColor,
                      textAlign: 'left',
                    }}>
                      {renderTextWithLinks(msg.displayText || msg.text)}
                    </div>
                    {msg.quickReplies?.length > 0 && (
                      <div style={{ marginTop: 10, maxWidth: '100%', alignSelf: 'flex-start' }}>
                        <QuickReplyPicker
                          items={msg.quickReplies}
                          onPick={handleQuickReplyPick}
                          botMessageColor={botMsgColor}
                          botTextColor={botTextColor}
                          quickReplyTextColor={quickReplyTextColor}
                          quickReplyBorderColor={quickReplyBorderColor}
                          quickReplyBackgroundColor={quickReplyBackgroundColor}
                        />
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <div key={idx} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  margin: '14px 0', // more vertical space between bubbles
                }}>
                  {/* Agent Name display for bot messages */}
                  {msg.role === 'bot' && displayAgentName && (
                    <span style={{
                      color: customization.agentNameColor || '#333',
                      fontSize: '14px',
                      fontFamily: customization.fontFamily || 'system-ui',
                      fontWeight: 'bold',
                      marginBottom: 4,
                      alignSelf: 'flex-start'
                    }}>
                      {displayAgentName}
                    </span>
                  )}
                  {/* Timestamp above message bubble */}
                  {customization.showTimestamps && msg.timestamp && (
                    <span style={{
                      color: customization.timestampColor || '#888',
                      fontSize: customization.timestampFontSize || '12px',
                      fontFamily: customization.timestampFontFamily || 'system-ui',
                      opacity: 0.7,
                      marginBottom: 8,
                      alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start'
                    }}>
                      {formatTimestamp(msg.timestamp)}
                    </span>
                  )}
                  <span style={{
                    display: 'inline-block',
                    background: msg.role === 'user' ? userMsgColor : botMsgColor,
                    color: msg.role === 'user' ? userTextColor : botTextColor,
                    padding: customization.bubblePadding || '14px 20px', // more padding inside bubble
                    borderRadius: 16,
                    maxWidth: '80%',
                    wordBreak: 'break-word',
                    fontFamily: customization.fontFamily || 'system-ui',
                    fontSize: fontSizes[fontSizeIdx], // Use dynamic font size instead of customization.fontSize
                    lineHeight: customization.lineHeight || 1.6,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                    textAlign: 'left', // <-- left justify text
                  }}>
                    {renderTextWithLinks(msg.displayText || msg.text)}
                  </span>
                  {msg.role === 'bot' && msg.quickReplies?.length > 0 && (
                    <div style={{ marginTop: 10, maxWidth: '100%', alignSelf: 'flex-start' }}>
                      <QuickReplyPicker
                        items={msg.quickReplies}
                        onPick={handleQuickReplyPick}
                        botMessageColor={botMsgColor}
                        botTextColor={botTextColor}
                        quickReplyTextColor={quickReplyTextColor}
                        quickReplyBorderColor={quickReplyBorderColor}
                        quickReplyBackgroundColor={quickReplyBackgroundColor}
                      />
                    </div>
                  )}
                </div>
              );
            })}

            {/* Streaming message display */}
            {streamingMessageDisplay}

            {/* Thinking - only show if no specific thinking message is already in the list */}
            {loading && !isStreaming && !messages.some(m => m.isThinking) && (
              isEmbedFullLayout ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', margin: '14px 0', maxWidth: '100%' }}>
                  {displayAgentName && (
                    <span style={{
                      color: customization.agentNameColor || '#333',
                      fontSize: '14px',
                      fontFamily: customization.fontFamily || 'system-ui',
                      fontWeight: 'bold',
                      marginBottom: 4,
                      alignSelf: 'flex-start',
                    }}>
                      {displayAgentName}
                    </span>
                  )}
                  <span style={{
                    color: customization.timestampColor || '#888',
                    fontSize: customization.timestampFontSize || '12px',
                    fontFamily: customization.timestampFontFamily || 'system-ui',
                    opacity: 0.85,
                    marginBottom: 6,
                  }}>
                    {formatTimestamp(new Date())}
                  </span>
                  <div style={{
                    color: botTextColor,
                    fontFamily: customization.fontFamily || 'system-ui',
                    fontSize: fontSizes[fontSizeIdx],
                    lineHeight: customization.lineHeight || 1.6,
                    opacity: 0.85,
                  }}>
                    {(() => {
                      const agentName = displayAgentName;
                      const waitingMessage = customization.waitingMessage || '';
                      if (agentName && waitingMessage) return `${agentName} ${waitingMessage}`;
                      if (agentName) return agentName;
                      if (waitingMessage) return waitingMessage;
                      return 'Thinking';
                    })()}{' '}
                    <span style={{ opacity: 0.6 }}>···</span>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', margin: '14px 0' }}>
                  <span style={{
                    display: 'inline-block',
                    background: botMsgColor,
                    color: '#222',
                    padding: customization.bubblePadding || '14px 20px',
                    borderRadius: 16,
                    maxWidth: '80%',
                    wordBreak: 'break-word',
                    fontFamily: customization.fontFamily || 'system-ui',
                    fontSize: fontSizes[fontSizeIdx], // Use dynamic font size instead of customization.fontSize
                    lineHeight: customization.lineHeight || 1.6,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                    position: 'relative',
                  }}>
                    <span style={{ opacity: 0.7 }}>
                      {(() => {
                        const agentName = displayAgentName;
                        const waitingMessage = customization.waitingMessage || '';
                        if (agentName && waitingMessage) {
                          return `${agentName} ${waitingMessage}`;
                        } else if (agentName) {
                          return agentName;
                        } else if (waitingMessage) {
                          return waitingMessage;
                        } else {
                          return 'Thinking';
                        }
                      })()}
                    </span>
                    <span style={{ display: 'inline-block', marginLeft: 8 }}>
                      <span style={{
                        display: 'inline-block',
                        width: 6, height: 6, borderRadius: '50%', background: '#bbb', margin: '0 2px',
                        animation: 'blink 1.4s infinite both', animationDelay: '0s'
                      }}></span>
                      <span style={{
                        display: 'inline-block',
                        width: 6, height: 6, borderRadius: '50%', background: '#bbb', margin: '0 2px',
                        animation: 'blink 1.4s infinite both', animationDelay: '0.2s'
                      }}></span>
                      <span style={{
                        display: 'inline-block',
                        width: 6, height: 6, borderRadius: '50%', background: '#bbb', margin: '0 2px',
                        animation: 'blink 1.4s infinite both', animationDelay: '0.4s'
                      }}></span>
                    </span>
                  </span>
                </div>
              )
            )}
            {/* Scroll target */}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Preset Response Buttons - only show when active */}
      {!isPaused && showPresetResponses && presetResponses.length > 0 && !messages.some(msg => msg.role === 'user') && !isEmbedFullLayout && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          marginBottom: '8px',
          padding: '8px',
          backgroundColor: customization.footerBackgroundColor || '#7B4B47',
          borderTop: '1px solid #E8E8E8',
          ...(isEmbedFullLayout ? { flexShrink: 0 } : {}),
        }}>
          {presetResponses.map((preset, index) => (
            <button
              key={preset.id || index}
              onClick={() => handlePresetClick(preset.text)}
              style={{
                borderRadius: '16px',
                textTransform: 'none',
                minHeight: '32px',
                padding: '4px 12px',
                textAlign: 'left',
                justifyContent: 'flex-start',
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                overflow: 'hidden',
                fontSize: '13px',
                fontFamily: customization.fontFamily || 'system-ui',
                border: `1px solid ${customization.starterPromptBorderColor || 'var(--primary-color, #1976d2)'}`,
                backgroundColor: customization.starterPromptBackgroundColor || '#FFFFFF',
                color: customization.starterPromptTextColor || 'var(--primary-color, #1976d2)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                outline: 'none',
              }}
              onMouseEnter={e => {
                e.target.style.backgroundColor = customization.starterPromptBackgroundColor || '#FFFFFF';
                e.target.style.opacity = 0.9;
              }}
              onMouseLeave={e => {
                e.target.style.backgroundColor = customization.starterPromptBackgroundColor || '#FFFFFF';
                e.target.style.opacity = 1;
              }}
            >
              {preset.text}
            </button>
          ))}
        </div>
      )}

      {/* Input box - only show when active */}
      {showBottomInput && <div style={{
        width: '100%',
        background: 'transparent',
        position: 'relative', // not absolute
        bottom: 0,
        left: 0,
        boxSizing: 'border-box',
        zIndex: 2,
        padding: isEmbedFullLayout && embedHasUserMessage ? 10 : 0,
        borderRadius: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        borderTop: 'none',
        minHeight: 50,
        justifyContent: 'center',
        ...(isEmbedFullLayout ? { flexShrink: 0 } : {}),

      }}>
        <div
          style={{
            position: 'relative',
            width: '100%',
            margin: isEmbedFullLayout && embedHasUserMessage ? 0 : '10px',
          }}
        >
          <VoiceSpeakingIndicator inCall={enableVoice && isInVoiceCall} speaking={isBotSpeaking} />
          <div style={{
            width: '100%',
            background: '#ffffff',
            borderRadius: '25px',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            border: isInputFocused ? '1px solid var(--primary-color, #1976d2)' : '1px solid #cfcfcf',
            boxShadow: isInputFocused ? '0 0 0 2px color-mix(in srgb, var(--primary-color, #1976d2) 20%, transparent)' : 'none',
            minHeight: 50,
            marginBottom: 0,
            padding: '8px 12px',
          }}>
            <InputBase
              inputRef={inputRef}
              multiline
              minRows={isEmbedFullLayout ? 3 : 1}
              maxRows={4}
              value={input}
              onChange={e => {
                setInput(e.target.value);
                updateActivity(true); // Track typing activity
              }}
              onKeyDown={handleInputKeyDown}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
              placeholder={inputPlaceholder}
              sx={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: fontSizes[fontSizeIdx],
                fontFamily: customization.fontFamily || 'system-ui',
                padding: '6px 12px',
                background: 'transparent',
                color: '#222',
                borderRadius: 0,
                boxShadow: 'none',
                margin: 0,
                height: 'auto',
                resize: 'none',
                overflowY: 'auto',
                lineHeight: 1.4,
                boxSizing: 'border-box',
                '& textarea': {
                  padding: 0,
                  margin: 0,
                  boxSizing: 'border-box',
                }
              }}
            />
            {enableVoice && (
              <IconButton
                type="button"
                title={isInVoiceCall ? 'End voice call' : 'Call AI (voice)'}
                onClick={isInVoiceCall ? endVoiceCall : startVoiceCall}
                disabled={loading || isVoiceConnecting || !sessionId}
                color="primary"
                size="medium"
                sx={{
                  background: 'transparent',
                  color: isInVoiceCall ? '#d32f2f' : (customization.sendIconColor || customization.headerBackgroundColor || 'var(--primary-color, #1976d2)'),
                  borderRadius: 0,
                  width: 44,
                  height: 44,
                  boxShadow: 'none',
                  margin: 0,
                }}
              >
                {isInVoiceCall ? <PhoneDisabledIcon /> : <PhoneIcon />}
              </IconButton>
            )}
            {enableVoice && isInVoiceCall && (
              <IconButton
                type="button"
                title={isVoiceMuted ? 'Unmute microphone' : 'Mute microphone'}
                onClick={toggleVoiceMute}
                color="primary"
                size="medium"
                sx={{ width: 44, height: 44, margin: 0 }}
              >
                {isVoiceMuted ? <MicOffIcon /> : <MicIcon />}
              </IconButton>
            )}
            <IconButton
              type="button"
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              color="primary"
              size="medium"
              sx={{
                background: 'transparent',
                color: customization.sendIconColor || customization.headerBackgroundColor || 'var(--primary-color, #1976d2)',
                borderRadius: 0,
                width: 44,
                height: 44,
                boxShadow: 'none',
                transition: 'background 0.2s',
                margin: 0,
                '&.Mui-disabled': {
                  background: 'transparent',
                  color: customization.sendIconDisabledColor || '#bdbdbd',
                }
              }}
            >
              <SendIcon />
            </IconButton>
          </div>
        </div>
        {enableVoice && voiceError && (
          <div style={{ fontSize: 12, color: '#d32f2f', padding: '0 14px 6px', textAlign: 'center' }}>
            {voiceError}
          </div>
        )}
        {isEmbedFullLayout && embedHasUserMessage && (
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              padding: '4px 0 0',
              boxSizing: 'border-box',
            }}
          >
            <button
              type="button"
              onClick={handleEmbedNewSession}
              disabled={loading}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: loading ? 'default' : 'pointer',
                fontSize: 13,
                fontWeight: 500,
                color: loading
                  ? '#bdbdbd'
                  : (customization.starterPromptTextColor || 'var(--primary-color, #1976d2)'),
                fontFamily: customization.fontFamily || 'system-ui',
                textDecoration: 'underline',
                opacity: loading ? 0.6 : 1,
              }}
            >
              New Session
            </button>
          </div>
        )}
      </div>}
      {/* Add this block for disclaimer */}
      {customization.disclaimerText && (
        <div
          style={{
            fontSize: '12px',
            color: '#888',
            textAlign: customization.disclaimerAlign || 'left',
            margin: '8px 0',
            padding: '0 16px',
            wordBreak: 'break-word',
            whiteSpace: 'pre-line',
            width: '100%',
            ...(isEmbedFullLayout ? { flexShrink: 0 } : {}),
          }}
        >
          {customization.disclaimerText}
        </div>
      )}
      {/* Close confirmation modal */}
      {showCloseConfirm && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.3)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: 8,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              padding: 32,
              minWidth: 280,
              maxWidth: 340,
              textAlign: 'center',
            }}
          >
            <div style={{ marginBottom: 16, fontWeight: 500, fontSize: 18 }}>
              {closeConfirmText || "Thanks for chatting with us today!"}
            </div>
            {customization.visitorTranscriptEnabled === true && (
              <div style={{ marginBottom: 16, textAlign: 'left' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={requestTranscript}
                      onChange={(e) => {
                        setRequestTranscript(e.target.checked);
                        if (!e.target.checked) {
                          setVisitorEmailError('');
                        }
                      }}
                      size="small"
                    />
                  }
                  label="Email me this transcript"
                  style={{ margin: 0, marginBottom: 8 }}
                />
                {requestTranscript && (
                  <TextField
                    fullWidth
                    size="small"
                    label="Email Address"
                    value={visitorEmail}
                    onChange={(e) => {
                      setVisitorEmail(e.target.value);
                      if (visitorEmailError) {
                        setVisitorEmailError('');
                      }
                    }}
                    error={!!visitorEmailError}
                    helperText={visitorEmailError || 'We will email your transcript after chat ends.'}
                    placeholder="name@example.com"
                  />
                )}
              </div>
            )}
            <Button
              variant="contained"
              color="primary"
              fullWidth
              style={{ marginBottom: 16, fontWeight: 600 }}
              onClick={handleEndChat}
              disabled={sendingTranscript}
            >
              {sendingTranscript ? 'Saving...' : 'END CHAT'}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={handleCancelClose}
              disabled={sendingTranscript}
            >
              CONTINUE CHAT
            </Button>
          </div>
        </div>
      )}
      {/* Chat rating dialog overlay */}
      {showRatingDialog && (
        <ChatRatingDialog
          open={showRatingDialog}
          prompt={customization.chatRatingPrompt || 'How would you rate your chat experience?'}
          thankYou={customization.chatRatingThankYou || 'Thank you for your feedback!'}
          allowComments={customization.chatRatingAllowComments === true}
          ratingSubmitted={ratingSubmitted}
          onSubmit={handleRatingSubmit}
          onCancel={() => {
            // Clear the ref when canceling (in case it was set)
            showingThankYouRef.current = false;
            // Use the proper handler that broadcasts to all tabs
            handleRatingDialogClose(true);
          }}
          onThankYouTimeout={() => {
            console.log('⏱️ Thank you message displayed for 2 seconds - now closing dialog, cleaning up, and minimizing');

            // Clear the ref to allow minimize operations
            showingThankYouRef.current = false;
            console.log('✅ Cleared showingThankYouRef - minimize now allowed');

            // After showing thank you message for 2 seconds:
            // 1. Close the dialog and cleanup locally + broadcast to other tabs
            // 2. Minimize the widget locally + broadcast to other tabs
            // Note: We'll ignore our own broadcast in the handler

            handleRatingDialogClose(true, true); // minimize=true, broadcastToOthers=true

            console.log('✅ Thank you message flow complete - dialog closed, cleaned up, and minimized');
          }}
        />
      )}
      {/* Agent leave popup notification */}
      {showAgentLeavePopup && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 2100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'fadeIn 0.3s ease-in',
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: 12,
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              padding: '28px 24px',
              width: 320,
              maxWidth: '90vw',
              textAlign: 'center',
              animation: 'slideIn 0.3s ease-out',
            }}
          >
            <div
              style={{
                fontSize: '20px',
                fontWeight: 600,
                color: '#333',
                marginBottom: '16px',
              }}
            >
              Agent Left
            </div>
            <div
              style={{
                fontSize: '15px',
                color: '#555',
                lineHeight: '1.5',
                whiteSpace: 'pre-line',
              }}
            >
              {agentLeaveMessage}
            </div>
            <div
              style={{
                marginTop: '20px',
                fontSize: '13px',
                color: '#999',
                fontStyle: 'italic',
              }}
            >
              This window will close automatically...
            </div>
          </div>
        </div>
      )}
      {showFormSuccessModal && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.3)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: 8,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              padding: '32px 24px',
              width: 320,
              maxWidth: '90vw',
              margin: '0 auto',
              textAlign: 'center',
            }}
          >
            <div style={{ marginBottom: 16, fontWeight: 500, fontSize: 18 }}>
              {formSuccessMessage || "Thank you for your submission!"}
            </div>
            {customization.visitorTranscriptEnabled === true && (
              <div style={{ marginBottom: 16, textAlign: 'left' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={requestTranscript}
                      onChange={(e) => {
                        setRequestTranscript(e.target.checked);
                        if (!e.target.checked) {
                          setVisitorEmailError('');
                        }
                      }}
                      size="small"
                    />
                  }
                  label="Email me this transcript"
                  style={{ margin: 0, marginBottom: 8 }}
                />
                {requestTranscript && (
                  <TextField
                    fullWidth
                    size="small"
                    label="Email Address"
                    value={visitorEmail}
                    onChange={(e) => {
                      setVisitorEmail(e.target.value);
                      if (visitorEmailError) {
                        setVisitorEmailError('');
                      }
                    }}
                    error={!!visitorEmailError}
                    helperText={visitorEmailError || 'We will email your transcript after chat ends.'}
                    placeholder="name@example.com"
                  />
                )}
              </div>
            )}
            <Button
              variant="contained"
              color="primary"
              fullWidth
              style={{ marginBottom: 16, fontWeight: 600 }}
              onClick={handleEndChat}
              disabled={sendingTranscript}
            >
              {sendingTranscript ? 'Saving...' : 'END CHAT'}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={handleContinueChatAfterForm}
              disabled={sendingTranscript}
            >
              CONTINUE CHAT
            </Button>
          </div>
        </div>
      )}
      {(!isEmbedFullLayout || !embedShowLanding) && (
        <div style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '0 16px 8px 16px', // Match disclaimer horizontal padding
          boxSizing: 'border-box',
          ...(isEmbedFullLayout ? { flexShrink: 0 } : {}),
        }}>
          <PoweredByChattitude
            tenantId={tenantId}
            poweredByLogoUrl={config?.customization?.poweredByLogoUrl}
            poweredByLogoHref={config?.customization?.poweredByLogoHref}
          />
        </div>
      )}
    </div>
  );
};

export default ChatbotFrame;

